// eslint-disable-next-line import/no-cycle
import {
  ActionTypeEnum,
  FacebookMessengerButton,
  FacebookMessengerSkinsUrlPreview,
  Property,
  ResponsiveDisplayStackEnum,
  TemplateWidth,
  Translation,
} from '@/types';
import { useI18n } from 'vue-i18n';
import { escapeRegExp } from 'lodash';
import { i18n } from '@/i18n';
import VariablesList, { Variable } from '@/components/template-builder/utils/variables-list';
import {
  substituteIntegrationWidgetUrl,
  extractIntegrationPreviewValues,
} from '@/components/template-builder/utils/integration-widget-url';
// eslint-disable-next-line import/no-cycle
import {
  buildHtmlRenderConfig,
  hasTranslatableFields,
  isShopMultilingual,
} from '@/components/template-builder/utils/integration-widget-i18n';
// eslint-disable-next-line import/no-cycle
import {
  COUNTDOWN_LABEL_FIELDS,
  COUNTDOWN_WIDGET_SELECTOR,
  CountdownLangContext,
  countdownLabelsOf,
  ensureCountdownLabelTranslations,
  sanitizeCountdownLabel,
  syncCountdownLabelFromConfig,
} from '@/components/template-builder/utils/countdown-email-i18n';
// eslint-disable-next-line import/no-cycle
import { renderIntegrationWidgetApi, IntegrationWidgetRenderError } from '@/composables/widgets/useIntegrationWidgets';
// eslint-disable-next-line import/no-cycle
import { facebookButtonStructure } from '@/components/template-builder/config/templates/facebookmessenger/raw-html-config';
import beautifier from 'js-beautify';
// eslint-disable-next-line import/no-cycle
import {
  TemplateEditorState,
  updateSectionsInState,
  replaceTranslationInState,
  removeUnusedTranslations,
  htmlHasSmartlist,
  addSmartListOriginalCode, setPerfectSizeForImage, fixImagesWidth,
  addTranslationsToState,
  refreshNextSteps,
  sortTranslationInState,
  removeTranslation,
} from '@/composables/template-editor/TemplateEditor';

// eslint-disable-next-line import/no-cycle
import {
  asInt, getFirstParent, getParents, showToast,
} from '@/helpers';
import { countriesList, getCountryName } from '@/components/template-builder/utils/countries-list';
// eslint-disable-next-line import/no-cycle
import { store } from '@/store';
// eslint-disable-next-line import/no-cycle
import {
  BORDERS_PROPERTIES_TO_REMOVE_IF_ZERO,
  HIDE_MOBILE_ATTRIBUTE,
  HIDE_MOBILE_COMMENT_REGEX,
  LINE_HEIGHT_POSSIBLE_VALUES,
  POPIN_AUTO_CLOSE_ATTRIBUTE,
  POPIN_AUTO_CLOSE_CLASS,
  TRANSLATION_ATTRIBUTE,
  TRANSLATION_MARKUP,
  escapeCssCommentsForNesting,
  unescapeCssCommentsFromNesting,
} from '@/components/template-builder/utils/constants';

// eslint-disable-next-line import/no-cycle
import {
  Lang,
  UserState,
  getShopDefaultLang,
} from '@/composables/User';

// eslint-disable-next-line import/no-cycle
import { TemplateTypeEnum } from '@/composables/shop/Templates';
// eslint-disable-next-line import/no-cycle
import { getCssPropertyByName } from '@/components/template-builder/getters';
// eslint-disable-next-line import/no-cycle
import { useEditorBackground } from '@/composables/useEditorBackground';
// eslint-disable-next-line import/no-cycle
import {
  createCountrySelectOption,
  getElementFromIframe,
  getTemplateIframeDocument, isDisplayTemplate,
  isEmailTemplate,
  isPushTemplate,
  withDesktopLayout,
} from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import {
  addOrUpdateDynamicStyle,
  getDynamicStylesForActiveSection,
  getDynamicStylesForSelector,
  removeDynamicStyle,
  getCascadedStyleValue,
  withCascadedStyleCache,
} from '../utils/parser';
// eslint-disable-next-line import/no-cycle
import {
  sanitizeTranslationMarkup,
  setTranslationInState,
  generateTranslations,
  translateDocument,
  TRANSLATABLE_ATTRIBUTES,
  createTranslation,
  removeTranslationInState,
  TranslationCreationResult,
} from '../utils/translate';
// eslint-disable-next-line import/no-cycle
import { refreshWhatsappSmartlist } from './whatsapp-smart-product-list';
// eslint-disable-next-line import/no-cycle
import { refreshRcsSmartlist } from './rcs-smart-product-list';
// eslint-disable-next-line import/no-cycle
import { isWhatsappSmartListEnabled } from '../getters/whatsapp-smart-product-list';
// eslint-disable-next-line import/no-cycle
import { isRcsSmartListEnabled } from '../getters/rcs-smart-product-list';
import { isVariableExpression, applyVariableToImage, DATA_VAR_SRC } from '../utils/variable-image';

// eslint-disable-next-line import/no-cycle
export * from './social-networks';
// eslint-disable-next-line import/no-cycle
export * from './media-display';
// eslint-disable-next-line import/no-cycle
export * from './link';
// eslint-disable-next-line import/no-cycle
export * from './smart-product-list';
// eslint-disable-next-line import/no-cycle
export * from './form';
// eslint-disable-next-line import/no-cycle
export * from './menu';

export const cleanCssByProperty = (selector: string, property: Property, mobile = false) => {
  let propertyRemoved = false;

  // Get all sections of current template
  const sections = TemplateEditorState.template?.sections;

  if (sections && sections.length) {
    sections.forEach((section) => {
      // Get dunamic styles of current section
      const styles = getDynamicStylesForActiveSection(section.id_template_elements, mobile);
      if (styles) {
        const { sheet } = styles;

        if (sheet) {
          if (sheet.cssRules && sheet.cssRules.length) {
            let index = 0;

            const processCssRule = (rule: CSSRule, mediaSheet?: CSSMediaRule) => {
              const styleRule = rule as CSSStyleRule;
              const currentSheet = mediaSheet || sheet;
              // We check if there are many properties in the rule
              if ((styleRule.cssText.match(new RegExp(';', 'g')) ?? []).length > 1) {
                // We split the rule to remove the desired property
                const explodedRule = styleRule.cssText.match(new RegExp('^([^{]+{)([^}]+)}'));

                if (explodedRule) {
                  const ruleSelector = explodedRule[1];
                  const ruleProperties = explodedRule[2].trim().split(';');

                  if (ruleProperties.length) {
                    const finalProperties: string[] = [];
                    let propertyChangedForThieRule = false;
                    let index2 = 0;
                    Object.values(ruleProperties).forEach((ruleProperty: any) => {
                      if ((new RegExp(`^${property.name}\\s*:`)).test(ruleProperty.trim()) && ruleSelector.replace('{', '').trim() === selector) {
                        // The current property matches the desired property, so we need to remove it
                        delete ruleProperties[index2];
                        propertyRemoved = true;
                        propertyChangedForThieRule = true;
                      } else if (ruleProperty.trim() !== '') {
                        finalProperties.push(ruleProperty.trim());
                      }
                      index2 += 1;
                    });

                    if (propertyChangedForThieRule && finalProperties.length) {
                      const finalRule = `${ruleSelector} ${finalProperties.join('; ')}; }`;
                      currentSheet.deleteRule(index);
                      // eslint-disable-next-line no-param-reassign
                      index -= 1;
                      currentSheet.insertRule(finalRule, index);
                    }
                  }
                }
              } else if ((new RegExp(`{\\s*${property.name}\\s*:`)).test(styleRule.cssText) && styleRule.selectorText === selector) {
                // If not, and if the CSS rule text matches the property, we remove the rule
                currentSheet.deleteRule(index);
                // eslint-disable-next-line no-param-reassign
                index -= 1;
                propertyRemoved = true;
              }
            };

            Array.from(sheet.cssRules).forEach((styleRule) => {
              if (styleRule.constructor.name === 'CSSStyleRule') {
                processCssRule(styleRule as CSSStyleRule);
                index += 1;
              } else if (styleRule.constructor.name === 'CSSMediaRule') {
                index = 0;
                Array.from((styleRule as CSSMediaRule).cssRules).forEach((mediaRule: CSSRule) => {
                  processCssRule(mediaRule as CSSStyleRule, styleRule as CSSMediaRule);
                  index += 1;
                });
              }
            });
          }
        }
      }
    });
  }

  if (propertyRemoved) {
    // If at least a property has been removed, we update the sections in state
    updateSectionsInState();
  }
};

(cleanCssByProperty as any).isCssSetter = true;

export const resetCssPropertyWithoutPseudoClasses = (selector: string, property: Property) => {
  if (undefined !== property.value) {
    const { name } = property;
    let newSelector = selector;
    newSelector = newSelector.replace(new RegExp('(:[a-zA-Z-]+\\([-:.a-zA-Z#]+\\))+', 'g'), '');
    addOrUpdateDynamicStyle(newSelector, { [name]: '0' });
  }
};

export const setCssProperty = (selector: string, property: Property, mobile = false) => {
  if (undefined !== property.value) {
    // eslint-disable-next-line prefer-const
    let { name, value } = property;
    let updateDynamicStyle = true;
    if (name === 'text-decoration-line' && value === '') {
      value = 'none';
    }
    // For email templates, background-image should only be in HTML background attribute, not in CSS
    // This prevents email clients from transforming background to background:none
    if (name === 'background-image' && isEmailTemplate(TemplateEditorState.template?.type)) {
      return;
    }
    if (value) {
      if (name === 'max-width' && new RegExp(' img').test(selector) && isEmailTemplate(TemplateEditorState.template?.type)) {
        // In case of changing the max-width property of an image, we also change inline properties
        const template = getTemplateIframeDocument();
        const elements = template?.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element && element.tagName === 'IMG') {
            setPerfectSizeForImage(element, value);
            updateDynamicStyle = false;
          }
        });
      } else if (name === 'vertical-align' && isEmailTemplate(TemplateEditorState.template?.type)) {
        // If property is vertical-align, we also set the valign property on the element
        const valign = value === 'middle' ? 'center' : value;
        const template = getTemplateIframeDocument();
        const element = template?.querySelector(selector) as HTMLElement;

        if (element) {
          element.setAttribute('valign', valign);
        }
      } else if (['background', 'background-image', 'background-repeat', 'background-position-x', 'background-position-y', 'background-size'].includes(name)) {
        const originalBackgroundImage = getCssPropertyByName(selector, 'background-image', true, mobile);
        let backgroundImage = (name === 'background-image' ? value.replace('!important', '').trim() : getCssPropertyByName(selector, 'background-image', true, mobile));
        let backgroundRepeat = (name === 'background-repeat' ? value.replace('!important', '').trim() : getCssPropertyByName(selector, 'background-repeat', true, mobile));
        let backgroundPositionX = (name === 'background-position-x'
          ? value.replace('!important', '').trim()
          : getCssPropertyByName(selector, 'background-position-x', true, mobile));
        let backgroundPositionY = (name === 'background-position-y'
          ? value.replace('!important', '').trim()
          : getCssPropertyByName(selector, 'background-position-y', true, mobile));
        let backgroundSize = (name === 'background-size' ? value.replace('!important', '').trim() : getCssPropertyByName(selector, 'background-size', true, mobile));
        let addImportant = false;
        let originalBackgroundProperty: string|boolean = false;
        let originalBackgroundPropertyValue: string|boolean = false;

        // Force background property in order to recalculate all
        if ((name !== 'background' && typeof backgroundImage === 'string' && backgroundImage && backgroundImage !== 'none' && backgroundImage.match(/url\(/))
          || (typeof backgroundImage === 'string' && backgroundImage && backgroundImage === 'none')) {
          originalBackgroundProperty = name;
          originalBackgroundPropertyValue = value;
          if (name !== 'background') {
            name = 'background';
            value = getCssPropertyByName(selector, 'background-color', true, mobile) ?? '';
          }
          // eslint-disable-next-line max-len
          if (typeof originalBackgroundImage === 'string' && originalBackgroundImage && originalBackgroundImage !== 'none' && originalBackgroundImage.match(/(linear|radial)-gradient/)) {
            value = originalBackgroundImage.replace(/.*((linear|radial)-gradient.*\))$/, '$1');
          }
        }
        // Keep image background
        if (typeof backgroundImage === 'string' && backgroundImage && backgroundImage !== 'none' && backgroundImage.match(/url\(/)) {
          if (backgroundImage.match(/, (linear|radial)-gradient/)) {
            backgroundImage = backgroundImage.replace(/, (linear|radial)-gradient.*\)$/, '');
            if (typeof backgroundRepeat === 'string') {
              backgroundRepeat = backgroundRepeat.replace(/,.*$/, '');
            }
            if (typeof backgroundPositionX === 'string') {
              backgroundPositionX = backgroundPositionX.replace(/,.*$/, '');
            }
            if (typeof backgroundPositionY === 'string') {
              backgroundPositionY = backgroundPositionY.replace(/,.*$/, '');
            }
            if (typeof backgroundSize === 'string') {
              backgroundSize = backgroundSize.replace(/,.*$/, '');
            }
          }
          if (value.match('!important')) {
            addImportant = true;
            value = value.replace('!important', '').trim();
          }
          let isGradient = false;
          if (value.match('gradient')) {
            isGradient = value;
            value = `${backgroundImage}`;
          } else {
            value = `${value} ${backgroundImage}`;
          }

          if (backgroundRepeat) {
            value = ` ${value} ${backgroundRepeat}`;
          }
          if (backgroundPositionX) {
            value = `${value} ${backgroundPositionX}`;
          }
          if (backgroundPositionY) {
            value = `${value} ${backgroundPositionY}`;
          }
          if (backgroundSize) {
            value = `${value} / ${backgroundSize}`;
          }
          if (isGradient) {
            value = `${value}, ${isGradient}`;
          }
        }
        if (addImportant) {
          value = `${value} !important`;
        }
        if (typeof originalBackgroundProperty === 'string' && typeof originalBackgroundPropertyValue === 'string') {
          addOrUpdateDynamicStyle(selector, { [originalBackgroundProperty]: originalBackgroundPropertyValue }, mobile);
        }
      } else if ((name === 'font-size' || (name === 'line-height' && /^\d+(\.\d+)?(?:\s*!important)?$/.test(value))) && isEmailTemplate(TemplateEditorState.template?.type)) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        recalculateLineHeightValue(selector, property, mobile);
        // The recalculateLineHeightValue method will define the line height
        if (name === 'line-height') {
          updateDynamicStyle = false;
        }
      }

      if (updateDynamicStyle) {
        addOrUpdateDynamicStyle(selector, { [name]: value }, mobile);
      }
    } else {
      // The rule is empty, we need to remove the rule
      cleanCssByProperty(selector, property, mobile);
    }
    // Executed after the addOrUpdateDynamicStyle because the initial change is necessary for the calculation of the following properties
    if (['background', 'background-color'].includes(name)) {
      const { applyBackgroundReferenceFromSelector } = useEditorBackground();
      const template = getTemplateIframeDocument();
      applyBackgroundReferenceFromSelector(selector, template);
    } else if (mobile === false && (name === 'padding-left' || name === 'padding-right' || name === 'padding') && isEmailTemplate(TemplateEditorState.template?.type)) {
      fixImagesWidth(getTemplateIframeDocument(), selector);
    }
  }
};

(setCssProperty as any).isCssSetter = true;

export const setCssPropertyAndRemoveIfZero = (selector: string, property: Property, mobile = false) => {
  console.log('[setCssPropertyAndRemoveIfZero]', property.name, '=', property.value, '| mobile:', mobile);
  if (undefined !== property.value) {
    const { name, value } = property;
    if (value) {
      if (value === 0 || value === '0px' || value === '0%') {
        // If value equals 0 we remove the rule
        removeDynamicStyle(selector, { [name]: value }, mobile);

        // We then check if no parent is defining a rule that may affect the deleted property
        const template = getTemplateIframeDocument();
        const element = template?.querySelector(selector) as HTMLElement;

        if (element) {
          const css = window.getComputedStyle(element);
          const appliedRule = css.getPropertyValue(name);

          // If true, we need to restore the rule, else we can continue
          if (appliedRule && parseInt(appliedRule, 10) !== 0) {
            addOrUpdateDynamicStyle(selector, { [name]: value }, mobile);
          } else if (name === 'border-width') {
            // If property is border-width, we also remove the other properties
            BORDERS_PROPERTIES_TO_REMOVE_IF_ZERO.forEach((borderProperty) => {
              removeDynamicStyle(selector, { [borderProperty]: '' }, mobile);
            });
          }
        }
      } else {
        setCssProperty(selector, property, mobile);
      }
    } else {
      // The rule is empty, we need to remove the rule
      cleanCssByProperty(selector, property, mobile);
    }
  }
};

(setCssPropertyAndRemoveIfZero as any).isCssSetter = true;

export const setCssPropertyWithImportant = (selector: string, property: Property, mobile = false) => {
  const updatedProperty = { ...property };
  updatedProperty.value = `${property.value} !important`;
  setCssProperty(selector, updatedProperty, mobile);
};

(setCssPropertyWithImportant as any).isCssSetter = true;

export const setCssPropertyWithImportantAndRemoveIfZero = (selector: string, property: Property, mobile = false) => {
  const updatedProperty = { ...property };
  updatedProperty.value = `${property.value} !important`;
  setCssPropertyAndRemoveIfZero(selector, updatedProperty, mobile);
};

(setCssPropertyWithImportantAndRemoveIfZero as any).isCssSetter = true;

// Outlook font hack selector - these elements need explicit font-family for Outlook compatibility
const OUTLOOK_FONT_HACK_SELECTOR = 'h1,h2,h3,h4,h5,h6,p,a,span,td,strong,li';

export const setCssPropertyWithOutlookFontHack = (selector: string, property: Property, mobile = false) => {
  // First apply the normal CSS property
  setCssProperty(selector, property, mobile);
  // Only apply Outlook hack for font-family property on email templates (desktop only)
  if (property.name === 'font-family' && property.value && isEmailTemplate(TemplateEditorState.template?.type)) {
    // Add the Outlook hack rule in static stylesheet using forceStylesSheet parameter
    addOrUpdateDynamicStyle(OUTLOOK_FONT_HACK_SELECTOR, { 'font-family': property.value }, false, 'static_persistent');
  }
};

(setCssPropertyWithOutlookFontHack as any).isCssSetter = true;

export const updateOutlookFontHackWithComputedStyle = (selector: string) => {
  setTimeout(() => {
    const element = getTemplateIframeDocument()?.querySelector(selector) as HTMLElement;
    if (element) {
      const computedFontFamily = window.getComputedStyle(element).getPropertyValue('font-family');
      if (computedFontFamily) {
        addOrUpdateDynamicStyle(OUTLOOK_FONT_HACK_SELECTOR, { 'font-family': computedFontFamily }, false, 'static_persistent');
      }
    }
  }, 0);
};

let handleTimeout: ReturnType<typeof setTimeout> | null;
export const setAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (property.value !== null) {
        // If we change image src attribute, we need to fix the max-width according to the width of the image or the parent
        if (property.name === 'src') {
          const incomingValue = property.value;

          // Check if the value is a template variable expression (e.g. {var=shop.logo_url})
          if (isVariableExpression(incomingValue)) {
            const resolvedUrl = applyVariableToImage(element, incomingValue);
            if (resolvedUrl) {
              // Perform dimension calculation on the resolved URL
              const prevImage = element as HTMLImageElement;
              const prevImgWidth: string | number = prevImage.width || 0;
              element.setAttribute('src', '');

              let selectorParent;
              switch (TemplateEditorState.template?.type) {
                case TemplateTypeEnum.POPUP:
                case TemplateTypeEnum.EMBED:
                case TemplateTypeEnum.TEASER:
                  selectorParent = '.spm_widget_display_image';
                  break;
                case TemplateTypeEnum.PUSHNOTIFICATIONS:
                  selectorParent = '.push-notification-image';
                  break;
                case TemplateTypeEnum.RICHTEXTING:
                  selectorParent = '.rcs-message__header';
                  break;
                case TemplateTypeEnum.WHATSAPP:
                  selectorParent = '.whatsapp-message__header';
                  break;
                case TemplateTypeEnum.EMAIL:
                default:
                  selectorParent = '.spm_widget_image';
                  break;
              }

              let parent = getParents(element, selectorParent);
              if ((!parent || parent.length === 0) && isPushTemplate(TemplateEditorState.template?.type)) {
                parent = getParents(element, '.push-notification-icon');
              }

              if (parent && parent.length) {
                const parentElement = parent[0];
                const parentWidth = parentElement.offsetWidth;
                element.setAttribute('src', resolvedUrl);
                // eslint-disable-next-line no-param-reassign
                element.onload = () => {
                  const image = element as HTMLImageElement;
                  const imgWidth = image.naturalWidth;
                  const maxWidth = (imgWidth < parentWidth) ? imgWidth : parentWidth;
                  if (!prevImgWidth || prevImgWidth > maxWidth) {
                    addOrUpdateDynamicStyle(selector, { 'max-width': `${maxWidth}px` });
                    element.setAttribute('width', maxWidth);
                  }
                  store.dispatch('liveEditor/refreshWidgetFields');
                };
              }
            }
            store.dispatch('liveEditor/refreshWidgetFields');
          } else {
            // Normal URL: clear any previous variable binding
            if (element.hasAttribute(DATA_VAR_SRC)) {
              element.removeAttribute(DATA_VAR_SRC);
            }

          // Retrieve prev size of image
          const prevImage = element as HTMLImageElement;
          let prevImgWidth: string | number;
          if (prevImage.width) {
            prevImgWidth = prevImage.width;
          } else {
            prevImgWidth = 0;
          }
          // Set no src to remove image and calculate parent's width
          element.setAttribute(property.name, '');

          let selectorParent;
          switch (TemplateEditorState.template?.type) {
            case TemplateTypeEnum.POPUP:
            case TemplateTypeEnum.EMBED:
            case TemplateTypeEnum.TEASER:
              selectorParent = '.spm_widget_display_image';
              break;
            case TemplateTypeEnum.PUSHNOTIFICATIONS:
              selectorParent = '.push-notification-image';
              break;
            case TemplateTypeEnum.RICHTEXTING:
              selectorParent = '.rcs-message__header';
              break;
            case TemplateTypeEnum.WHATSAPP:
              selectorParent = '.whatsapp-message__header';
              break;
            case TemplateTypeEnum.EMAIL:
            default:
              selectorParent = '.spm_widget_image';
              break;
          }

          let parent = getParents(element, selectorParent);

          if ((!parent || parent.length === 0) && isPushTemplate(TemplateEditorState.template?.type)) {
            // Image is icon, not large image
            parent = getParents(element, '.push-notification-icon');
          }

          if (parent && parent.length) {
            const parentElement = parent[0];
            const parentWidth = parentElement.offsetWidth;

            // We set the correct src of the image
            element.setAttribute(property.name, property.value);
            // eslint-disable-next-line no-param-reassign
            element.onload = () => {
              // When image is loaded, we calculate its width
              const image = element as HTMLImageElement;
              const imgWidth = image.naturalWidth;

              // We set the proper CSS rule according to the lowest calculated width
              const maxWidth = (imgWidth < parentWidth) ? imgWidth : parentWidth;
              if (!prevImgWidth || prevImgWidth > maxWidth) {
                addOrUpdateDynamicStyle(selector, { 'max-width': `${maxWidth}px` });
                element.setAttribute('width', maxWidth);
              }
              store.dispatch('liveEditor/refreshWidgetFields');
            };
          }
          }
        } else {
          element.setAttribute(property.name, property.value);

          if (property.name === POPIN_AUTO_CLOSE_ATTRIBUTE) {
            if (property.value === '0' || property.value === '') {
              element.removeAttribute(property.name);
              element.classList.remove(POPIN_AUTO_CLOSE_CLASS);
            } else {
              element.classList.add(POPIN_AUTO_CLOSE_CLASS);
            }
          }
        }

        if (property.name !== POPIN_AUTO_CLOSE_ATTRIBUTE && !property.skipTranslation && TRANSLATABLE_ATTRIBUTES.includes(property.name)) {
          setTranslationInState(element, 'attribute', property.name);
        }
      }
    });
  }
};

/**
 * Set a data-attribute on a game wheel element and trigger a wheel re-render.
 * Used for wheel style properties (display mode, colors, sizes, etc.)
 * that require the canvas to be redrawn after the DOM attribute changes.
 */
export const setGameWheelAttribute = (selector: string, property: Property) => {
  setAttribute(selector, property);

  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (element) {
    const iframeWindow = (document.querySelector('iframe.template-wrapper') as HTMLIFrameElement)?.contentWindow as any;
    if (iframeWindow?.shopimind?.SPMGames?.refreshWheelByElement) {
      iframeWindow.shopimind.SPMGames.refreshWheelByElement(element);
    } else if ((element as any)?._spmGameWheel) {
      (element as any)._spmGameWheel.refresh();
    }
  }
};

/**
 * Convert a data-* attribute name to a CSS custom property name.
 * Example: 'data-wheel-size' → '--spm-game-wheel-size'
 */
const toGameCssVarName = (attrName: string): string => `--spm-game-${attrName.replace(/^data-/, '')}`;

/**
 * Refresh the game instance attached to the element matching the selector.
 */
const refreshGameInstance = (selector: string) => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (element) {
    const iframeWindow = (document.querySelector('iframe.template-wrapper') as HTMLIFrameElement)?.contentWindow as any;
    if (iframeWindow?.shopimind?.SPMGames) {
      if ((element as any)?.classList.contains('spm_widget_game_wheel')) {
        iframeWindow.shopimind.SPMGames.refreshWheelByElement(element);
      } else if ((element as any)?.classList.contains('spm_widget_game_slot_machine')) {
        iframeWindow.shopimind.SPMGames.refreshSlotMachineByElement(element);
      } else if ((element as any)?._spmGameScratchCard) {
        (element as any)._spmGameScratchCard.refresh();
      }
    }
  }
};

/**
 * Hybrid setter for game widget visual properties.
 * Writes a CSS custom property (supports mobile/desktop via the existing pipeline)
 * AND keeps the data-* attribute in sync for backward compatibility.
 * Triggers a game refresh after the update.
 */
export const setGameCssProperty = (selector: string, property: Property, mobile = false) => {
  if (property.value === undefined) return;

  const cssVarName = toGameCssVarName(property.name);
  addOrUpdateDynamicStyle(selector, { [cssVarName]: property.value }, mobile);

  // In desktop mode, also update the data-* attribute for backward compatibility
  if (!mobile) {
    setAttribute(selector, property);
  }

  refreshGameInstance(selector);
};
(setGameCssProperty as any).isCssSetter = true;

/**
 * Setter for slot machine visual attributes.
 * Sets the DOM attribute then triggers a refresh on the slot machine instance.
 */
/**
 * Setter for scratch card cover image — maps background-* properties to data-cover-image-* attributes.
 */
export const setGameCoverImageProp = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (!element) return;

  if (property.name === 'background-image') {
    const match = property.value?.match(/url\("?([^")]+)"?\)/);
    const url = match ? match[1] : '';
    element.setAttribute('data-cover-image', url);
  } else if (property.name === 'background-size') {
    element.setAttribute('data-cover-image-size', property.value || 'cover');
  } else if (property.name === 'background-position-x' || property.name === 'background-position-y') {
    element.setAttribute('data-cover-image-position', property.value || 'center');
  }

  // Refresh scratch card
  const widget = element.closest('.spm_widget_game_scratch_card') || element;
  if ((widget as any)?._spmGameScratchCard) {
    (widget as any)._spmGameScratchCard.refresh();
  }
};

/**
 * Setter for scratch card cover icon — maps background-image to img src.
 */
export const setGameCoverIconProp = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (!element) return;

  if (property.name === 'background-image') {
    const iconEl = element.querySelector('img.spm_game_scratch_card_cover_icon') as HTMLElement | null;
    const match = property.value?.match(/url\("?([^")]+)"?\)/);
    const url = match ? match[1] : '';
    element.setAttribute('data-cover-icon', url);
    if (iconEl) {
      iconEl.setAttribute('src', url);
    }
  }

  // Refresh scratch card
  const widget = element.closest('.spm_widget_game_scratch_card') || element;
  if ((widget as any)?._spmGameScratchCard) {
    (widget as any)._spmGameScratchCard.refresh();
  }
};

export const setGameScratchCardAttribute = (selector: string, property: Property) => {
  setAttribute(selector, property);

  // Find the scratch card widget (selector may point to a child element like the cover icon img)
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (element) {
    const widget = element.closest('.spm_widget_game_scratch_card') || element;
    if ((widget as any)?._spmGameScratchCard) {
      (widget as any)._spmGameScratchCard.refresh();
    }
  }
};

export const setGameSlotMachineAttribute = (selector: string, property: Property) => {
  setAttribute(selector, property);

  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector);
  if (element) {
    const iframeWindow = (document.querySelector('iframe.template-wrapper') as HTMLIFrameElement)?.contentWindow as any;
    if (iframeWindow?.shopimind?.SPMGames?.refreshSlotMachineByElement) {
      iframeWindow.shopimind.SPMGames.refreshSlotMachineByElement(element);
    } else if ((element as any)?._spmGameSlotMachine) {
      (element as any)._spmGameSlotMachine.refresh();
    }
  }
};

export const setCssButtonWidthProperty = (selector: string, property: Property) => {
  if (undefined !== property.value && (isEmailTemplate(TemplateEditorState.template?.type) || isDisplayTemplate(TemplateEditorState.template?.type))) {
    // eslint-disable-next-line prefer-const
    let { name, value } = property;
    // get padding left and right if width is auto
    if (value === 'auto') {
      let paddingSelector = selector;
      if (isEmailTemplate(TemplateEditorState.template?.type)) {
        paddingSelector += ' th';
      }
      const leftPadding = getCssPropertyByName(paddingSelector, 'padding-left', true);
      if (typeof leftPadding !== 'string' || (parseInt(leftPadding, 10) === 0)) {
        addOrUpdateDynamicStyle(paddingSelector, { 'padding-left': '10px' });
      }
      const rightPadding = getCssPropertyByName(paddingSelector, 'padding-right', true);
      if (typeof rightPadding !== 'string' || (parseInt(rightPadding, 10) === 0)) {
        addOrUpdateDynamicStyle(paddingSelector, { 'padding-right': '10px' });
      }
    }
    if (isDisplayTemplate(TemplateEditorState.template?.type) && selector.match('spm_generate_voucher_button')) {
      if (value === 'auto') {
        addOrUpdateDynamicStyle(selector, { display: 'inline-block' });
      } else {
        removeDynamicStyle(selector, { display: '' });
      }
    }
    if (isDisplayTemplate(TemplateEditorState.template?.type)) {
      setCssPropertyWithImportant(selector, property);
    } else {
      setCssProperty(selector, property);
    }
  }
};

/**
 * Set button width using display property for email templates (V4)
 * display: block = full width, display: inline-block = fit to text
 * Adds default padding when switching to inline-block mode
 */
export const setCssButtonDisplayProperty = (selector: string, property: Property, mobile = false) => {
  if (undefined === property.value || !isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const forceStylesSheet = mobile ? 'dynamic_persistent_mobile' as const : false as const;
  const { value } = property;

  if (value === 'inline-block') {
    // Remove any width constraint to allow inline-block to size to content
    removeDynamicStyle(selector, { width: '' }, mobile, forceStylesSheet);
    // Add padding left and right if not already set
    const leftPadding = getCssPropertyByName(selector, 'padding-left', true);
    if (typeof leftPadding !== 'string' || (parseInt(leftPadding, 10) === 0)) {
      addOrUpdateDynamicStyle(selector, { 'padding-left': '10px' }, mobile, forceStylesSheet);
    }
    const rightPadding = getCssPropertyByName(selector, 'padding-right', true);
    if (typeof rightPadding !== 'string' || (parseInt(rightPadding, 10) === 0)) {
      addOrUpdateDynamicStyle(selector, { 'padding-right': '10px' }, mobile, forceStylesSheet);
    }
  }
  if(mobile === true) {
    setCssPropertyWithImportant(selector, property, mobile);
  } else {
    setCssProperty(selector, property, mobile);
  }

};

(setCssButtonDisplayProperty as any).isCssSetter = true;

export const setAttributeByName = (name: string) => (selector: string, property: Property) => {
  const updatedProperty = { ...property };
  updatedProperty.name = name;
  setAttribute(selector, updatedProperty);
};

export const setContent = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element && undefined !== property.value) {
    // This setter can be involved by translation
    sanitizeTranslationMarkup(element).innerHTML = property.value;
    setTranslationInState(element, 'markup');
  }
};

export const setClass = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      const classes = element.getAttribute('class')?.split(' ').filter((className: string) => !className.startsWith(property.name));
      if (classes) {
        classes.push(`${property.name}${property.value}`);
        element.setAttribute('class', (classes.join(' ') ?? ''));
      }
    });
  }
};

export const setPositionClass = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      // Reset all position-related properties to avoid conflicts
      element.style.top = '';
      element.style.bottom = '';
      element.style.left = '';
      element.style.right = '';
      element.style.transform = '';
      element.style.transformOrigin = '';
      element.style.margin = '';
      element.style.marginTop = '';
      element.style.marginBottom = '';
      element.style.marginLeft = '';
      element.style.marginRight = '';

      // Update class
      const classes = element.getAttribute('class')?.split(' ').filter((className: string) => !className.startsWith(property.name));
      if (classes) {
        classes.push(`${property.name}${property.value}`);
        element.setAttribute('class', (classes.join(' ') ?? ''));
      }
    });
  }
};

export const setTeaserWidth = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      element.style.setProperty(property.name, property.value);
      // Also update #spm_body width for correct positioning
      const spmBody = template?.querySelector('#spm_body.spm_teaser') as HTMLElement;
      if (spmBody && property.name === 'width') {
        spmBody.style.width = property.value;
        // For circle shape, sync height with width to maintain perfect circle
        if (spmBody.classList.contains('spm_shape_circle')) {
          element.style.height = property.value;
          element.style.minHeight = property.value;
          element.style.maxHeight = property.value;
        }
      }
    });
  }
};

export const setVoucherIconClass = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      const classes = element.getAttribute('class')?.split(' ').filter((className: string) => !className.startsWith(property.name));
      if (classes) {
        classes.push(`${property.name}${property.value}`);
        element.setAttribute('class', (classes.join(' ') ?? ''));
        const parentElement = getFirstParent(element, '.spm_generate_voucher_button');
        if (!classes.join(' ').match('spm_icon-hidden')) {
          parentElement.classList.add('spm_with_icon');
        } else {
          parentElement.classList.remove('spm_with_icon');
        }
      }
    });
  }
};

export const toggleClass = (selector: string, property: Property, options: any = null) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  const value = typeof options === 'object' && options !== null && options.revert === true ? 'false' : 'true';
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (property.value === value) {
        element.classList.add(property.name);
      } else {
        element.classList.remove(property.name);
      }
    });
  }
  if (options && options.callback) {
    options.callback();
  }
};

export const toggleClassName = (selector: string, className: string) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  elements.forEach((element: HTMLElement) => {
    if (element.classList.contains(className)) {
      element.classList.remove(className);
    } else {
      element.classList.add(className);
    }
  });
};

export const toggleClassAndRemoveFromChild = (selector: string, property: Property, options: any = null) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  const value = typeof options === 'object' && options !== null && options.revert === true ? 'false' : 'true';

  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (property.value === value) {
        element.classList.add(property.name);
      } else {
        element.classList.remove(property.name);
        // Supprime la classe de tous les enfants
        Array.from(element.querySelectorAll(`.${property.name}`)).forEach((child) => {
          (child as HTMLElement).classList.remove(property.name);
        });
      }
    });
  }
};

export const setTagType = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement|null = template?.querySelector(selector);
  if (element) {
    element.outerHTML = `<${property.value}>${element.innerHTML}</${property.value}>`;
  }
};

export const setAnimated = (selector: string) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      element.classList.add('spm_animated');
    });
  }
};

export const setFacebookButtons = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  let buttonsContainer: HTMLElement | null = template?.querySelector(selector);

  if (!buttonsContainer) {
    // In case the widget doesn't have the container, we create it
    const regExpWidgetId = new RegExp('#spm_widget_[a-zA-Z0-9]+');
    const matches = regExpWidgetId.exec(selector);

    if (matches) {
      const widgetId = Array.from(matches)[0];
      const widget = template.querySelector(widgetId);

      if (widget) {
        const div = document.createElement('div');
        div.classList.add('buttons', 'fbButtons');
        widget.append(div);
        buttonsContainer = template?.querySelector(selector);
      }
    }
  }

  if (buttonsContainer) {
    buttonsContainer.innerHTML = '';
    const parentNode = buttonsContainer.closest('.spm_draggable_widget');

    if (parentNode) {
      parentNode.classList.remove('fb_widget_with_buttons');
    }

    property.value.forEach((button: FacebookMessengerButton) => {
      const placeholder: HTMLElement = document.createElement('div');
      const { newHtml } = generateTranslations(facebookButtonStructure(), { newElement: true });
      placeholder.innerHTML = newHtml;
      const newButton = placeholder.querySelector<HTMLElement>('.button');

      if (newButton) {
        if (button.dataRedirect !== '') {
          const dataRedirect = button.action === ActionTypeEnum.REDIRECT ? `base64:${btoa(button.dataRedirect)}` : `#redirect_${btoa(button.dataRedirect)}`;
          newButton.setAttribute('data-redirect', dataRedirect);
          setTranslationInState(newButton, 'attribute', 'data-redirect');
        }

        sanitizeTranslationMarkup(newButton).innerHTML = button.text;
        setTranslationInState(newButton, 'markup');

        if (buttonsContainer) {
          buttonsContainer.append(newButton);
        }
      }
    });

    if (property.value.length && parentNode) {
      parentNode.classList.add('fb_widget_with_buttons');
    }
  }
};

export const setFacebookMessengerSkin = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);

  if (element) {
    const parent: HTMLElement | null = element.parentElement;

    if (parent) {
      const image: HTMLElement | null = parent.querySelector('img');

      if (image) {
        const newSkin: string = property.value;
        const newUrl: string = (FacebookMessengerSkinsUrlPreview as { [key: string]: string })[newSkin.toUpperCase()];

        image.setAttribute('src', newUrl);
      }
    }
  }
};

export const calculateSmsLimits = (message: string): {limitFirst: number; limitMultiple: number} => {
  const regexpSpecialChars = new RegExp('[Þ°¨«»„…•–—´`]', 'g');

  let limitFirst;
  let limitMultiple;

  if (regexpSpecialChars.test(message)) {
    limitFirst = 70;
    limitMultiple = 67;
  } else {
    limitFirst = 160;
    limitMultiple = 153;
  }

  return { limitFirst, limitMultiple };
};

export const calculateSmsLength = (message: string): {nbMessages: number; nbCharactersMax: number; returnMessage: string} => {
  const { t } = useI18n();
  const availableVariables: Variable[] = VariablesList.allVariables;

  const maxMessages = 5;
  const { limitFirst, limitMultiple } = calculateSmsLimits(message);

  // On remplace toutes les variables pouvant contenir une URL par une URL raccourcie
  let body = message.replace(/((http|ftp)+(s)?:\/\/[^<>\s]+)|(CUSTOM_LINK)|(SITE_URL_W_HTTP)|(SITE_URL)/gi, 's6m.me/12345');

  availableVariables.forEach((variable: Variable) => {
    body = body.replace(new RegExp(variable.value, 'gi'), '');
  });

  let bodyLength = body.length;

  // On compte double certains caractères
  body = body.replace(new RegExp('[|^€{}\\[\\]\\~\\\\]', 'g'), '');
  bodyLength += bodyLength - body.length;

  let nbMessages = (bodyLength + 15 > limitFirst) ? Math.ceil((bodyLength + 15) / limitMultiple) : 1;
  if (nbMessages > maxMessages) {
    nbMessages = maxMessages;
  }

  // @TODO : corriger le bug de comptage (présent en prod également) lorsque des variables sont présentes dans le texte, afin de limiter la saisie dans le champ textarea

  const nbCharactersMax = nbMessages > 1 ? ((nbMessages * limitMultiple) - 15) : (limitFirst - 15);

  // On supprime du tableau des variables celles qui correspondent à des liens, vu que l'on connait la longueur finale de la chaine
  const availableVariablesWithoutLinks = availableVariables.filter((variable: Variable) => (!['CUSTOM_LINK', 'SITE_URL_W_HTTP', 'SITE_URL'].includes(variable.value)))
    .map((variable: Variable) => variable.value);

  const regexpVariables = new RegExp(`(${availableVariablesWithoutLinks.join(')|(')})`, 'g');

  let returnMessage;
  if (regexpVariables.test(message)) {
    // On a d'autres variables dans le message, donc on va afficher le bon message
    returnMessage = t('templateBuilder.informations.smsInformationMessageVariables', {
      nbMessages,
      smsUnitPrice: '0.07',
    });
  } else {
    // On a pas d'autre variable, on va calculer la longueur du message
    returnMessage = t('templateBuilder.informations.smsInformationMessageStandard', {
      nbCharacters: bodyLength,
      nbCharactersMax,
      nbMessages,
      smsUnitPrice: '0.07',
    });
  }

  return { nbMessages, nbCharactersMax, returnMessage };
};

export const getCssStyles = (type: string, beautify = false): string => {
  const template = getTemplateIframeDocument();

  if (!template) {
    return '';
  }

  const selector = `style[data-spm-styles="${type}"]`;

  const css: HTMLElement | null = template.querySelector(selector);

  if (!css) {
    return '';
  }

  return beautify ? beautifier.css(css.innerHTML.replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, '')) : css.innerHTML.replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, '');
};

export const setCssStyles = (type: string, value: string, sectionId: string | undefined): void => {
  const template = getTemplateIframeDocument();

  if (template) {
    const selector = `style[data-spm-styles="${type}"]`;
    let css = template.querySelector(selector) as HTMLStyleElement;

    if (!css) {
      const head = template.querySelector('head');

      if (head) {
        css = document.createElement('style');
        css.setAttribute('data-spm-styles', type);
        if (sectionId) {
          css.setAttribute('data-spm-section', sectionId);
        }
        head.appendChild(css);
      }
    }

    if (css) {
      css.innerText = beautifier.css(value).replace(/(\r\n|\n|\r|<br>)/gm, '');
    }
  }
};

// Debounce map for recalculateLineHeightValue to handle rapid clicks
const recalculateLineHeightDebounceMap: Map<string, ReturnType<typeof setTimeout>> = new Map();

// Store the calculated coefficient per selector - NO EXPIRATION
// The coefficient is only recalculated when line-height is explicitly changed by the user
const storedCoefficientMap: Map<string, number> = new Map();

// Allowed line-height coefficient values
const ALLOWED_LINE_HEIGHT_COEFFICIENTS = ['1', '1.1', '1.3', '1.5', '1.75', '2', '3'];

/**
 * Find the closest allowed coefficient to a given value
 */
const findClosestCoefficient = (value: number): number => {
  return parseFloat(
    ALLOWED_LINE_HEIGHT_COEFFICIENTS.reduce((prev, curr) =>
      Math.abs(parseFloat(curr) - value) < Math.abs(parseFloat(prev) - value) ? curr : prev,
    ),
  );
};

/**
 * Get the current line-height value for a selector.
 * Priority: dynamic styles > computed style (with inline temporarily removed)
 */
const getCurrentLineHeight = (selector: string, mobile: boolean): string | undefined => {
  const dynamicStyles = getDynamicStylesForSelector(selector, mobile);

  // 1. Check dynamic styles first
  if (dynamicStyles['line-height']) {
    return dynamicStyles['line-height'];
  }

  // 2. Fallback to computed style (temporarily remove inline to get inherited value)
  const elements = getTemplateIframeDocument()?.querySelectorAll(selector);
  const firstElement = elements?.[0] as HTMLElement;
  if (firstElement) {
    const originalInlineLineHeight = firstElement.style.lineHeight;
    firstElement.style.removeProperty('line-height');
    const computedLineHeight = window.getComputedStyle(firstElement).getPropertyValue('line-height');
    if (originalInlineLineHeight) {
      firstElement.style.lineHeight = originalInlineLineHeight;
    }
    return computedLineHeight;
  }

  return undefined;
};

/**
 * Get the current font-size for a selector from computed style.
 * This is called BEFORE debounce to capture the old value before DOM updates.
 */
const getCurrentFontSize = (selector: string): string | undefined => {
  const elements = getTemplateIframeDocument()?.querySelectorAll(selector);
  const firstElement = elements?.[0] as HTMLElement;
  if (firstElement) {
    return window.getComputedStyle(firstElement).getPropertyValue('font-size');
  }
  return undefined;
};

/**
 * Recalculate line-height when font-size or line-height changes.
 *
 * Two cases:
 * 1. font-size changes → recalculate line-height (in px) preserving the coefficient
 * 2. line-height changes (coefficient like 1.5) → convert to px using current font-size
 */
export const recalculateLineHeightValue = (selector: string, property: Property, mobile = false) => {
  // Only for email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const mobileAsBool = typeof mobile === 'boolean' ? mobile : false;

  // CRITICAL: Capture old values BEFORE debounce, because the DOM will be updated
  // by addOrUpdateDynamicStyle before the debounced function executes.
  // The browser recalculates computed line-height when font-size changes (if line-height is a coefficient).
  const oldFontSize = getCurrentFontSize(selector);
  const oldLineHeight = getCurrentLineHeight(selector, mobileAsBool);

  // Debounce to handle rapid clicks
  const debounceKey = `${selector}-${mobile}`;
  const existingTimeout = recalculateLineHeightDebounceMap.get(debounceKey);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeoutId = setTimeout(() => {
    recalculateLineHeightDebounceMap.delete(debounceKey);
    executeRecalculateLineHeight(selector, property, mobileAsBool, oldFontSize, oldLineHeight);
  }, 50);

  recalculateLineHeightDebounceMap.set(debounceKey, timeoutId);
};

(recalculateLineHeightValue as any).isCssSetter = true;

/**
 * Execute the line-height recalculation after debounce.
 */
const executeRecalculateLineHeight = (
  selector: string,
  property: Property,
  mobile: boolean,
  oldFontSize: string | undefined,
  oldLineHeight: string | undefined,
) => {
  if (property.value === undefined) {
    return;
  }

  const elements = getTemplateIframeDocument()?.querySelectorAll(selector);
  if (!elements || elements.length === 0) {
    return;
  }

  let newLineHeight: number | undefined;

  if (property.name === 'font-size') {
    // CASE 1: Font-size changed → recalculate line-height preserving coefficient
    const newFontSize = property.value;
    const newFontSizePx = parseFloat(newFontSize);
    const coefficientKey = `${selector}-${mobile}`;

    // Check if we have a stored coefficient (no expiration - always use if available)
    const storedCoefficient = storedCoefficientMap.get(coefficientKey);

    let coefficient: number;

    if (storedCoefficient !== undefined) {
      // Use the stored coefficient (avoids timing issues with DOM updates)
      coefficient = storedCoefficient;
    } else {
      // Calculate coefficient from captured values
      if (!oldLineHeight || oldLineHeight === 'normal' || oldLineHeight === '0px') {
        return;
      }

      if (!oldFontSize || oldFontSize === '0px') {
        return;
      }

      // Line-height should be in px (from dynamic or computed)
      if (!oldLineHeight.match(/px(?:\s*!important)?$/)) {
        return;
      }

      const lineHeightPx = parseFloat(oldLineHeight);
      const oldFontSizePx = parseFloat(oldFontSize);

      // Calculate coefficient and snap to closest allowed value
      const rawCoefficient = lineHeightPx / oldFontSizePx;
      coefficient = findClosestCoefficient(rawCoefficient);
    }

    // Store the coefficient for future use
    storedCoefficientMap.set(coefficientKey, coefficient);

    // Calculate new line-height
    newLineHeight = Math.round(coefficient * newFontSizePx);
  } else if (property.name === 'line-height') {
    // CASE 2: Line-height changed (coefficient → px conversion)
    const lineHeightValue = property.value;
    const coefficientKey = `${selector}-${mobile}`;

    // Check if it's a coefficient (number without unit) or percentage
    if (/^\d+(\.\d+)?(?:\s*!important)?$/.test(lineHeightValue)) {
      // Coefficient like 1.5
      const coefficient = parseFloat(lineHeightValue);
      const currentFontSize = getCurrentFontSize(selector);

      if (currentFontSize && currentFontSize !== '0px') {
        newLineHeight = Math.round(coefficient * parseFloat(currentFontSize));

        // Store this new coefficient for future font-size changes
        storedCoefficientMap.set(coefficientKey, coefficient);
      }
    } else if (lineHeightValue.match(/%(?:\s*!important)?$/)) {
      // Percentage like 150%
      const percentage = parseFloat(lineHeightValue) / 100;
      const currentFontSize = getCurrentFontSize(selector);

      if (currentFontSize && currentFontSize !== '0px') {
        newLineHeight = Math.round(percentage * parseFloat(currentFontSize));

        // Store this coefficient for future font-size changes
        storedCoefficientMap.set(coefficientKey, percentage);
      }
    }
    // If already in px, no conversion needed - let setCssProperty handle it directly
  }

  // Apply the new line-height if calculated
  if (newLineHeight) {
    // Update inline styles for elements that have them (e.g. WYSIWYG)
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement.style.lineHeight) {
        htmlElement.style.lineHeight = `${newLineHeight}px`;
      }
    });

    // Update dynamic stylesheet
    if (mobile === true) {
      setCssPropertyWithImportant(selector, {
        name: 'line-height',
        value: `${newLineHeight}px`,
        getter: () => '',
        setters: [],
      }, mobile);
    } else {
      addOrUpdateDynamicStyle(selector, { 'line-height': `${newLineHeight}px` }, mobile);
    }
  }
};

/**
 * Handle line-height recalculation when font-size or line-height is removed (unchecked).
 * This should be called BEFORE removeDynamicStyle to capture old values.
 *
 * @param selector - CSS selector
 * @param propertyName - 'font-size' or 'line-height'
 * @param mobile - mobile mode
 * @returns An object with oldFontSize to be used after removal, or undefined
 */
export const prepareLineHeightRecalculationBeforeRemoval = (
  selector: string,
  propertyName: string,
  mobile = false,
): { oldFontSize: string | undefined } | undefined => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return undefined;
  }

  if (propertyName === 'font-size') {
    // Capture old font-size BEFORE removal
    const oldFontSize = getCurrentFontSize(selector);
    return { oldFontSize };
  }

  if (propertyName === 'line-height') {
    // Clear the stored coefficient when line-height is removed
    const coefficientKey = `${selector}-${mobile}`;
    storedCoefficientMap.delete(coefficientKey);
    // Return empty object to signal that line-height recalculation is needed
    return { oldFontSize: undefined };
  }

  return undefined;
};

/**
 * Handle line-height recalculation AFTER font-size is removed.
 * This recalculates line-height using the inherited font-size.
 *
 * @param selector - CSS selector
 * @param oldFontSize - The font-size before removal (captured by prepareLineHeightRecalculationBeforeRemoval)
 * @param mobile - mobile mode
 */
export const executeLineHeightRecalculationAfterFontSizeRemoval = (
  selector: string,
  oldFontSize: string | undefined,
  mobile = false,
): void => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  if (!oldFontSize || oldFontSize === '0px') {
    return;
  }

  const mobileAsBool = typeof mobile === 'boolean' ? mobile : false;
  const coefficientKey = `${selector}-${mobileAsBool}`;

  // Get current line-height from dynamic styles
  const dynamicStyles = getDynamicStylesForSelector(selector, mobileAsBool);
  const currentLineHeight = dynamicStyles['line-height'];

  if (!currentLineHeight || !currentLineHeight.match(/px(?:\s*!important)?$/)) {
    return;
  }

  // Get the new (inherited) font-size after removal
  const newFontSize = getCurrentFontSize(selector);
  if (!newFontSize || newFontSize === '0px') {
    return;
  }

  const lineHeightPx = parseFloat(currentLineHeight);
  const oldFontSizePx = parseFloat(oldFontSize);
  const newFontSizePx = parseFloat(newFontSize);

  // Check if we have a stored coefficient, otherwise calculate it
  let coefficient = storedCoefficientMap.get(coefficientKey);

  if (coefficient === undefined) {
    // Calculate coefficient from old values
    const rawCoefficient = lineHeightPx / oldFontSizePx;
    coefficient = findClosestCoefficient(rawCoefficient);
    storedCoefficientMap.set(coefficientKey, coefficient);
  }

  // Calculate new line-height
  const newLineHeight = Math.round(coefficient * newFontSizePx);

  // Update inline styles for elements that have them
  const elements = getTemplateIframeDocument()?.querySelectorAll(selector);
  if (elements) {
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      if (htmlElement.style.lineHeight) {
        htmlElement.style.lineHeight = `${newLineHeight}px`;
      }
    });
  }

  // Update dynamic stylesheet
  if (mobileAsBool === true) {
    setCssPropertyWithImportant(selector, {
      name: 'line-height',
      value: `${newLineHeight}px`,
      getter: () => '',
      setters: [],
    }, mobileAsBool);
  } else {
    addOrUpdateDynamicStyle(selector, { 'line-height': `${newLineHeight}px` }, mobileAsBool);
  }
};

/**
 * Handle line-height recalculation AFTER line-height is removed.
 * This converts the inherited line-height (coefficient) to px.
 *
 * @param selector - CSS selector
 * @param mobile - mobile mode
 */
export const executeLineHeightRecalculationAfterLineHeightRemoval = (
  selector: string,
  mobile = false,
): void => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const mobileAsBool = typeof mobile === 'boolean' ? mobile : false;

  // Get the inherited line-height after removal (from static stylesheet or computed)
  const elements = getTemplateIframeDocument()?.querySelectorAll(selector);
  const firstElement = elements?.[0] as HTMLElement;

  if (!firstElement) {
    return;
  }

  // Remove inline line-height temporarily to get inherited value
  const originalInlineLineHeight = firstElement.style.lineHeight;
  firstElement.style.removeProperty('line-height');
  const inheritedLineHeight = window.getComputedStyle(firstElement).getPropertyValue('line-height');

  // Restore inline if it existed
  if (originalInlineLineHeight) {
    firstElement.style.lineHeight = originalInlineLineHeight;
  }

  if (!inheritedLineHeight || inheritedLineHeight === 'normal' || inheritedLineHeight === '0px') {
    return;
  }

  // Get current font-size (may be dynamic)
  const currentFontSize = getCurrentFontSize(selector);
  if (!currentFontSize || currentFontSize === '0px') {
    return;
  }

  // IMPORTANT: Get the INHERITED font-size (from static stylesheet) to calculate the correct coefficient
  // We need to temporarily remove the dynamic font-size to get the inherited value
  const dynamicStyles = getDynamicStylesForSelector(selector, mobileAsBool);
  const dynamicFontSize = dynamicStyles['font-size'];

  let inheritedFontSize = currentFontSize;
  if (dynamicFontSize) {
    // Temporarily remove font-size from dynamic stylesheet to get inherited value
    removeDynamicStyle(selector, { 'font-size': 'font-size' }, mobileAsBool);

    // Also remove inline font-size temporarily
    const originalInlineFontSize = firstElement.style.fontSize;
    if (originalInlineFontSize) {
      firstElement.style.removeProperty('font-size');
    }

    // Get the inherited font-size
    inheritedFontSize = window.getComputedStyle(firstElement).getPropertyValue('font-size');

    // Restore inline if it existed
    if (originalInlineFontSize) {
      firstElement.style.fontSize = originalInlineFontSize;
    }

    // Restore dynamic font-size
    addOrUpdateDynamicStyle(selector, { 'font-size': dynamicFontSize }, mobileAsBool);
  }

  let newLineHeight: number | undefined;
  const currentFontSizePx = parseFloat(currentFontSize);
  const inheritedFontSizePx = parseFloat(inheritedFontSize);

  // Check if inherited line-height is a coefficient (already computed to px by browser)
  // or if it's already in px format
  if (inheritedLineHeight.match(/px$/)) {
    // Browser has already computed it to px - this means the static stylesheet has a coefficient
    // Calculate coefficient using INHERITED font-size (not current/dynamic)
    const lineHeightPx = parseFloat(inheritedLineHeight);
    const rawCoefficient = lineHeightPx / inheritedFontSizePx;
    const coefficient = findClosestCoefficient(rawCoefficient);

    // Store the coefficient
    const coefficientKey = `${selector}-${mobileAsBool}`;
    storedCoefficientMap.set(coefficientKey, coefficient);

    // Calculate new line-height using CURRENT font-size (which may be dynamic)
    newLineHeight = Math.round(coefficient * currentFontSizePx);
  }

  if (newLineHeight) {
    // Update inline styles for elements that have them
    if (elements) {
      elements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        if (htmlElement.style.lineHeight) {
          htmlElement.style.lineHeight = `${newLineHeight}px`;
        }
      });
    }

    // Update dynamic stylesheet
    if (mobileAsBool === true) {
      setCssPropertyWithImportant(selector, {
        name: 'line-height',
        value: `${newLineHeight}px`,
        getter: () => '',
        setters: [],
      }, mobileAsBool);
    } else {
      addOrUpdateDynamicStyle(selector, { 'line-height': `${newLineHeight}px` }, mobileAsBool);
    }
  }
};

/**
 * VML measurements do not accept CSS pixel units. An invalid value is discarded by Word, which
 * then applies its own default inset (0.1in,0.05in,0.1in,0.05in) and shrinks the block in Outlook.
 */
const cssLengthToVmlLength = (value: string): string => {
  const pixels = parseFloat(value);

  if (!Number.isFinite(pixels) || pixels === 0) {
    return '0';
  }

  return `${Math.round(pixels * 0.75 * 100) / 100}pt`;
};

/**
 * The cell whose content the VML shape must wrap. A shape placed as a direct child of a row sits
 * beside the cell instead of behind its content: Word treats it as one more cell, the row grows by
 * the shape width and every centred block shifts. Given a row, the shape goes inside its first cell;
 * given a cell (column background), it goes inside that cell.
 */
const getVmlHostCell = (element: HTMLElement): HTMLElement => {
  if (element.tagName === 'TR') {
    return ((element as HTMLTableRowElement).cells[0] as HTMLElement | undefined) ?? element;
  }
  return element;
};

/**
 * URL of an element's background image. The editor stores it in the background attribute for email
 * templates; the computed style only stands in for markup that never went through the editor, since
 * it may still carry a stale inline image from an earlier tool.
 */
const getBackgroundImageUrl = (element: HTMLElement): string => {
  const attribute = element.getAttribute('background');
  if (attribute) return attribute;
  const match = window.getComputedStyle(element).backgroundImage.match(/url\(['"]?([^'"]*?)['"]?\)/);
  return match ? match[1] : '';
};

// Elements that get a background shape at save time when they carry a background image
const BACKGROUND_VML_HOST_SELECTOR = '.spm_section, .spm_rich_editor_wrapper, .container, .spm_draggable_widget, [style*="background-image"]';

const PX_LENGTH = /^\d+(\.\d+)?px$/;
const PERCENT_LENGTH = /^\d+(\.\d+)?%$/;

/**
 * Width an element declares: the stylesheets first, as they override it, then its width attribute.
 */
const getDeclaredWidth = (element: HTMLElement): string => {
  const width = getCascadedStyleValue(element, 'width');
  if (width) return width;
  const attribute = (element.getAttribute('width') || '').trim();
  return /^\d+(\.\d+)?$/.test(attribute) ? `${attribute}px` : attribute;
};

/**
 * Horizontal paddings a table cell declares, in px.
 */
const getDeclaredHorizontalPadding = (element: HTMLElement): number => {
  if (element.tagName !== 'TD' && element.tagName !== 'TH') return 0;
  return ['padding-left', 'padding-right']
    .map((property) => getCascadedStyleValue(element, property))
    .reduce((total, padding) => total + (PX_LENGTH.test(padding) ? parseFloat(padding) : 0), 0);
};

const BORDER_SIDES = ['top', 'right', 'bottom', 'left'];

const getDeclaredBorderWidth = (element: HTMLElement, side: string): number => {
  if ((getCascadedStyleValue(element, `border-${side}-style`) || 'none') === 'none') return 0;
  return parseFloat(getCascadedStyleValue(element, `border-${side}-width`)) || 0;
};

const VML_CONTENT_ATTRIBUTE = 'data-spm-vml-content';
// The Word-only declarations the VML repair adds are recorded on their element: only they are removed
const VML_OFFSET_RECORD = 'data-spm-vml-offset';
const VML_FRAME_RECORD = 'data-spm-vml-frame';

/**
 * Horizontal borders Word takes out of the width a table declares, in px: only those the VML repair turned
 * into a padding, as Word draws any other around the table.
 */
const getConvertedHorizontalBorders = (element: HTMLElement): number => (
  element.tagName === 'TABLE' && element.hasAttribute(VML_FRAME_RECORD)
    ? getDeclaredBorderWidth(element, 'left') + getDeclaredBorderWidth(element, 'right') : 0
);

/**
 * Percentage of its container an element spans. Table layout spreads a row whose cells all declare a
 * percentage over the whole row, in proportion: a lone 67% column spans its row. A products list cell is
 * no lone cell: the sending fills its row with as many products as its grid takes.
 */
const getEffectivePercentage = (element: HTMLElement, width: string): number => {
  const percentage = parseFloat(width);
  if ((element.tagName !== 'TD' && element.tagName !== 'TH') || element.hasAttribute('s-grid')) return percentage;
  const rowWidths = Array.from(element.parentElement?.children ?? []).map((cell) => getDeclaredWidth(cell as HTMLElement));
  if (!rowWidths.every((rowWidth) => PERCENT_LENGTH.test(rowWidth))) return percentage;
  const total = rowWidths.reduce((sum, rowWidth) => sum + parseFloat(rowWidth), 0);
  return total > 0 && total < 100 ? (percentage * 100) / total : percentage;
};

/**
 * Pixel width Word gives the content of an element, from the closest ancestor that declares a width:
 * percentages are resolved on the way, the paddings of the cells and the converted borders of the tables
 * crossed are taken out. A pixel width is the content of its element, converted borders aside, a
 * percentage fills its parent paddings included. 0 when nothing declares one.
 */
const getContainingPixelWidth = (element: HTMLElement | null): number => {
  let insets = 0;
  for (let node = element; node; node = node.parentElement) {
    const width = getDeclaredWidth(node);
    insets += getConvertedHorizontalBorders(node);
    if (PX_LENGTH.test(width)) {
      return Math.max(0, Math.round(parseFloat(width) - insets));
    }
    insets += getDeclaredHorizontalPadding(node);
    if (PERCENT_LENGTH.test(width)) {
      const containingWidth = getContainingPixelWidth(node.parentElement);
      return containingWidth
        ? Math.max(0, Math.round((getEffectivePercentage(node, width) / 100) * containingWidth - insets))
        : 0;
    }
  }
  return 0;
};

// Spacing an earlier repair wrote without a record; an author's zero spacing is left alone
const LEGACY_VML_TABLE_OFFSET = /;?\s*mso-table-lspace:\s*(?!0(?:pt|px)?\s*(?:;|$))[^;]*/;

const appendStyleDeclarations = (element: Element, declarations: string) => {
  // The lone border-collapse the editor puts on text wrappers is dropped at send; keep it that way
  const style = (element.getAttribute('style') || '').trim().replace(/;$/, '').replace(/^border-collapse:\s*collapse$/, '');
  element.setAttribute('style', `${style ? `${style}; ` : ''}${declarations}`);
};

const removeStyleDeclarations = (element: Element, pattern: RegExp) => {
  const current = element.getAttribute('style') || '';
  const style = current.replace(pattern, '').trim();
  if (style === current.trim()) return;
  if (style) {
    element.setAttribute('style', style);
  } else {
    element.removeAttribute('style');
  }
};

const addVmlDeclarations = (element: Element, record: string, declarations: string) => {
  appendStyleDeclarations(element, declarations);
  const recorded = element.getAttribute(record);
  element.setAttribute(record, recorded ? `${recorded}; ${declarations}` : declarations);
};

const removeVmlDeclarations = (element: Element, record: string) => {
  const recorded = element.getAttribute(record);
  if (recorded === null) return;
  element.removeAttribute(record);
  recorded.split(';').map((declaration) => declaration.trim()).filter(Boolean).forEach((declaration) => {
    removeStyleDeclarations(element, new RegExp(`;?\\s*${escapeRegExp(declaration)}(?=\\s*(?:;|$))`));
  });
};

// Ghost tables only Outlook 2013 and older read: they ignore the table spacings
const VML_GHOST_ATTRIBUTE = 'data-spm-vml-ghost';
const VML_GHOST_OPENING = new RegExp(`^\\[if lte mso 15\\]>\\s*<table ${VML_GHOST_ATTRIBUTE}`);
const VML_GHOST_CLOSING = '[if lte mso 15]></td></tr></table><![endif]';
const VML_GHOST_CLOSING_PATTERN = /^\[if lte mso 15\]>\s*<\/td>\s*<\/tr>\s*<\/table>\s*<!\[endif\]$/;

const isBlankText = (node: Node | null): boolean => node?.nodeType === Node.TEXT_NODE && !(node.textContent || '').trim();

const isCommentMatching = (node: Node, pattern: RegExp): boolean => node.nodeType === Node.COMMENT_NODE
  && pattern.test((node.textContent || '').trim());

const getVmlGhostOpening = (table: Element): string => {
  const previous = table.previousSibling;
  const text = previous?.nodeType === Node.COMMENT_NODE ? previous.textContent || '' : '';
  return VML_GHOST_OPENING.test(text.trim()) ? text : '';
};

const buildVmlGhostOpening = (spacer: number, width: number): string => `[if lte mso 15]><table ${VML_GHOST_ATTRIBUTE} role="presentation" `
  + `cellpadding="0" cellspacing="0" border="0" width="${width}" style="width:${width}px"><tr>`
  + `<td width="${spacer}" style="width:${spacer}px;font-size:0;line-height:0">&nbsp;</td>`
  + `<td width="${width - spacer}" style="width:${width - spacer}px"><![endif]`;

const wrapWithVmlGhost = (table: Element, opening: string) => {
  table.before(table.ownerDocument.createComment(opening));
  table.after(table.ownerDocument.createComment(VML_GHOST_CLOSING));
};

/**
 * Removes the ghost tables: each marked opening and the first closing after it among its siblings. The
 * editor removes, moves and inserts elements without the comments around them, so the table a ghost
 * wrapped may be gone or no longer alone; a comment the author wrote elsewhere is never taken for one.
 * Returns how many there were.
 */
const removeVmlGhosts = (container: Element): number => {
  const walker = container.ownerDocument.createTreeWalker(container, NodeFilter.SHOW_COMMENT);
  const openings: Node[] = [];
  while (walker.nextNode()) {
    if (isCommentMatching(walker.currentNode, VML_GHOST_OPENING)) {
      openings.push(walker.currentNode);
    }
  }
  new Set(openings.map((opening) => opening.parentNode)).forEach((parent) => {
    let unclosed = 0;
    Array.from(parent?.childNodes ?? []).forEach((node) => {
      if (isCommentMatching(node, VML_GHOST_OPENING)) {
        unclosed += 1;
        node.remove();
      } else if (unclosed > 0 && isCommentMatching(node, VML_GHOST_CLOSING_PATTERN)) {
        unclosed -= 1;
        node.remove();
      }
    });
  });
  return openings.length;
};

/**
 * Convert rgb/rgba color to hex format
 */
const rgbToHex = (color: string): string => {
  if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return '';
  if (color.startsWith('#')) return color;
  if (color.startsWith('rgb')) {
    const rgbMatch = color.match(/\d+/g);
    if (rgbMatch && rgbMatch.length >= 3) {
      const toHex = (channel: string) => parseInt(channel, 10).toString(16).padStart(2, '0');
      return `#${toHex(rgbMatch[0])}${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}`;
    }
  }
  return color;
};

/**
 * Colour an element paints, or else the colour of its first ancestor that paints a background. A
 * background shape shows it while Outlook blocks the image.
 */
const getPaintedBackgroundColor = (element: HTMLElement | null): string => {
  for (let current = element; current; current = current.parentElement) {
    const color = window.getComputedStyle(current).backgroundColor;
    if (color && color !== 'transparent' && !/^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)$/.test(color)) {
      return rgbToHex(color) || '#ffffff';
    }
  }
  return '#ffffff';
};

/**
 * Colour of the first ancestor that paints a background. A rounded-image shape fills the pixels its
 * image leaves transparent with a solid colour: it must be the colour of what sits behind the image,
 * not white, or the shape masks the cell it is drawn on.
 */
const getEffectiveBackgroundColor = (element: HTMLElement): string => getPaintedBackgroundColor(element.parentElement);

// Ghost tables, Word cell paddings and frame conversions stay consistent only where the save repairs them
const isVmlRefreshedAtSave = (): boolean => TemplateEditorState.template?.version === 4
  && !TemplateEditorState.template?.informations?.imported;

/**
 * Border widths of a table Word may draw as a padding instead: only a border painted in the colour
 * behind it looks the same that way. Null when the table has no such border.
 */
const getConvertibleFrameBorders = (table: HTMLElement, host: HTMLElement): number[] | null => {
  const widths = BORDER_SIDES.map((side) => getDeclaredBorderWidth(table, side));
  if (!widths.some((width) => width > 0)) return null;
  const behind = getEffectiveBackgroundColor(host).toLowerCase();
  const style = window.getComputedStyle(table);
  const sameColour = BORDER_SIDES.every((side, index) => widths[index] === 0
    || rgbToHex(style.getPropertyValue(`border-${side}-color`)).toLowerCase() === behind);
  return sameColour ? widths : null;
};

/**
 * Word lays a table border out of the width the table declares and draws it around it: a framed
 * background section grows past the page. A border painted in the colour behind it is, for Word only,
 * turned into a padding of the cell holding the shape, which keeps the frame within the declared width.
 */
export const convertVmlFrameBorder = (table: HTMLElement, host: HTMLElement) => {
  const widths = getConvertibleFrameBorders(table, host);
  if (!widths) return;
  addVmlDeclarations(table, VML_FRAME_RECORD, 'mso-border-alt: none');
  addVmlDeclarations(host, VML_FRAME_RECORD, `mso-padding-alt: ${widths.map((width) => `${width}px`).join(' ')}`);
};

const getFirstCell = (table: HTMLElement): HTMLElement | null => (table as HTMLTableElement).rows?.[0]?.cells?.[0] ?? null;

/**
 * Table whose background shape sits in this cell, the first of its first row; a column's shape has none.
 */
const getVmlFrame = (host: HTMLElement): HTMLElement | null => {
  const table = host.parentElement?.closest('table') as HTMLElement | null;
  return table && getBackgroundImageUrl(table) && getFirstCell(table) === host ? table : null;
};

const removeVmlOffsets = (container: Element): boolean => {
  const ghosts = removeVmlGhosts(container);
  const records = container.querySelectorAll(`[${VML_OFFSET_RECORD}]`);
  records.forEach((element) => removeVmlDeclarations(element, VML_OFFSET_RECORD));
  return ghosts > 0 || records.length > 0;
};

/**
 * Removes every Word-only adjustment the VML repair made. Returns whether there was any.
 */
export const removeVmlLayoutAdjustments = (root: Element): boolean => {
  const frames = root.querySelectorAll(`[${VML_FRAME_RECORD}]`);
  frames.forEach((element) => removeVmlDeclarations(element, VML_FRAME_RECORD));
  return removeVmlOffsets(root) || frames.length > 0;
};

const unwrapVmlContent = (host: HTMLElement) => {
  // A host holding the frame record owns the conversion of its table's border
  if (host.hasAttribute(VML_FRAME_RECORD)) {
    removeVmlDeclarations(host, VML_FRAME_RECORD);
    const frame = host.parentElement?.closest('table');
    if (frame) {
      removeVmlDeclarations(frame, VML_FRAME_RECORD);
    }
  }
  host.querySelectorAll(`:scope > div[${VML_CONTENT_ATTRIBUTE}]`).forEach((wrapper) => {
    removeVmlOffsets(wrapper);
    wrapper.querySelectorAll('table').forEach((table) => removeStyleDeclarations(table, LEGACY_VML_TABLE_OFFSET));
    while (wrapper.firstChild) {
      host.insertBefore(wrapper.firstChild, wrapper);
    }
    host.removeChild(wrapper);
  });
};

const isCentredCell = (cell: HTMLElement): boolean => (
  getCascadedStyleValue(cell, 'text-align') || cell.getAttribute('align') || (cell.tagName === 'TH' ? 'center' : '')
) === 'center';

const getCellPadding = (cell: HTMLElement, side: string): number => parseFloat(getCascadedStyleValue(cell, `padding-${side}`)) || 0;

/**
 * Inside a textbox Word lays a table that declares a width out without its cell's left padding. Outlook-only
 * spacings put it back: a cell holding only such tables hands its padding to them (a spacing for 2016+, the
 * first column of a ghost table for 2013 and older, which ignore spacings); in a mixed cell a full-width
 * centred table takes twice the padding given back on the right, any other the padding. Ghost tables and
 * cell paddings are left out where the save does not bring them up to date.
 */
export const offsetVmlTables = (wrapper: HTMLElement, full: boolean) => {
  const isInNestedShape = (table: Element) => {
    const shape = table.parentElement?.closest(`div[${VML_CONTENT_ATTRIBUTE}]`);
    return !!shape && shape !== wrapper && wrapper.contains(shape);
  };
  const offsetTables = Array.from(wrapper.querySelectorAll('table'))
    .map((table) => ({ table, cell: table.parentElement?.closest('td, th') as HTMLElement | null }))
    .filter((entry): entry is { table: HTMLTableElement; cell: HTMLElement } => !!entry.cell && wrapper.contains(entry.cell)
      && !isInNestedShape(entry.table) && !!getDeclaredWidth(entry.table) && getCellPadding(entry.cell, 'left') > 0);
  const outermostTables = offsetTables.filter(({ table }) => !offsetTables.some((other) => other.table !== table && other.table.contains(table)));
  const tableOnlyCells = new Set(full ? outermostTables.map(({ cell }) => cell)
    .filter((cell) => Array.from(cell.childNodes).every((node) => node.nodeType === Node.COMMENT_NODE || isBlankText(node)
      || outermostTables.some(({ table }) => table === node))) : []);

  offsetTables.forEach(({ table, cell }) => {
    const padding = getCellPadding(cell, 'left');
    if (tableOnlyCells.has(cell) || getDeclaredWidth(table) !== '100%' || !isCentredCell(cell)) {
      addVmlDeclarations(table, VML_OFFSET_RECORD, `mso-table-lspace:${cssLengthToVmlLength(`${padding}px`)}`);
      return;
    }
    const spacing = cssLengthToVmlLength(`${padding * 2}px`);
    addVmlDeclarations(table, VML_OFFSET_RECORD, `mso-table-lspace:${spacing}; mso-table-rspace:-${spacing}`);
  });

  if (!full) return;

  outermostTables.forEach(({ table, cell }) => {
    const padding = Math.round(getCellPadding(cell, 'left'));
    const contentWidth = getContainingPixelWidth(cell);
    if (tableOnlyCells.has(cell)) {
      if (contentWidth > 0) wrapWithVmlGhost(table, buildVmlGhostOpening(padding, contentWidth + padding));
    } else if (contentWidth > padding) {
      wrapWithVmlGhost(table, buildVmlGhostOpening(padding, contentWidth));
    }
  });

  tableOnlyCells.forEach((cell) => {
    const paddings = ['top', 'right', 'bottom'].map((side) => `${getCellPadding(cell, side)}px`);
    addVmlDeclarations(cell, VML_OFFSET_RECORD, `mso-padding-alt: ${paddings.join(' ')} 0`);
  });
};

/**
 * Brings the Word-only adjustments of every background shape up to date at save: a block moved out of a
 * shape loses them, and each shape, section or widget, gets its own back. Returns whether there was any.
 */
export const refreshVmlLayoutAdjustments = (root: Element): boolean => {
  const removed = removeVmlLayoutAdjustments(root);
  const wrappers = Array.from(root.querySelectorAll(`div[${VML_CONTENT_ATTRIBUTE}]`)) as HTMLElement[];
  withCascadedStyleCache(() => wrappers.forEach((wrapper) => {
    const host = wrapper.parentElement;
    const frame = host && getVmlFrame(host);
    if (host && frame) {
      convertVmlFrameBorder(frame, host);
    }
    offsetVmlTables(wrapper, true);
  }));
  return removed || wrappers.length > 0;
};

/**
 * Tables of a container paired by position with those it held before its markup was replaced, only when
 * both markups list the same tables.
 */
const pairReplacedTables = (previousTables: Element[], container: Element): { previous: Element; table: Element }[] => {
  const tables = Array.from(container.querySelectorAll('table'));
  if (tables.length !== previousTables.length
    || tables.some((table, index) => table.className !== previousTables[index].className)) return [];
  return tables.map((table, index) => ({ previous: previousTables[index], table }));
};

/**
 * Gives the tables of a container the Outlook offsets, ghost tables and cell paddings of the tables it
 * held before its markup was replaced by code stored ahead of the VML repair, such as the original code
 * of a smart list.
 */
export const copyVmlTableOffsets = (previousTables: Element[], container: Element) => {
  const pairs = pairReplacedTables(previousTables, container);
  if (!pairs.length) return;
  const copyRecord = (previous: Element, element: Element) => {
    if (element.getAttribute(VML_OFFSET_RECORD) === previous.getAttribute(VML_OFFSET_RECORD)) return;
    removeVmlDeclarations(element, VML_OFFSET_RECORD);
    const recorded = previous.getAttribute(VML_OFFSET_RECORD);
    if (recorded) {
      removeStyleDeclarations(element, LEGACY_VML_TABLE_OFFSET);
      addVmlDeclarations(element, VML_OFFSET_RECORD, recorded);
    }
  };
  removeVmlGhosts(container);
  pairs.forEach(({ previous, table }) => {
    copyRecord(previous, table);
    const previousCell = previous.parentElement?.closest('td, th');
    const cell = table.parentElement?.closest('td, th');
    if (previousCell && cell && container.contains(cell)) {
      copyRecord(previousCell, cell);
    }
    const ghost = getVmlGhostOpening(previous);
    if (ghost) {
      wrapWithVmlGhost(table, ghost);
    }
  });
};

/**
 * Word lays out the content of a textbox from a block: tables placed straight in the textbox lose
 * their cell margins. The content of the host cell is moved into a div, as MJML and Campaign Monitor
 * emit it, and the shape wraps that div.
 */
const wrapVmlContent = (host: HTMLElement): HTMLElement => {
  const wrapper = host.ownerDocument.createElement('div');
  wrapper.setAttribute(VML_CONTENT_ATTRIBUTE, '');
  while (host.firstChild) {
    wrapper.appendChild(host.firstChild);
  }
  host.appendChild(wrapper);
  return wrapper;
};

const wrapWithVml = (element: HTMLElement, startPosition: InsertPosition, endPosition: InsertPosition, opening: string) => {
  const host = getVmlHostCell(element);
  const insideHost = host !== element || startPosition === 'beforebegin';
  unwrapVmlContent(host);
  const full = isVmlRefreshedAtSave();
  const frame = full && host !== element ? element.closest('table') : null;
  if (frame) {
    convertVmlFrameBorder(frame, host);
  }
  const wrapper = wrapVmlContent(host);
  withCascadedStyleCache(() => offsetVmlTables(wrapper, full));
  host.insertAdjacentHTML(insideHost ? 'afterbegin' : startPosition, opening);
  host.insertAdjacentHTML(insideHost ? 'beforeend' : endPosition, '<!--[if gte mso 9]></v:textbox></v:rect><![endif]-->');
};

const VML_SHAPE_COMMENT = /\[if gte mso 9\]/;

const removeVmlCommentChildren = (el: Node) => {
  Array.from(el.childNodes).forEach((node) => {
    if (isCommentMatching(node, VML_SHAPE_COMMENT)) {
      el.removeChild(node);
    }
  });
};

const removeComments = (el: any, forColumn = false) => {
  if (!el) return;
  if (!forColumn) {
    // Legacy shapes sat in the row itself, current ones sit in its first cell: clean both
    removeVmlCommentChildren(el);
    const host = getVmlHostCell(el);
    if (host !== el) {
      removeVmlCommentChildren(host);
    }
    unwrapVmlContent(host);
  } else {
    // Column shapes used to sit around the cell, they now sit inside it
    removeVmlCommentChildren(el);
    unwrapVmlContent(el);
    if (el.previousSibling?.nodeType === Node.COMMENT_NODE) {
      if (el.previousSibling.textContent && el.previousSibling.textContent.trim().match(/\[if gte mso 9\]>(<v:textbox>|<v:rect xmlns:v=)[\s\S]*?<!\[endif\]/g)) {
        if (el.parentNode) {
          el.parentNode.removeChild(el.previousSibling);
        }
      }
    }
    if (el.nextSibling?.nodeType === Node.COMMENT_NODE) {
      if (el.nextSibling.textContent && el.nextSibling.textContent.trim().match(/\[if gte mso 9\]>(<\/v:textbox>|<\/v:rect xmlns:v=)[\s\S]*?<!\[endif\]/g)) {
        if (el.parentNode) {
          el.parentNode.removeChild(el.nextSibling);
        }
      }
    }
  }
};

const removeWidgetVmlShape = (widget: HTMLElement) => {
  const row = getFirstCell(widget)?.parentElement;
  if (row) {
    removeComments(row);
  }
};

/**
 * Code of a widget without the background shape its first cell holds: the shape belongs to the widget,
 * not to code stored apart from it, such as the original code of a products list.
 */
export const getWidgetCodeWithoutVmlShape = (widget: HTMLElement): string => {
  const copy = widget.cloneNode(true) as HTMLElement;
  removeWidgetVmlShape(copy);
  return copy.innerHTML;
};

/**
 * Shape comments of a node on each side of one of its children.
 */
const getVmlShapeCommentsAround = (parent: Node | null | undefined, child: Node | null | undefined) => {
  const nodes = parent ? Array.from(parent.childNodes) : [];
  const index = child ? nodes.indexOf(child as ChildNode) : -1;
  const comments = index < 0 ? [] : nodes.filter((node) => isCommentMatching(node, VML_SHAPE_COMMENT));
  return {
    openings: comments.filter((node) => nodes.indexOf(node) < index),
    closings: comments.filter((node) => nodes.indexOf(node) > index),
  };
};

/**
 * Puts code stored apart from a widget back in it, within the background shape its first cell holds:
 * the shape and the Word padding of its frame stay, and a shape the stored code kept is dropped. A
 * shape from before the content wrapper, around the cell in its row, stays as well.
 */
export const restoreWidgetCodeInVmlShape = (widget: HTMLElement, code: string) => {
  const previousHost = getFirstCell(widget);
  const wrapper = Array.from(previousHost?.children ?? []).find((child) => child.hasAttribute(VML_CONTENT_ATTRIBUTE));
  const { openings, closings } = getVmlShapeCommentsAround(previousHost, wrapper);
  const legacyShape = getVmlShapeCommentsAround(previousHost?.parentElement, previousHost);
  const frameRecord = previousHost?.getAttribute(VML_FRAME_RECORD);

  const stored = widget.cloneNode(false) as HTMLElement;
  stored.innerHTML = code;
  removeWidgetVmlShape(stored);
  // eslint-disable-next-line no-param-reassign
  widget.innerHTML = '';
  widget.append(...Array.from(stored.childNodes));

  const host = getFirstCell(widget);
  if (!host) return;
  if (legacyShape.openings.length && legacyShape.closings.length) {
    host.before(...legacyShape.openings);
    host.after(...legacyShape.closings);
  }
  if (!openings.length || !closings.length) return;
  wrapVmlContent(host);
  host.prepend(...openings);
  host.append(...closings);
  if (frameRecord) {
    addVmlDeclarations(host, VML_FRAME_RECORD, frameRecord);
  }
};

/**
 * Build the `inset` attribute of a v:textbox (format: left,top,right,bottom) from the paddings of
 * the cell holding the content.
 */
const buildVmlInset = (cell: HTMLElement | null): string => {
  if (!cell) {
    return '0,0,0,0';
  }

  const paddingStyle = window.getComputedStyle(cell);

  return [paddingStyle.paddingLeft, paddingStyle.paddingTop, paddingStyle.paddingRight, paddingStyle.paddingBottom]
    .map((padding: string) => cssLengthToVmlLength(padding || '0px'))
    .join(',');
};

/**
 * The content of a section sits either in a `th` or in a `td` depending on the widget, so both must
 * be looked up when reading the paddings and the vertical alignment feeding the VML shape.
 */
const getVmlContentCellSelector = (baseSelector: string): string => (
  `${baseSelector} > tbody > tr > th, ${baseSelector} > tbody > tr > td`
);

type VmlFillType = 'frame' | 'tile';

/**
 * VML expresses `origin` and `position` in two different coordinate systems depending on the fill
 * being emitted: a tiled fill measures from the top-left corner (0 = left/top, 1 = right/bottom)
 * while a framed one measures from the centre (-0.5 = left/top, 0.5 = right/bottom). Feeding one
 * system to the other shifts the image or, out of range, distorts the shape. Same split as
 * mj-section (mjml#1814).
 */
const cssBackgroundPositionToVml = (
  backgroundPosition: string,
  fillType: VmlFillType,
): { x: string; y: string } => {
  const isTile = fillType === 'tile';

  const toVmlFraction = (value: string, start: string, end: string): string => {
    // Anything that is not a plain percentage (lengths, calc(), keyword pairs) has no VML
    // equivalent, so it falls through to the centre rather than producing NaN
    const percentage = /^-?\d+(\.\d+)?%$/.test(value) ? parseFloat(value) : NaN;

    if (Number.isFinite(percentage)) {
      const bounded = Math.min(Math.max(percentage, 0), 100);
      return `${Math.round((isTile ? bounded : bounded - 50) * 100) / 10000}`;
    }

    if (value === start) return isTile ? '0' : '-0.5';
    if (value === end) return isTile ? '1' : '0.5';
    return isTile ? '0.5' : '0';
  };

  // Handle single value case: "center" → "center center", "20%" → "20% center"
  const parts = backgroundPosition.split(' ');

  return {
    x: toVmlFraction(parts[0] || 'center', 'left', 'right'),
    y: toVmlFraction(parts[1] || 'center', 'top', 'bottom'),
  };
};

/**
 * Outlook renders the shape at a fixed height (mso-fit-shape-to-text is false), so a declared
 * height shorter than the rendered content clips the whole block: keep whichever is taller.
 * The row is measured rather than the table, to match what the VML actually wraps and to leave
 * the table borders out. Only desktop is a valid reference: the mobile and tablet previews reflow
 * the content, so their height would be written into a shape the recipient never renders that way.
 */
const resolveVmlHeight = (element: HTMLElement, declaredHeight?: string): string | undefined => {
  if (TemplateEditorState.templateWidth !== TemplateWidth.DESKTOP) {
    return declaredHeight;
  }

  const measuredElement = (element.querySelector(':scope > tbody > tr') as HTMLElement) ?? element;
  const renderedHeight = measuredElement.offsetHeight;

  if (!renderedHeight) {
    return declaredHeight;
  }

  if (!declaredHeight || !declaredHeight.endsWith('px')) {
    return `${renderedHeight}px`;
  }

  return parseFloat(declaredHeight) >= renderedHeight ? declaredHeight : `${renderedHeight}px`;
};

/**
 * Width of the VML shape, as a style fragment, from the width declared for the element, read across
 * every sheet the sending pipeline inlines and its width attribute: the area the background covers,
 * inside the borders of a table. A percentage is resolved against the width available in its container,
 * since Word would take it relative to the page. Without any declaration the shape spans the reading
 * pane, which never widens the page, whereas a width measured in the editor captures the viewport and
 * the placeholders.
 */
export const resolveVmlCanvasWidth = (element: HTMLElement, convertFrame = isVmlRefreshedAtSave()): string => {
  const width = getDeclaredWidth(element);
  // Word keeps a border it draws past the shape; a converted one becomes padding the shape must leave room for
  const host = element.tagName === 'TABLE' ? getFirstCell(element) : null;
  const frameBorders = convertFrame && host ? getConvertibleFrameBorders(element, host) : null;
  const borders = frameBorders ? frameBorders[1] + frameBorders[3] : 0;

  if (PX_LENGTH.test(width)) {
    return `width:${Math.max(0, Math.round(parseFloat(width) - borders))}px`;
  }

  if (PERCENT_LENGTH.test(width)) {
    const containingWidth = getContainingPixelWidth(element.parentElement);
    return containingWidth
      ? `width:${Math.max(0, Math.round((getEffectivePercentage(element, width) / 100) * containingWidth - borders))}px`
      : `mso-width-percent:${Math.round(parseFloat(width) * 10)}`;
  }

  return 'mso-width-percent:1000';
};

/**
 * Insert VML code for Outlook background image compatibility (auto/tile mode)
 * @param element - The HTML element to insert VML around
 * @param width - Width style for VML rect
 * @param url - URL of the background image
 * @param backgroundColor - Fallback background color (hex format), shown while the image is blocked
 * @param startPosition - Where to insert opening VML
 * @param endPosition - Where to insert closing VML
 * @param height - Optional height for VML rect (e.g., "300px")
 * @param position - VML position values {x, y} from 0 to 1, the tiled fill coordinate system
 */
const insertOutlookCompatibility = (
  element: HTMLElement,
  width: string,
  url: string,
  backgroundColor: string,
  startPosition: InsertPosition,
  endPosition: InsertPosition,
  height?: string,
  position?: { x: string; y: string },
  inset?: string,
) => {
  const conditionalComment = '<!--[if gte mso 9]>';
  // Always get height (either passed or computed from element)
  const resolvedHeight = height || window.getComputedStyle(element).height;
  const heightStyle = `;height:${resolvedHeight}`;
  const rectElement = `<v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="${width}${heightStyle}">`;

  // Use type="tile" with origin/position for positioning support
  const originPos = position ? `origin="${position.x},${position.y}" position="${position.x},${position.y}"` : '';
  const fillElement = `<v:fill type="tile" src="${url}" color="${backgroundColor}" ${originPos}/>`;

  // The shape wraps the cell content: it must grow with it, a rigid shape makes Word drop whatever
  // does not fit. The height stays as the size the shape starts from.
  const fitToText = 'true';
  // inset format: left,top,right,bottom
  const vmlInset = inset || '0,0,0,0';
  const textboxElement = `<v:textbox style="mso-fit-shape-to-text:${fitToText}" inset="${vmlInset}">`;
  const closingComment = '<![endif]-->';

  wrapWithVml(element, startPosition, endPosition, `${conditionalComment}${rectElement}${fillElement}${textboxElement}${closingComment}`);
};

/**
 * Insert VML code for Outlook background image compatibility (cover mode)
 * Uses type="frame" with aspect="atleast" for cover-like behavior
 * @param element - The HTML element to insert VML around
 * @param width - Width style for VML rect (e.g., "width:600px" or "mso-width-percent:1000")
 * @param url - URL of the background image
 * @param backgroundColor - Fallback background color (hex format)
 * @param startPosition - Where to insert opening VML
 * @param endPosition - Where to insert closing VML
 * @param height - Optional height for VML rect (e.g., "300px")
 * @param position - VML position values {x, y} from -0.5 to 0.5, the framed fill coordinate system
 */
const insertOutlookCoverCompatibility = (
  element: HTMLElement,
  width: string,
  url: string,
  backgroundColor: string,
  startPosition: InsertPosition,
  endPosition: InsertPosition,
  height?: string,
  position?: { x: string; y: string },
  inset?: string,
) => {
  const conditionalComment = '<!--[if gte mso 9]>';
  // Always get height (either passed or computed from element)
  const resolvedHeight = height || window.getComputedStyle(element).height;
  const heightStyle = `;height:${resolvedHeight}`;
  const rectElement = `<v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="${width}${heightStyle}">`;
  const originPos = position ? `origin="${position.x},${position.y}" position="${position.x},${position.y}"` : 'origin="0,0" position="0,0"';
  const fillElement = `<v:fill type="frame" src="${url}" color="${backgroundColor}" size="1,1" aspect="atleast" ${originPos}/>`;
  // The shape wraps the cell content: it must grow with it, a rigid shape makes Word drop whatever
  // does not fit. The height stays as the size the shape starts from.
  const fitToText = 'true';
  // inset format: left,top,right,bottom
  const vmlInset = inset || '0,0,0,0';
  const textboxElement = `<v:textbox style="mso-fit-shape-to-text:${fitToText}" inset="${vmlInset}">`;
  const closingComment = '<![endif]-->';

  wrapWithVml(element, startPosition, endPosition, `${conditionalComment}${rectElement}${fillElement}${textboxElement}${closingComment}`);
};

// Debounce timer for setBackgroundImage
let setBackgroundImageTimer: ReturnType<typeof setTimeout> | null = null;

export const setBackgroundImage = (selector: string, property: Property, forColumn = false) => {
  // Don't debounce when setting the actual background-image URL - apply immediately
  const isSettingImageUrl = property.name === 'background-image' && property.value !== 'none';

  // Clear previous timer to debounce rapid calls
  if (setBackgroundImageTimer) {
    clearTimeout(setBackgroundImageTimer);
  }

  // For actual image selection, execute immediately; for other properties, debounce
  const executeSetBackgroundImage = () => {
    if (isEmailTemplate(TemplateEditorState.template?.type)) {
      const element = getTemplateIframeDocument()?.querySelector(selector);
      if (element) {
        const htmlElement = element as HTMLElement;
        const isRichEditorWrapper = selector.includes('.spm_rich_editor_wrapper');
        const canvasWidth = resolveVmlCanvasWidth(htmlElement);
        // Get computed styles
        const computedStyle = window.getComputedStyle(htmlElement);

        // Get URL from property value or from computed style (for height changes)
        let url = '';
        if (property.value.match(/url\("([^"]+)"\)/)) {
          url = property.value.match(/url\("([^"]+)"\)/)[1];
        } else if (['height', 'min-height', 'background-size', 'background-position', 'background-repeat'].includes(property.name)) {
          url = getBackgroundImageUrl(htmlElement);
        }

        // Get background-size to determine VML mode
        // Use property value if background-size is being changed, otherwise use computed style
        const backgroundSize = property.name === 'background-size' ? property.value : (computedStyle.backgroundSize || 'cover');
        const useCoverMode = backgroundSize === 'cover';

        // Get background-position for VML positioning
        const backgroundPosition = property.name === 'background-position' ? property.value : (computedStyle.backgroundPosition || 'center center');
        const vmlPosition = cssBackgroundPositionToVml(backgroundPosition, useCoverMode ? 'frame' : 'tile');

        // Get height for Outlook VML
        let vmlHeight: string | undefined;
        // If height property is being toggled off (empty value), force no height
        const isHeightToggleOff = (property.name === 'height' || property.name === 'min-height') && property.value === '';
        const isContainer = selector.match('.container');
        // If height is being set directly via property, use that value
        const isHeightProperty = property.name === 'height' || property.name === 'min-height';
        if (isHeightToggleOff) {
          vmlHeight = undefined;
        } else if (isHeightProperty && property.value && property.value !== '0px' && property.value !== 'auto') {
          // Use property value directly when height is being changed
          vmlHeight = property.value;
        } else if (isRichEditorWrapper || isContainer) {
          // For rich editor wrapper or container sections, check if height is explicitly set in dynamic styles on selector itself
          const dynamicStyles = getDynamicStylesForSelector(selector, false);
          const dynamicHeight = dynamicStyles.height;
          vmlHeight = dynamicHeight && dynamicHeight !== '0px' && dynamicHeight !== 'auto' ? dynamicHeight : undefined;
        } else {
          const thSelector = `${selector} > tbody > tr > th`;
          const dynamicStyles = getDynamicStylesForSelector(thSelector, false);
          const thHeight = dynamicStyles.height;
          vmlHeight = thHeight && thHeight !== '0px' && thHeight !== 'auto' ? thHeight : undefined;
        }
        // A declared height shorter than the content is ignored by every client but Outlook
        vmlHeight = resolveVmlHeight(htmlElement, vmlHeight);

        const backgroundColor = getPaintedBackgroundColor(htmlElement);

        // Get paddings for VML inset (format: left,top,right,bottom)
        const getPaddingInset = (): string => {
          // Extract base selector (handle comma-separated selectors)
          const baseSelector = selector.split(',')[0].trim();
          const paddingElement = getTemplateIframeDocument()
            ?.querySelector(getVmlContentCellSelector(baseSelector)) as HTMLElement;
          return buildVmlInset(paddingElement ?? null);
        };
        const vmlInset = getPaddingInset();


        // Handle rich editor wrapper case - VML wraps the table via tbody > tr
        if (isRichEditorWrapper) {
          const wrapperRow = htmlElement.querySelector(':scope > tbody > tr');
          if (wrapperRow) {
            // Remove last outlook compatibility comment
            removeComments(wrapperRow);

            // Use appropriate VML mode based on background-size
            if (url) {
              if (useCoverMode) {
                insertOutlookCoverCompatibility(wrapperRow as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend', vmlHeight, vmlPosition, vmlInset);
              } else {
                insertOutlookCompatibility(wrapperRow as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend', vmlHeight, vmlPosition, vmlInset);
              }
            }
          }
        } else {
          const bgOutlookSelector = `${selector} > tbody > tr`;
          const bgElement = getTemplateIframeDocument()?.querySelector(bgOutlookSelector);
          if (bgElement) {
            // Remove last outlook compatibility comment
            removeComments(bgElement);
            if (selector.match('.container')) {
              const parentBgElement = getTemplateIframeDocument()?.querySelector(`${selector.replace('.container', '')} > tbody > tr`);
              removeComments(parentBgElement);

              if (parentBgElement) {
                parentBgElement.removeAttribute('background');
                removeDynamicStyle(`${selector.replace(' .container', '')}`, { backgroundImage: '' });
              }

              const parentTable = getTemplateIframeDocument()?.querySelector(`${selector.replace('.container', '')}`);
              if (parentTable) {
                parentTable.removeAttribute('background');
              }
            } else {
              const containerElement = getTemplateIframeDocument()?.querySelector(`${selector} .container > tbody > tr`);
              if (containerElement) {
                removeComments(containerElement);
              }

              const parentTable = getTemplateIframeDocument()?.querySelector(`${selector} .container`);
              if (parentTable) {
                parentTable.removeAttribute('background');
                removeDynamicStyle(`${selector} .container`, { backgroundImage: '' });
              }
            }

            // Use appropriate VML mode based on background-size
            if (url) {
              if (useCoverMode) {
                insertOutlookCoverCompatibility(bgElement as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend', vmlHeight, vmlPosition, vmlInset);
              } else {
                insertOutlookCompatibility(bgElement as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend', vmlHeight, vmlPosition, vmlInset);
              }
            }
          } else {
            // Remove last outlook compatibility comment
            removeComments(element, forColumn);

            if (element) {
              element.removeAttribute('background');
            }

            // Use appropriate VML mode based on background-size
            if (url) {
              if (useCoverMode) {
                if (forColumn) {
                  insertOutlookCoverCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'beforebegin', 'afterend', vmlHeight, vmlPosition, vmlInset);
                } else {
                  insertOutlookCoverCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend', vmlHeight, vmlPosition, vmlInset);
                }
              } else {
                if (forColumn) {
                  insertOutlookCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'beforebegin', 'afterend', vmlHeight, vmlPosition, vmlInset);
                } else {
                  insertOutlookCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend', vmlHeight, vmlPosition, vmlInset);
                }
              }
            }
          }
        }
        if (property.value === 'none') {
          element.removeAttribute('background');
          if (isRichEditorWrapper) {
            const wrapperRow = htmlElement.querySelector(':scope > tbody > tr');
            if (wrapperRow) {
              removeComments(wrapperRow);
            }
          } else {
            // For sections, VML is on > tbody > tr
            const sectionRow = htmlElement.querySelector(':scope > tbody > tr');
            if (sectionRow) {
              removeComments(sectionRow);
            } else {
              removeComments(element, forColumn);
            }
          }
        } else if (!isRichEditorWrapper) {
          element.setAttribute('background', url);
        }
      }
    }
    if (property.value === 'none') {
      if (isEmailTemplate(TemplateEditorState.template?.type)) {
        removeDynamicStyle(selector, { backgroundImage: '', background: '', backgroundSize: '', backgroundRepeat: '' });
      } else {
        removeDynamicStyle(selector, { backgroundImage: '' });
      }
    } else if (isEmailTemplate(TemplateEditorState.template?.type) && property.name === 'background-image') {
      // For email templates, don't store background-image URL in CSS (some email clients transform background to background:none)
      // URL is stored only in HTML background attribute. Remove any existing CSS background/background-image
      removeDynamicStyle(selector, { backgroundImage: '', background: '' });
    } else {
      setCssProperty(selector, property);
    }

    setBackgroundImageTimer = null;
  };

  // Execute immediately for image URL changes or removal, debounce for other property changes
  const isRemovingImage = property.name === 'background-image' && property.value === 'none';
  if (isSettingImageUrl || isRemovingImage) {
    executeSetBackgroundImage();
  } else {
    setBackgroundImageTimer = setTimeout(executeSetBackgroundImage, 300);
  }
};

/**
 * Update VML inset when padding changes (if background image is defined)
 * This ensures the VML textbox inset matches the CSS padding
 */
export const updateVmlInsetOnPaddingChange = (selector: string, property: Property) => {
  // Only update VML if we're in an email template
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Find the wrapper element to check if background image is set
  // selector is like '{SELECTOR} .spm_rich_editor_wrapper > tbody > tr > td'
  // We need to get the wrapper selector
  const wrapperSelector = selector.replace(/ > tbody > tr > td$/, '').replace(/ > tbody > tr > th$/, '');
  const wrapperElement = getTemplateIframeDocument()?.querySelector(wrapperSelector) as HTMLElement;

  if (wrapperElement) {
    const computedStyle = window.getComputedStyle(wrapperElement);
    const bgImage = computedStyle.backgroundImage;

    // Only update VML if there's a background image
    if (bgImage && bgImage !== 'none') {
      // Create a dummy property to trigger VML regeneration
      const bgProperty: Property = {
        name: 'background-image',
        value: bgImage,
        getter: property.getter,
        setters: [],
      };
      // Use a small delay to ensure padding CSS is applied first
      setTimeout(() => {
        setBackgroundImage(wrapperSelector, bgProperty);
      }, 50);
    }
  }
};

/**
 * Set vertical-align and valign attribute, and regenerate the background VML if a background image exists
 */
export const setVerticalAlignAndVml = (selector: string, property: Property) => {
  // Set CSS vertical-align
  setCssProperty(selector, property);

  // Set valign attribute on the element (map CSS values to valid HTML valign values)
  const element = getTemplateIframeDocument()?.querySelector(selector) as HTMLElement;
  if (element) {
    // Map CSS vertical-align values to valid HTML valign values
    let valignValue = property.value;
    if (valignValue === 'center') valignValue = 'middle';

    if (property.value === '' || property.value === 'initial' || property.value === 'unset') {
      element.removeAttribute('valign');
    } else {
      element.setAttribute('valign', valignValue);
    }
  }

  // Only update VML if we're in an email template
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Find the wrapper element to check if background image is set
  const wrapperSelector = selector.replace(/ > tbody > tr > td$/, '').replace(/ > tbody > tr > th$/, '');
  const wrapperElement = getTemplateIframeDocument()?.querySelector(wrapperSelector) as HTMLElement;

  if (wrapperElement) {
    const computedStyle = window.getComputedStyle(wrapperElement);
    const bgImage = computedStyle.backgroundImage;

    // Only update VML if there's a background image
    if (bgImage && bgImage !== 'none') {
      const bgProperty: Property = {
        name: 'background-image',
        value: bgImage,
        getter: property.getter,
        setters: [],
      };
      // Use a small delay to ensure CSS is applied first
      setTimeout(() => {
        setBackgroundImage(wrapperSelector, bgProperty);
      }, 50);
    }
  }
};

/**
 * Set background position from grid values (tl, tc, tr, ml, mc, mr, bl, bc, br)
 * Converts grid position to CSS background-position and updates VML
 */
export const setBackgroundPositionFromGrid = (selector: string, property: Property) => {
  // Map grid values to CSS background-position
  const gridToCss: Record<string, string> = {
    tl: 'left top',
    tc: 'center top',
    tr: 'right top',
    ml: 'left center',
    mc: 'center center',
    mr: 'right center',
    bl: 'left bottom',
    bc: 'center bottom',
    br: 'right bottom',
  };

  const cssValue = gridToCss[property.value] || 'center center';

  // Create a new property with CSS value for background-position
  const cssProperty: Property = {
    name: 'background-position',
    value: cssValue,
    getter: property.getter,
    setters: property.setters,
  };

  // Apply CSS and update VML
  setCssProperty(selector, cssProperty);
  setBackgroundImage(selector, cssProperty);
};

/**
 * Set background image with cover mode support for Outlook
 * This function handles the VML generation for cover mode backgrounds
 */
export const setBackgroundImageWithCover = (
  selector: string,
  property: Property,
  backgroundSize: string = 'cover',
  backgroundPosition: string = 'center',
  forColumn = false,
) => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const element = getTemplateIframeDocument()?.querySelector(selector);
  if (!element) {
    return;
  }

  const htmlElement = element as HTMLElement;
  let canvasWidth: string;
  if (selector.match('.container')) {
    const css = window.getComputedStyle(htmlElement);
    const width = css.getPropertyValue('width');
    canvasWidth = `width:${width}`;
  } else {
    canvasWidth = 'mso-width-percent:1000;';
  }

  const url = property.value.match(/url\("([^"]+)"\)/) ? property.value.match(/url\("([^"]+)"\)/)[1] : '';

  const backgroundColor = getPaintedBackgroundColor(htmlElement);

  const bgOutlookSelector = `${selector} > tbody > tr`;
  const bgElement = getTemplateIframeDocument()?.querySelector(bgOutlookSelector);

  // Determine which VML function to use based on backgroundSize
  const useCoverMode = backgroundSize === 'cover' || backgroundSize === 'contain';

  if (bgElement) {
    // Remove existing VML comments
    removeComments(bgElement);

    if (selector.match('.container')) {
      const parentBgElement = getTemplateIframeDocument()?.querySelector(`${selector.replace('.container', '')} > tbody > tr`);
      removeComments(parentBgElement);

      if (parentBgElement) {
        parentBgElement.removeAttribute('background');
        removeDynamicStyle(`${selector.replace(' .container', '')}`, { backgroundImage: '' });
      }

      const parentTable = getTemplateIframeDocument()?.querySelector(`${selector.replace('.container', '')}`);
      if (parentTable) {
        parentTable.removeAttribute('background');
      }
    } else {
      const containerElement = getTemplateIframeDocument()?.querySelector(`${selector} .container > tbody > tr`);
      if (containerElement) {
        removeComments(containerElement);
      }

      const parentTable = getTemplateIframeDocument()?.querySelector(`${selector} .container`);
      if (parentTable) {
        parentTable.removeAttribute('background');
        removeDynamicStyle(`${selector} .container`, { backgroundImage: '' });
      }
    }

    // Insert appropriate VML
    if (useCoverMode && url) {
      insertOutlookCoverCompatibility(bgElement as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend');
    } else if (url) {
      insertOutlookCompatibility(bgElement as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend');
    }
  } else {
    // Remove existing VML comments
    removeComments(element, forColumn);

    if (element) {
      element.removeAttribute('background');
    }

    // Insert appropriate VML
    if (useCoverMode && url) {
      if (forColumn) {
        insertOutlookCoverCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'beforebegin', 'afterend');
      } else {
        insertOutlookCoverCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend');
      }
    } else if (url) {
      if (forColumn) {
        insertOutlookCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'beforebegin', 'afterend');
      } else {
        insertOutlookCompatibility(element as HTMLElement, canvasWidth, url, backgroundColor, 'afterbegin', 'beforeend');
      }
    }
  }

  // Set/remove background attribute
  if (property.value === 'none' || !url) {
    element.removeAttribute('background');
    if (bgElement) {
      removeComments(bgElement);
    } else {
      removeComments(element, forColumn);
    }
  } else {
    element.setAttribute('background', url);
  }
};

export const setBackgroundImageProperty = (selector: string, property: Property) => {
  if (property.value) {
    setCssProperty(selector, property);
  } else {
    removeDynamicStyle(selector, { [property.name]: '' });
  }
};

export const setHtmlContent = (selector: string, property: Property, widgetId: string) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null = template?.querySelector(selector);
  if (element && undefined !== property.value) {
    const clonedElement = element.cloneNode(true) as HTMLElement;
    clonedElement.innerHTML = property.value;
    const { newHtml } = generateTranslations(
      clonedElement.innerHTML,
      {
        newElement: true,
        translateVariables: false,
        ignoreSpecificItems: true,
      },
    );

    const div = document.createElement('div');
    div.innerHTML = newHtml;

    const fragment = document.createDocumentFragment();

    while (div.firstChild) {
      // appendChild déplace le firstChild du tempContainer au fragment
      fragment.appendChild(div.firstChild);
    }

    let newInnerHTML = '';
    fragment.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        newInnerHTML += (node as HTMLElement).outerHTML;
      } else if (node.nodeType === Node.TEXT_NODE) {
        newInnerHTML += node.textContent ?? '';
      }
    });

    // eslint-disable-next-line no-param-reassign
    property.value = newInnerHTML;

    // This setter can be involved by translation
    element.innerHTML = property.value;

    // Update translation in state
    const newDiv = document.createElement('div');
    newDiv.innerHTML = property.value;

    const defaultLanguage: string = UserState.activeShop?.langs.filter((lang) => lang.default).map((lang) => lang.id)[0] ?? 'fr';
    const translationIds = property.value.match(/[a-zA-Z-]+="LANG_[a-zA-Z0-9.]+"/gm);
    if (translationIds && translationIds.length) {
      translationIds.forEach((translationId: string) => {
        const attributeRaw = translationId.replace(/"/gm, '');
        const [attributeName, attributeValue] = attributeRaw.split('=');
        if (attributeName && attributeValue) {
          const matchedElement = newDiv.querySelector(`[${attributeName}="${attributeValue}"]`);
          if (matchedElement) {
            if (attributeName === 'id') {
              replaceTranslationInState(attributeValue.replace('LANG_', ''), defaultLanguage, matchedElement.innerHTML);
            } else {
              const attributeToGet = attributeName.replace(`${TRANSLATION_ATTRIBUTE}-`, '');
              const newValue = matchedElement.getAttribute(attributeToGet);
              if (newValue !== undefined || newValue !== null) {
                replaceTranslationInState(attributeValue.replace('LANG_', ''), defaultLanguage, newValue as string);
              }
            }
          }
        }
      });
    }

    updateSectionsInState(false);
    removeUnusedTranslations();

    if (htmlHasSmartlist(element.innerHTML)) {
      addSmartListOriginalCode(widgetId, element.innerHTML);
    }
  }
};

export const setSelectOptionLabel = (selector: string, property: Property, attributeName: string) => {
  setAttribute(selector, property);

  const template = getTemplateIframeDocument();

  const optionElement = template.querySelector(selector);
  if (optionElement) {
    const optionValue = optionElement.getAttribute(attributeName) || '';
    optionElement.innerHTML = optionValue;
  }
};

export const toggleMobileStyle = (selector: string, value: boolean) => {
  const template = getTemplateIframeDocument();
  if (template) {
    const element = template.querySelector(selector);
    if (element) {
      const parentSection = element.getAttribute('data-spmelementid') ? element : element.closest('[data-spmelementid]');
      if (parentSection) {
        const spmElementId = parentSection.getAttribute('data-spmelementid');
        if (spmElementId) {
          const mobileStyle = getDynamicStylesForActiveSection(spmElementId, true);
          const staticStylesheet = getElementFromIframe(`style[data-spm-styles="static_persistent_mobile"][data-spm-section="${spmElementId}"]`);
          // Helper to remove all nested comments
          const removeAllComments = (css: string): string => {
            let result = css;
            while (HIDE_MOBILE_COMMENT_REGEX.test(result)) {
              result = result.replace(HIDE_MOBILE_COMMENT_REGEX, '$1');
            }
            return result;
          };

          if (!value) {
            parentSection.setAttribute(HIDE_MOBILE_ATTRIBUTE, 'true');
            // First remove any existing comments to avoid duplication
            const cleanMobileStyle = removeAllComments(mobileStyle.innerHTML);
            // Escape existing */ to prevent nested comment issues
            const escapedMobileStyle = escapeCssCommentsForNesting(cleanMobileStyle);
            mobileStyle.innerHTML = `/*${HIDE_MOBILE_ATTRIBUTE} ${escapedMobileStyle} ${HIDE_MOBILE_ATTRIBUTE}*/`;
            if (staticStylesheet) {
              const cleanStaticStyle = removeAllComments(staticStylesheet.innerHTML);
              const escapedStaticStyle = escapeCssCommentsForNesting(cleanStaticStyle);
              staticStylesheet.innerHTML = `/*${HIDE_MOBILE_ATTRIBUTE} ${escapedStaticStyle} ${HIDE_MOBILE_ATTRIBUTE}*/`;
            }
          } else {
            mobileStyle.innerHTML = unescapeCssCommentsFromNesting(removeAllComments(mobileStyle.innerHTML));
            parentSection.removeAttribute(HIDE_MOBILE_ATTRIBUTE);
            if (staticStylesheet) {
              staticStylesheet.innerHTML = unescapeCssCommentsFromNesting(removeAllComments(staticStylesheet.innerHTML));
            }
          }
        }
        updateSectionsInState(false);
      }
    }
  }
};

export const reorderColumn = (column: Element, direction: string) => {
  const currentDir = column.getAttribute('dir');
  const isCurrentlyRtl = currentDir === ResponsiveDisplayStackEnum.RTL;
  const shouldBeRtl = direction === ResponsiveDisplayStackEnum.RTL;

  // Mettre à jour l'attribut dir et la classe spm_dir_*
  if (shouldBeRtl) {
    column.setAttribute('dir', direction);
    column.classList.remove('spm_dir_ltr');
    column.classList.add('spm_dir_rtl');
    const columns_inside = Array.from(column.querySelectorAll('.spm_droppable_widget'));
    columns_inside.forEach((column_inside) => {
      column_inside.setAttribute('dir', ResponsiveDisplayStackEnum.LTR);
      column_inside.classList.remove('spm_dir_rtl');
      column_inside.classList.add('spm_dir_ltr');
    });
  } else {
    column.removeAttribute('dir');
    column.classList.remove('spm_dir_rtl', 'spm_dir_ltr');
    const columns_inside = Array.from(column.querySelectorAll('.spm_droppable_widget'));
    columns_inside.forEach((column_inside) => {
      column_inside.removeAttribute('dir');
      column_inside.classList.remove('spm_dir_rtl', 'spm_dir_ltr');
    });
  }

  // Réordonner seulement si l'état change
  if (isCurrentlyRtl !== shouldBeRtl) {
    const trs = Array.from(column.querySelectorAll('tr'));
    trs.forEach((tr) => {
      const cells = Array.from(tr.children) as HTMLElement[];
      const reversed = [...cells].reverse();
      reversed.forEach((cell) => tr.appendChild(cell));
    });
  }
};

export const setResponsiveDisplayStack = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));

  // Toggle spm_responsive
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (property.value === ResponsiveDisplayStackEnum.LTR || property.value === ResponsiveDisplayStackEnum.RTL) {
        element.classList.add(property.name);
      } else {
        element.classList.remove(property.name);
        // Supprime la classe de tous les enfants
        Array.from(element.querySelectorAll(`.${property.name}`)).forEach((child) => {
          (child as HTMLElement).classList.remove(property.name);
        });
      }
    });
  }

  // Add attribute dir to .columns
  const element = template.querySelector(selector);
  if (element) {
    const columns = Array.from(element.querySelectorAll('.columns'));
    columns.forEach((column) => {
      reorderColumn(column, property.value);
    });
  }
};

export const toggleAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (element.hasAttribute(property.name)) {
        element.removeAttribute(property.name);
      } else {
        element.setAttribute(property.name, property.value);
      }
    });
  }
};

export const toggleRequiredAttribute = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (element.hasAttribute(property.name)) {
        element.removeAttribute(property.name);
      } else {
        element.setAttribute(property.name, property.value);
        (element as HTMLFormElement).reportValidity();
      }
    });
  }
};

export const toggleShowLabelInside = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      if (element.classList.contains(property.name)) {
        store.commit('liveEditor/setFormWidgetShowLabel', false);
      } else {
        store.commit('liveEditor/setFormWidgetShowLabel', true);
      }
    });
  }
};

export const triggerCheckboxRadioRequired = (selector: string, inputSelector: string, requiredClass: string) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector));
  if (elements.length > 0) {
    elements.forEach((element: HTMLElement) => {
      const inputElement = element.querySelector(inputSelector);
      if (inputElement) {
        if (element.classList.contains(requiredClass)) {
          (inputElement as HTMLInputElement).required = true;
          (inputElement as HTMLInputElement).reportValidity();
        } else {
          (inputElement as HTMLInputElement).required = false;
        }
      }
    });
  }
};

export const setRichTextingCTAItems = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null | undefined = template?.querySelector(selector) as HTMLElement | null;
  if (element && undefined !== property.value && Array.isArray(property.value)) {
    const defaultLanguage: string = UserState.activeShop?.langs.filter((lang) => lang.default).map((lang) => lang.id)[0] ?? 'fr';

    const existingCTAElement = Array.from(element.querySelectorAll('.rcs-message__cta__item'));
    const existingCTAElementTranslationIds: string[] = [];

    existingCTAElement.forEach((ctaElement) => {
      const spmTranslationId = ctaElement.querySelector(TRANSLATION_MARKUP)?.getAttribute('id');
      const ctaItem = property.value.find((item: any) => item.translationId === spmTranslationId?.replace('LANG_', '').split('.')[0]);
      if (ctaItem) {
        existingCTAElementTranslationIds.push(ctaItem.translationId);
        sanitizeTranslationMarkup(ctaElement as HTMLElement).innerHTML = ctaItem.value;
        ctaElement.setAttribute('data-rcs-action-type', ctaItem.action.type);
        ctaElement.setAttribute('href', ctaItem.action.value);
        ctaElement.setAttribute(`${TRANSLATION_ATTRIBUTE}-href`, `LANG_${ctaItem.translationId}.href`);
        if (ctaItem.description && ctaItem.action.type === 'webAddress') {
          ctaElement.setAttribute('data-rcs-url-description', ctaItem.description);
          ctaElement.setAttribute(`${TRANSLATION_ATTRIBUTE}-data-rcs-url-description`, `LANG_${ctaItem.translationId}.data-rcs-url-description`);
          // Ensure translation exists in state (first time adding description to existing CTA)
          const descKey = `${ctaItem.translationId}.data-rcs-url-description`;
          const descTranslationsToAdd: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
            section: TemplateEditorState.template?.activeSection ? asInt(TemplateEditorState.template?.activeSection) : 0,
            language: language.id,
            fieldType: 'data-rcs-url-description',
            key: descKey,
            value: ctaItem.description,
            callback: (translationValue: string) => translationValue,
          })) ?? [];
          addTranslationsToState(descTranslationsToAdd);
          replaceTranslationInState(descKey, defaultLanguage, ctaItem.description);
        } else {
          ctaElement.removeAttribute('data-rcs-url-description');
          ctaElement.removeAttribute(`${TRANSLATION_ATTRIBUTE}-data-rcs-url-description`);
          removeTranslation(`${ctaItem.translationId}.data-rcs-url-description`);
        }
        const redirectMatch = ctaItem.action.value.match(/#redirect_(.+)/);
        if (redirectMatch && redirectMatch.length > 0 && redirectMatch[1]) {
          ctaElement.setAttribute('data-redirect', redirectMatch[1]);
        } else {
          ctaElement.removeAttribute('data-redirect');
        }
        replaceTranslationInState(`${ctaItem.translationId}.text`, defaultLanguage, ctaItem.value);
        replaceTranslationInState(`${ctaItem.translationId}.href`, defaultLanguage, ctaItem.action.value);
      } else {
        ctaElement.remove();
      }
    });

    const ctaElementsToAppend = property.value.filter((ctaItem: any) => !existingCTAElementTranslationIds.includes(ctaItem.translationId));

    // eslint-disable-next-line no-restricted-syntax
    for (const cta of ctaElementsToAppend) {
      const newDiv = document.createElement('div');

      const translations: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
        section: TemplateEditorState.template?.activeSection ? asInt(TemplateEditorState.template?.activeSection) : 0,
        language: language.id,
        fieldType: 'text',
        key: `${cta.translationId}.text`,
        value: cta.value,
        callback: (translationValue: string) => translationValue,
      })) ?? [];
      const actionTranslations: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
        section: TemplateEditorState.template?.activeSection ? asInt(TemplateEditorState.template?.activeSection) : 0,
        language: language.id,
        fieldType: 'link',
        key: `${cta.translationId}.href`,
        value: cta.action.value,
        callback: (translationValue: string) => translationValue,
      })) ?? [];
      const descTranslations: Translation[] = (cta.description && cta.action.type === 'webAddress') ? (UserState.activeShop?.langs.map((language: Lang) => ({
        section: TemplateEditorState.template?.activeSection ? asInt(TemplateEditorState.template?.activeSection) : 0,
        language: language.id,
        fieldType: 'data-rcs-url-description',
        key: `${cta.translationId}.data-rcs-url-description`,
        value: cta.description,
        callback: (translationValue: string) => translationValue,
      })) ?? []) : [];
      addTranslationsToState([...translations, ...actionTranslations, ...descTranslations]);

      newDiv.innerHTML = `<${TRANSLATION_MARKUP} id="LANG_${cta.translationId}.text">${cta.value}</${TRANSLATION_MARKUP}>`;
      newDiv.setAttribute('class', 'rcs-message__cta__item');
      newDiv.setAttribute('data-rcs-action-type', cta.action.type);
      newDiv.setAttribute('href', cta.action.value);
      if (cta.description && cta.action.type === 'webAddress') {
        newDiv.setAttribute('data-rcs-url-description', cta.description);
        newDiv.setAttribute(`${TRANSLATION_ATTRIBUTE}-data-rcs-url-description`, `LANG_${cta.translationId}.data-rcs-url-description`);
      }
      const redirectMatch = cta.action.value.match(/#redirect_(.+)/);
      if (redirectMatch && redirectMatch.length > 0 && redirectMatch[1]) {
        newDiv.setAttribute('data-redirect', redirectMatch[1]);
      } else {
        newDiv.removeAttribute('data-redirect');
      }
      newDiv.setAttribute(`${TRANSLATION_ATTRIBUTE}-href`, `LANG_${cta.translationId}.href`);
      element.append(newDiv);
    }

    updateSectionsInState(false);
    sortTranslationInState();
    refreshNextSteps();

    // If smartlist is enabled, refresh carousel to sync CTAs on cards
    if (isRcsSmartListEnabled()) {
      refreshRcsSmartlist();
    }
  }
};

export const setWhatsappCTAItems = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null | undefined = template?.querySelector(selector) as HTMLElement | null;
  if (element && undefined !== property.value && Array.isArray(property.value)) {
    const defaultLanguage: string = UserState.activeShop?.langs.filter((lang) => lang.default).map((lang) => lang.id)[0] ?? 'fr';

    const existingCTAElement = Array.from(element.querySelectorAll('.whatsapp-message__cta__item'));
    const existingCTAElementTranslationIds: string[] = [];

    existingCTAElement.forEach((ctaElement) => {
      const spmTranslationId = ctaElement.querySelector(TRANSLATION_MARKUP)?.getAttribute('id');
      const ctaItem = property.value.find((item: any) => item.translationId === spmTranslationId?.replace('LANG_', '').split('.')[0]);
      if (ctaItem) {
        existingCTAElementTranslationIds.push(ctaItem.translationId);
        sanitizeTranslationMarkup(ctaElement as HTMLElement).innerHTML = ctaItem.value;
        ctaElement.setAttribute('data-whatsapp-action-type', ctaItem.action.type);
        ctaElement.setAttribute('href', ctaItem.action.value);
        ctaElement.setAttribute(`${TRANSLATION_ATTRIBUTE}-href`, `LANG_${ctaItem.translationId}.href`);
        const redirectMatch = ctaItem.action.value.match(/#redirect_(.+)/);
        if (redirectMatch && redirectMatch.length > 0 && redirectMatch[1]) {
          ctaElement.setAttribute('data-redirect', redirectMatch[1]);
        } else {
          ctaElement.removeAttribute('data-redirect');
        }
        replaceTranslationInState(`${ctaItem.translationId}.text`, defaultLanguage, ctaItem.value);
        replaceTranslationInState(`${ctaItem.translationId}.href`, defaultLanguage, ctaItem.action.value);
      } else {
        ctaElement.remove();
      }
    });

    const ctaElementsToAppend = property.value.filter((ctaItem: any) => !existingCTAElementTranslationIds.includes(ctaItem.translationId));

    // eslint-disable-next-line no-restricted-syntax
    for (const cta of ctaElementsToAppend) {
      const newDiv = document.createElement('div');

      const translations: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
        section: TemplateEditorState.template?.activeSection ? asInt(TemplateEditorState.template?.activeSection) : 0,
        language: language.id,
        fieldType: 'text',
        key: `${cta.translationId}.text`,
        value: cta.value,
        callback: (translationValue: string) => translationValue,
      })) ?? [];
      const actionTranslations: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
        section: TemplateEditorState.template?.activeSection ? asInt(TemplateEditorState.template?.activeSection) : 0,
        language: language.id,
        fieldType: 'link',
        key: `${cta.translationId}.href`,
        value: cta.action.value,
        callback: (translationValue: string) => translationValue,
      })) ?? [];
      addTranslationsToState([...translations, ...actionTranslations]);

      newDiv.innerHTML = `<${TRANSLATION_MARKUP} id="LANG_${cta.translationId}.text">${cta.value}</${TRANSLATION_MARKUP}>`;
      newDiv.setAttribute('class', 'whatsapp-message__cta__item');
      newDiv.setAttribute('data-whatsapp-action-type', cta.action.type);
      newDiv.setAttribute('href', cta.action.value);
      const redirectMatch = cta.action.value.match(/#redirect_(.+)/);
      if (redirectMatch && redirectMatch.length > 0 && redirectMatch[1]) {
        newDiv.setAttribute('data-redirect', redirectMatch[1]);
      } else {
        newDiv.removeAttribute('data-redirect');
      }
      newDiv.setAttribute(`${TRANSLATION_ATTRIBUTE}-href`, `LANG_${cta.translationId}.href`);
      element.append(newDiv);
    }

    updateSectionsInState(false);
    refreshNextSteps();

    // If smartlist is enabled, refresh carousel to sync CTAs on cards
    if (isWhatsappSmartListEnabled()) {
      refreshWhatsappSmartlist();
    }
  }
};

/**
 * Set default country for phone input widget
 * Updates both data-default-country attribute and selected option in select
 */
export const setDefaultCountry = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const element: HTMLElement | null | undefined = template?.querySelector(selector) as HTMLElement | null;

  if (element && property.value) {
    const countryCode = property.value as string;

    // Update data-default-country attribute
    element.setAttribute('data-default-country', countryCode);

    // Find the custom select element
    const customSelectElement = element.querySelector('.spm_widget_input_phone__country_select') as HTMLElement | null;

    if (customSelectElement) {
      // Find country data from countriesList
      const country = countriesList.find((c) => c.code === countryCode);

      if (country) {
        // Update trigger display with flag and dial code
        const trigger = customSelectElement.querySelector('.spm_custom_select__trigger') as HTMLElement | null;
        if (trigger) {
          trigger.textContent = `${country.flag} (${country.dialCode})`;
        }

        // Update hidden input value
        const hiddenInput = customSelectElement.querySelector('.spm_custom_select__hidden_input') as HTMLInputElement | null;
        if (hiddenInput) {
          hiddenInput.value = countryCode;
        }

        // Update selected state on options
        const allOptions = customSelectElement.querySelectorAll('.spm_custom_select__option');
        allOptions.forEach((option) => {
          option.classList.remove('spm_custom_select__option--selected');
        });

        const targetOption = customSelectElement.querySelector(`.spm_custom_select__option[data-value="${countryCode}"]`) as HTMLElement | null;
        if (targetOption) {
          targetOption.classList.add('spm_custom_select__option--selected');
        }
      }
    }

    updateSectionsInState(false);
  }
};

// Countdown widget setters
// Display countdown logic is now handled by the NestJS spm-countdown.js script
// loaded in the iframe, with MutationObserver for live attribute changes.
// These setters only need to call setAttribute — the observer handles the rest.

export const cleanupAllCountdownIntervals = (): void => {
  // No-op: intervals are now managed inside the iframe by spm-countdown.js
};

export const startCountdownInterval = (widget: HTMLElement, _selector: string) => {
  // Trigger initialization via the iframe's shopimind.initCountdowns()
  const iframeWindow = (document.querySelector('iframe.template-wrapper') as HTMLIFrameElement)?.contentWindow as any;
  if (iframeWindow?.shopimind?.initCountdowns) {
    // Reset initialized flag so initCountdowns picks it up
    delete (widget as any)._countdownInitialized;
    iframeWindow.shopimind.initCountdowns();
  }
};

export const setCountdownEndDate = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

export const setCountdownRelativeDate = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

export const setCountdownDateVariable = (selector: string, property: Property) => {
  setAttribute(selector, property);
  // Also set data-end-date to the variable syntax so it gets resolved at render time
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector) as HTMLElement;
  if (element && property.value) {
    element.setAttribute('data-end-date', `{var=${property.value}}`);
  }
};

export const setCountdownTimezone = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

export const setCountdownShowDays = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

export const setCountdownShowHours = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

export const setCountdownShowMinutes = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

export const setCountdownShowSeconds = (selector: string, property: Property) => {
  setAttribute(selector, property);
};

// Custom setter for button alignment that handles both align attribute and margin CSS
export const setButtonAlignment = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const elements: Array<HTMLElement> = Array.from(template?.querySelectorAll(selector) || []);

  // Determine context: widget-specific call vs global config call
  // Global calls target sections, widget calls target specific elements
  const isGlobalCall = !selector.includes('#spm_widget_');

  const marginMappings: { [key: string]: string } = {
    left: '0 auto 0 0',
    center: '0 auto',
    right: '0 0 0 auto',
  };

  const targetMarginValue = marginMappings[property.value as string];

  if (isGlobalCall) {
    // Global config call: loop through each button and check for widget-specific styles
    elements.forEach((element: HTMLElement) => {
      // Build widget-specific selector for this button element
      const widgetTable = element.closest('table[id^="spm_widget_"]');
      const widgetSelector = `#${widgetTable?.id} table.button, #${widgetTable?.id} table.spm_button`;
      const dynamicStyles = getDynamicStylesForSelector(widgetSelector, false);

      let hasWidgetMargin = false;
      // Check for margin shorthand or individual margin properties
      if (dynamicStyles.margin ||
          dynamicStyles['margin-top'] ||
          dynamicStyles['margin-right'] ||
          dynamicStyles['margin-bottom'] ||
          dynamicStyles['margin-left']) {
        hasWidgetMargin = true;
      }

      // Only set align attribute if no widget-specific margin is defined
      if (!hasWidgetMargin && property.value) {
        element.setAttribute('align', property.value as string);
      }
    });
  } else {
    // Widget-specific call: apply setAttribute directly without verification
    setAttribute(selector, property);
  }

  // Always set the corresponding margin CSS (cascade will handle priority)
  if (targetMarginValue) {
    const marginProperty = {
      ...property,
      name: 'margin',
      value: targetMarginValue,
    };
    setCssProperty(selector, marginProperty);
  }
};

export const setMarginAlignment = (selector: string, property: Property) => {
  if (property.value !== undefined) {
    const marginMappings: { [key: string]: { left: string; right: string } } = {
      left: { left: '0', right: 'auto' },
      center: { left: 'auto', right: 'auto' },
      right: { left: 'auto', right: '0' },
    };

    const mapping = marginMappings[property.value as string];
    if (mapping) {
      setCssProperty(selector, { ...property, name: 'margin-left', value: mapping.left });
      setCssProperty(selector, { ...property, name: 'margin-right', value: mapping.right });
    }
  }
};

// ============================================
// EMAIL COUNTDOWN WIDGET SETTERS (GIF-based)
// ============================================

const EMAIL_COUNTDOWN_GIF_BASE_URL = `${process.env.VUE_APP_NESTJS_TRACKING_API_URL}/countdown/gif`;

/**
 * Convert rgba color to hex format
 * @param rgba - Color in rgba format (e.g., "rgba(104,73,73,1)")
 * @returns Color in hex format (e.g., "#684949")
 */
const rgbaToHex = (rgba: string): string => {
  // If already hex format, return as is
  if (rgba.startsWith('#')) {
    return rgba;
  }

  // If transparent, return as is
  if (rgba === 'transparent') {
    return rgba;
  }

  // Parse rgba format
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (match) {
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  // Return original if not parseable
  return rgba;
};

/**
 * Build the GIF URL with all countdown parameters
 */
const buildEmailCountdownGifUrl = (widget: HTMLElement): string => {
  const params = new URLSearchParams();

  // Date settings
  params.set('endDate', widget.getAttribute('data-end-date') || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString());
  params.set('timezone', widget.getAttribute('data-timezone') || 'Europe/Paris');

  // Visibility
  params.set('showDays', widget.getAttribute('data-show-days') || 'true');
  params.set('showHours', widget.getAttribute('data-show-hours') || 'true');
  params.set('showMinutes', widget.getAttribute('data-show-minutes') || 'true');
  params.set('showSeconds', widget.getAttribute('data-show-seconds') || 'true');

  // Labels — taken from the translated attributes, which already hold the language
  // currently displayed in the editor (see countdown-email-i18n).
  const labels = countdownLabelsOf(widget);
  COUNTDOWN_LABEL_FIELDS.forEach((field) => params.set(field.param, labels[field.param]));

  // Digit styles
  params.set('digitBgColor', (widget.getAttribute('data-digit-bg-color') || '#1a1a1a').replace('#', ''));
  params.set('digitColor', (widget.getAttribute('data-digit-color') || '#ffffff').replace('#', ''));
  params.set('digitSize', widget.getAttribute('data-digit-size') || '48');
  params.set('digitFontWeight', widget.getAttribute('data-digit-font-weight') || '700');
  params.set('digitBorderRadius', widget.getAttribute('data-digit-border-radius') || '8');
  params.set('digitPadding', widget.getAttribute('data-digit-padding') || '15');

  // Label styles
  params.set('labelColor', (widget.getAttribute('data-label-color') || '#ffffff').replace('#', ''));
  params.set('labelSize', widget.getAttribute('data-label-size') || '12');
  params.set('labelFontWeight', widget.getAttribute('data-label-font-weight') || '400');

  // Separator styles
  params.set('separatorType', widget.getAttribute('data-separator-type') || 'colon');
  params.set('separatorColor', (widget.getAttribute('data-separator-color') || '#ffffff').replace('#', ''));

  // Layout styles
  params.set('layout', widget.getAttribute('data-layout') || 'horizontal');
  params.set('gap', widget.getAttribute('data-gap') || '10');
  params.set('width', widget.getAttribute('data-width') || '500');
  params.set('height', widget.getAttribute('data-height') || '100');
  params.set('bgColor', (widget.getAttribute('data-bg-color') || 'transparent').replace('#', ''));

  return `${EMAIL_COUNTDOWN_GIF_BASE_URL}?${params.toString()}`;
};

/**
 * Remove every loading overlay of a countdown widget: the one inside the widget, and the ones
 * left next to it. Before SC-3945 the loader was appended to the widget <table> itself; once
 * saved and parsed again, the HTML parser moves such a <div> out of the table, just before it.
 */
const removeCountdownLoaders = (widget: HTMLElement) => {
  widget.querySelectorAll('.spm_countdown_loader').forEach((loader) => loader.remove());
  Array.from(widget.parentElement?.children ?? [])
    .filter((element) => element.classList.contains('spm_countdown_loader'))
    .forEach((loader) => loader.remove());
};

/**
 * Show loading overlay on countdown widget
 */
const showCountdownLoader = (widget: HTMLElement) => {
  // Remove existing loader if any
  removeCountdownLoaders(widget);

  // Create loader overlay
  const loader = document.createElement('div');
  loader.className = 'spm_countdown_loader';
  loader.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  `;

  // Create spinner
  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-top-color: #3498db;
    border-radius: 50%;
    animation: spm-countdown-spin 0.8s linear infinite;
  `;

  // Add keyframes if not exists
  const doc = widget.ownerDocument;
  if (doc && !doc.getElementById('spm-countdown-loader-styles')) {
    const style = doc.createElement('style');
    style.id = 'spm-countdown-loader-styles';
    style.textContent = `
      @keyframes spm-countdown-spin {
        to { transform: rotate(360deg); }
      }
    `;
    doc.head.appendChild(style);
  }

  loader.appendChild(spinner);

  // Ensure widget has relative positioning
  const widgetStyle = window.getComputedStyle(widget);
  if (widgetStyle.position === 'static') {
    widget.style.position = 'relative';
  }

  // Inside the widget cell, not the <table>: a <div> child of a table is invalid HTML and
  // would be moved out of the widget the next time the section is parsed (SC-3945).
  const cell = widget.querySelector('th, td') ?? widget;
  cell.appendChild(loader);
};

/**
 * Hide loading overlay on countdown widget
 */
const hideCountdownLoader = (widget: HTMLElement) => {
  removeCountdownLoaders(widget);
};

/**
 * Update the countdown GIF image source
 */
const updateEmailCountdownGif = (widget: HTMLElement) => {
  const img = widget.querySelector('img.spm_countdown_gif') as HTMLImageElement;
  if (img) {
    const gifUrl = buildEmailCountdownGifUrl(widget);

    // Add cache-busting parameter to force reload
    const cacheBustUrl = `${gifUrl}&_t=${Date.now()}`;

    // Show loader
    showCountdownLoader(widget);

    // Also update width/height attributes immediately
    const width = widget.getAttribute('data-width') || '500';
    const height = widget.getAttribute('data-height') || '100';
    img.setAttribute('width', width);
    img.setAttribute('height', height);
    img.style.maxWidth = '100%';
    img.style.height = 'auto';

    // Use the image element's own load event
    const onLoadHandler = () => {
      hideCountdownLoader(widget);
      img.removeEventListener('load', onLoadHandler);
      img.removeEventListener('error', onErrorHandler);
    };
    const onErrorHandler = () => {
      hideCountdownLoader(widget);
      img.removeEventListener('load', onLoadHandler);
      img.removeEventListener('error', onErrorHandler);
    };

    img.addEventListener('load', onLoadHandler);
    img.addEventListener('error', onErrorHandler);

    // Update the src to trigger loading
    img.setAttribute('src', cacheBustUrl);
  }
};

export const setEmailCountdownEndDate = (selector: string, property: Property) => {
  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

export const setEmailCountdownDateVariable = (selector: string, property: Property) => {
  setAttribute(selector, property);
  // Also set data-end-date to the variable syntax so it gets resolved at render time
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(selector) as HTMLElement;
  if (element && property.value) {
    element.setAttribute('data-end-date', `{var=${property.value}}`);
  }
};

export const setEmailCountdownTimezone = (selector: string, property: Property) => {
  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

/** Languages the countdown labels must cover (see countdown-email-i18n). */
const countdownLangContext = (): CountdownLangContext => ({
  langs: UserState.activeShop?.langs?.map((lang: Lang) => lang.id) ?? [getShopDefaultLang()],
  defaultLang: getShopDefaultLang(),
});

/**
 * Configuration-panel edit of a unit label. The panel always edits the default
 * language: the value is mirrored into the translated attribute and the default
 * language translation, then the GIF is rebuilt.
 */
export const setEmailCountdownLabel = (selector: string, property: Property) => {
  const value = sanitizeCountdownLabel(String(property.value ?? ''));
  setAttribute(selector, { ...property, value });
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    syncCountdownLabelFromConfig(widget, property.name, value, countdownLangContext());
    updateEmailCountdownGif(widget);
  }
};

export const setEmailCountdownStyle = (selector: string, property: Property) => {
  // Convert rgba colors to hex for color properties
  const colorProperties = [
    'data-bg-color',
    'data-digit-bg-color',
    'data-digit-color',
    'data-label-color',
    'data-separator-color',
  ];

  if (colorProperties.includes(property.name) && property.value) {
    property.value = rgbaToHex(property.value);
  }

  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

export const setEmailCountdownShowDays = (selector: string, property: Property) => {
  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

export const setEmailCountdownShowHours = (selector: string, property: Property) => {
  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

export const setEmailCountdownShowMinutes = (selector: string, property: Property) => {
  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

export const setEmailCountdownShowSeconds = (selector: string, property: Property) => {
  setAttribute(selector, property);
  const template = getTemplateIframeDocument();
  const widget = template?.querySelector(selector) as HTMLElement;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

/**
 * Initialize email countdown widget with default GIF
 */
export const initializeEmailCountdownWidget = (widgetId: string) => {
  const template = getTemplateIframeDocument();
  const widget = template?.getElementById(widgetId) as HTMLElement;
  if (widget) {
    // Register the per-language labels before deriving the URL from them.
    ensureCountdownLabelTranslations(widget, countdownLangContext());
    updateEmailCountdownGif(widget);
  }
};

/**
 * Rebuild the GIF of the widget holding a countdown label translation — called when
 * the translation panel writes a label (edit or language switch) so the preview
 * follows the displayed language.
 */
export const refreshEmailCountdownForLabel = (element: HTMLElement) => {
  const widget = element.closest(COUNTDOWN_WIDGET_SELECTOR) as HTMLElement | null;
  if (widget) {
    updateEmailCountdownGif(widget);
  }
};

// Apply global margin CSS to button align attribute on widget creation
export const applyGlobalMarginToButtonAlign = (widgetId: string) => {
  // Only apply for EMAIL templates
  const templateType = TemplateEditorState.template?.type;
  if (!isEmailTemplate(templateType)) {
    return;
  }

  const template = getTemplateIframeDocument();
  if (!template) {
    return;
  }

  // Get the widget element
  const widgetElement = template.getElementById(widgetId);
  if (!widgetElement) {
    return;
  }

  // Find all table.button and table.spm_button elements inside the widget
  const buttonElements = widgetElement.querySelectorAll('table.button, table.spm_button');
  if (!buttonElements || buttonElements.length === 0) {
    return; // No table.button or table.spm_button found in this widget
  }

  // Get margin CSS from widget-specific selector (try spm_button first, fallback to button)
  let widgetButtonSelector = `#${widgetId} table.spm_button`;
  let marginResult = getCssPropertyByName(widgetButtonSelector, 'margin');
  if (!marginResult) {
    widgetButtonSelector = `#${widgetId} table.button`;
    marginResult = getCssPropertyByName(widgetButtonSelector, 'margin');
  }

  // Extract string value from result (can be string or object)
  let globalMargin: string = '';
  if (typeof marginResult === 'string') {
    globalMargin = marginResult;
  } else if (marginResult && typeof marginResult === 'object' && 'value' in marginResult) {
    globalMargin = String(marginResult.value);
  }

  if (!globalMargin) {
    return; // No margin found, do nothing
  }

  // Reverse mapping: margin value → align value
  const marginToAlignMappings: { [key: string]: string } = {
    '0 auto 0 0': 'left',
    '0px auto 0px 0px': 'left',
    '0 auto': 'center',
    '0px auto': 'center',
    '0 0 0 auto': 'right',
    '0px 0px 0px auto': 'right',
  };

  // Normalize margin value (remove extra spaces)
  const normalizedMargin = globalMargin.replace(/\s+/g, ' ').trim();
  const alignValue = marginToAlignMappings[normalizedMargin];

  if (!alignValue) {
    return; // No matching align value found, do nothing
  }

  // Apply align attribute to all button elements found
  buttonElements.forEach((buttonElement) => {
    buttonElement.setAttribute('align', alignValue);
  });
};

/**
 * Apply global border-radius VML to button widget on drop
 * Creates VML if global border-radius is defined and no local override exists
 * @param widgetId - The ID of the dropped widget
 */
export const applyGlobalBorderRadiusToButton = (widgetId: string) => {
  // Only apply for EMAIL templates
  const templateType = TemplateEditorState.template?.type;
  if (!isEmailTemplate(templateType)) {
    return;
  }

  // Use setTimeout to ensure DOM is fully rendered and styles applied after drop
  setTimeout(() => {
    const template = getTemplateIframeDocument();
    if (!template) {
      return;
    }

    // Get the widget element
    const widgetElement = template.getElementById(widgetId);
    if (!widgetElement) {
      return;
    }

    // Find table.spm_button inside the widget
    const buttonTable = widgetElement.querySelector('table.spm_button');
    if (!buttonTable) {
      return;
    }

    // Get effective border-radius using computed styles (includes global styles)
    const radiusPx = getEffectiveButtonBorderRadius(buttonTable);

    if (radiusPx === 0) {
      return; // No border-radius, no VML needed
    }

    // Apply VML to the button
    applyButtonVml(buttonTable, radiusPx);
  }, 100);
};

/**
 * Remove VML roundrect comments and mso conditional comments from an element
 * @param element - The element to remove VML comments from
 */
export const removeVmlRoundrectComments = (element: Element) => {
  const nodesToRemove: Node[] = [];
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.COMMENT_NODE) {
      const content = node.textContent || '';
      if (content.includes('v:roundrect') || content.includes('v:image')
          || content.includes('[if !mso]') || content.includes('<![endif]')) {
        nodesToRemove.push(node);
      }
    }
  });
  nodesToRemove.forEach((node) => node.parentNode?.removeChild(node));
};

/**
 * Set image src attribute and refresh VML if border-radius is active
 * @param selector - CSS selector for the image element
 * @param property - Property containing the src value
 */
export const setImageSrcWithVmlRefresh = (selector: string, property: Property) => {
  // First set the attribute normally
  setAttribute(selector, property);

  // Then refresh VML if needed (after a small delay to ensure src is set)
  setTimeout(() => {
    refreshImageVmlOnSrcChange(selector);
  }, 100);
};

/**
 * Cleanup VML for image widget - called when border-radius is toggled off
 * @param selector - CSS selector for the image element
 */
export const cleanupImageVml = (selector: string) => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const imgElement = getTemplateIframeDocument()?.querySelector(selector) as HTMLImageElement;
  if (!imgElement) {
    return;
  }

  const thElement = imgElement.closest('th');
  if (thElement) {
    removeVmlRoundrectComments(thElement);
  }

  // Remove border-radius from dynamic styles
  const widgetSelector = selector.replace(' > tbody > tr > th img', '');
  removeDynamicStyle(`${widgetSelector} > tbody > tr > th img`, { borderRadius: '' });
  removeDynamicStyle(`${widgetSelector} > tbody > tr > th a`, { borderRadius: '', display: '', overflow: '' });
};

/**
 * Set border-radius on image widget with VML compatibility for Outlook
 * Uses v:roundrect with v:fill type="frame" for rounded corners in Outlook
 * @param selector - CSS selector for the image element
 * @param property - Property containing the border-radius value
 */
export const setImageBorderRadius = (selector: string, property: Property) => {
  // Apply CSS border-radius to img element
  setCssProperty(selector, property);

  // Only generate VML for email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Parse border-radius value
  const radiusValue = property.value || '0px';
  const radiusPx = parseInt(radiusValue.replace('px', ''), 10) || 0;

  // Get the img element
  const imgElement = getTemplateIframeDocument()?.querySelector(selector) as HTMLImageElement;
  if (!imgElement) {
    return;
  }

  // Get the parent <th> element (container for VML)
  const thElement = imgElement.closest('th');
  if (!thElement) {
    return;
  }

  // Remove existing VML comments
  removeVmlRoundrectComments(thElement);

  // If radius is 0, just remove VML and return
  if (radiusPx === 0) {
    return;
  }

  // Get image dimensions
  const imgWidth = imgElement.offsetWidth || imgElement.naturalWidth || 300;
  const imgHeight = imgElement.offsetHeight || imgElement.naturalHeight || 200;
  const imgSrc = imgElement.getAttribute('src') || '';

  // Calculate VML arcsize (percentage based on smallest dimension)
  // arcsize is a percentage where 50% = fully rounded (circle if square)
  const smallestDim = Math.min(imgWidth, imgHeight);
  const arcsize = Math.min(50, Math.round((radiusPx / smallestDim) * 100));

  // Get link href if image is wrapped in an anchor
  const anchorElement = imgElement.closest('a');
  const linkHref = anchorElement?.getAttribute('href') || '';
  const hrefAttr = linkHref ? ` href="${linkHref}"` : '';

  // Build VML for Outlook (shown only in MSO)
  const vmlBlock = `<!--[if mso]>
<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"
  style="width:${imgWidth}px;height:${imgHeight}px;v-text-anchor:middle;"
  arcsize="${arcsize}%"
  stroke="false"
  fillcolor="${getEffectiveBackgroundColor(imgElement)}"${hrefAttr}>
  <v:fill type="frame" src="${imgSrc}" />
  <w:anchorlock/>
</v:roundrect>
<![endif]-->`;

  // Conditional comments to hide original image in Outlook
  const nonMsoOpen = '<!--[if !mso]><!-->';
  const nonMsoClose = '<!--<![endif]-->';

  // Insert VML before the anchor or img
  const insertTarget = anchorElement || imgElement;
  insertTarget.insertAdjacentHTML('beforebegin', vmlBlock);

  // Wrap original image/anchor with conditional comments to hide in Outlook
  insertTarget.insertAdjacentHTML('beforebegin', nonMsoOpen);
  insertTarget.insertAdjacentHTML('afterend', nonMsoClose);

  // Apply CSS border-radius for non-Outlook clients
  const widgetSelector = selector.replace(' > tbody > tr > th img', '');
  addOrUpdateDynamicStyle(`${widgetSelector} > tbody > tr > th img`, {
    borderRadius: radiusValue,
  });

  // Also apply border-radius to the anchor if present (for consistent rendering)
  if (anchorElement) {
    addOrUpdateDynamicStyle(`${widgetSelector} > tbody > tr > th a`, {
      borderRadius: radiusValue,
      display: 'inline-block',
      overflow: 'hidden',
    });
  }
};

/**
 * Refresh VML for image widget when link changes (if border-radius is active)
 * Called from setLink to update VML href when link is modified after border-radius
 * @param thElement - The <th> element containing the image
 */
export const refreshImageVmlOnLinkChange = (thElement: HTMLElement) => {
  // Only process for email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Check if there's an existing VML comment (meaning border-radius is active)
  let hasVml = false;
  thElement.childNodes.forEach((node) => {
    if (node.nodeType === Node.COMMENT_NODE && node.textContent?.includes('v:roundrect')) {
      hasVml = true;
    }
  });

  if (!hasVml) {
    return; // No VML, nothing to update
  }

  // Get the img element
  const imgElement = thElement.querySelector('img') as HTMLImageElement;
  if (!imgElement) {
    return;
  }

  // Get current border-radius from dynamic styles
  const widgetElement = thElement.closest('[id^="spm_widget"]');
  if (!widgetElement) {
    return;
  }

  const widgetSelector = `body #spm_body #${widgetElement.id}`;
  const imgSelector = `${widgetSelector} > tbody > tr > th img`;
  const dynamicStyles = getDynamicStylesForSelector(imgSelector, false);
  const radiusValue = dynamicStyles.borderRadius || '0px';
  const radiusPx = parseInt(radiusValue.replace('px', ''), 10) || 0;

  if (radiusPx === 0) {
    return; // No border-radius, remove VML
  }

  // Delegate all VML generation to applyImageVml
  applyImageVml(imgElement, radiusPx);
};

/**
 * Refresh VML for image widget when image src changes (if border-radius is active)
 * Called from setAttribute to update VML src when image is modified
 * @param selector - CSS selector for the image element
 */
export const refreshImageVmlOnSrcChange = (selector: string) => {
  // Only process for email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const imgElement = getTemplateIframeDocument()?.querySelector(selector) as HTMLImageElement;
  if (!imgElement) {
    return;
  }

  const thElement = imgElement.closest('th');
  if (!thElement) {
    return;
  }

  // Check if there's an existing VML comment (meaning border-radius is active)
  let hasVml = false;
  thElement.childNodes.forEach((node) => {
    if (node.nodeType === Node.COMMENT_NODE && node.textContent?.includes('v:roundrect')) {
      hasVml = true;
    }
  });

  if (!hasVml) {
    return; // No VML, nothing to update
  }

  // Get current border-radius from dynamic styles
  const widgetElement = thElement.closest('[id^="spm_widget"]');
  if (!widgetElement) {
    return;
  }

  const widgetSelector = `body #spm_body #${widgetElement.id}`;
  const imgSelector = `${widgetSelector} > tbody > tr > th img`;
  const dynamicStyles = getDynamicStylesForSelector(imgSelector, false);
  const radiusValue = dynamicStyles.borderRadius || '0px';
  const radiusPx = parseInt(radiusValue.replace('px', ''), 10) || 0;

  if (radiusPx === 0) {
    return;
  }

  // Delegate all VML generation to applyImageVml
  applyImageVml(imgElement, radiusPx);
};

/**
 * Remove VML button comments from the inner th element of table.spm_button
 * @param innerTh - The inner th element of table.spm_button
 */
export const removeVmlButtonComments = (innerTh: Element) => {
  const nodesToRemove: Node[] = [];
  innerTh.childNodes.forEach((node) => {
    if (node.nodeType === Node.COMMENT_NODE) {
      const content = node.textContent || '';
      if (content.includes('v:roundrect') || content.includes('[if mso]')
          || content.includes('[if !mso]') || content.includes('<![endif]')) {
        nodesToRemove.push(node);
      }
    }
  });
  nodesToRemove.forEach((node) => node.parentNode?.removeChild(node));
};

const getButtonLink = (cell: Element | null): Element | undefined => Array.from(cell?.children ?? [])
  .find((child) => child.tagName === 'A');

/**
 * Gives the buttons of a container the Outlook markup the VML repair wrote around their link before its
 * markup was replaced by code stored ahead of the repair: a rounded shape, or a plain table where a
 * background shape holds the button, as Word cannot draw a shape in another. Only a button linking to
 * the same URL takes it: a rendered product links to the product itself.
 */
export const copyVmlButtons = (previousTables: Element[], container: Element) => {
  pairReplacedTables(previousTables, container).forEach(({ previous, table }) => {
    if (!table.classList.contains('spm_button')) return;
    const previousCell = previous.querySelector('th');
    const cell = table.querySelector('th');
    const previousLink = getButtonLink(previousCell);
    const link = getButtonLink(cell);
    if (!previousCell || !cell || !previousLink || !link
      || previousLink.getAttribute('href') !== link.getAttribute('href')) return;
    removeVmlButtonComments(cell);
    const children = Array.from(previousCell.childNodes);
    const linkIndex = children.indexOf(previousLink as ChildNode);
    let after: ChildNode = link as ChildNode;
    children.forEach((node, index) => {
      if (node.nodeType !== Node.COMMENT_NODE) return;
      const copy = node.cloneNode(true) as ChildNode;
      if (index < linkIndex) {
        link.before(copy);
      } else {
        after.after(copy);
        after = copy;
      }
    });
  });
};

/**
 * Generate VML block for a button with rounded corners
 * VML is placed in the widget's th, wrapping the entire table.spm_button
 * @param buttonTable - The table.spm_button element
 * @param radiusPx - Border radius in pixels
 * @returns VML HTML string or null if no VML needed
 */
const generateButtonVmlBlock = (buttonTable: Element, radiusPx: number): string | null => {
  // Get the inner th and anchor
  const innerTh = buttonTable.querySelector('th') as HTMLElement;
  const anchorElement = buttonTable.querySelector('a') as HTMLAnchorElement;
  if (!innerTh || !anchorElement) return null;

  // Force reflow to ensure styles are applied
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  anchorElement.offsetHeight;

  // Get computed styles (after reflow)
  const anchorStyles = window.getComputedStyle(anchorElement);
  const buttonTableStyles = window.getComputedStyle(buttonTable);

  // Calculate button height from line-height + paddings (more reliable than offsetHeight for inline-block)
  const lineHeight = parseInt(anchorStyles.lineHeight, 10) || parseInt(anchorStyles.fontSize, 10) || 16;
  const paddingTop = parseInt(anchorStyles.paddingTop, 10) || 0;
  const paddingBottom = parseInt(anchorStyles.paddingBottom, 10) || 0;
  const buttonHeight = lineHeight + paddingTop + paddingBottom;

  // Check if button is text-adapted using computed style (most reliable after style changes)
  // Text-adapted width only: the anchor's own width, driven by its text, not by the viewport
  // Force reflow by reading offsetHeight first
  // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
  anchorElement.offsetHeight;
  // Use getBoundingClientRect for more reliable width calculation
  const rect = anchorElement.getBoundingClientRect();
  const anchorWidth = Math.ceil(rect.width) || 0;
  const widthStyle = anchorWidth > 0 ? `width:${anchorWidth + 1}px;` : '';

  // Get background color (from anchor - styles are now on <a>)
  const bgColor = rgbToHex(anchorStyles.backgroundColor) || '#1F7F4C';
  // Get text color from anchor
  const textColor = rgbToHex(anchorStyles.color) || '#ffffff';

  // Get text styles
  const fontSize = anchorStyles.fontSize || '16px';
  // Replace double quotes with single quotes in font-family to avoid breaking the style attribute
  const fontFamily = (anchorStyles.fontFamily || 'sans-serif').replace(/"/g, "'");
  const fontWeight = anchorStyles.fontWeight || 'normal';
  const textDecoration = anchorStyles.textDecorationLine || 'none';
  const textAlign = anchorStyles.textAlign || 'center';

  // Get border styles from anchor (styles are now on <a>)
  const borderWidth = parseInt(anchorStyles.borderWidth, 10) || 0;
  const borderColor = rgbToHex(anchorStyles.borderColor) || '#000000';
  const hasStroke = borderWidth > 0;

  // Get button alignment from table align attribute
  const buttonAlign = buttonTable.getAttribute('align') || 'center';
  const msoPositionHorizontal = buttonAlign === 'left' ? 'left' : buttonAlign === 'right' ? 'right' : 'center';

  // Get link href and text content
  const linkHref = anchorElement.getAttribute('href') || '';
  const buttonText = anchorElement.textContent || '';

  // Calculate VML arcsize (percentage based on height)
  const arcsize = Math.min(50, Math.round((radiusPx / buttonHeight) * 100));

  // Build stroke attributes
  const strokeAttr = hasStroke ? 't' : 'f';
  const strokeColorAttr = hasStroke ? `\n  strokecolor="${borderColor}"` : '';
  const strokeWeightAttr = hasStroke ? `\n  strokeweight="${borderWidth}px"` : '';

  // Build complete VML block for Outlook
  // Use <a> tag inside <center> to ensure text color is respected in Outlook
  return `<!--[if mso]>
<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"
  href="${linkHref}"
  style="height:${buttonHeight}px;${widthStyle}v-text-anchor:middle;mso-position-horizontal:${msoPositionHorizontal};"
  arcsize="${arcsize}%"
  stroke="${strokeAttr}"${strokeColorAttr}${strokeWeightAttr}
  fillcolor="${bgColor}">
  <w:anchorlock/>
  <center style="text-align:${textAlign};"><a href="${linkHref}" style="color:${textColor};font-family:${fontFamily};font-size:${fontSize};font-weight:${fontWeight};text-decoration:${textDecoration};text-align:${textAlign};mso-line-height-rule:exactly;">${buttonText}</a></center>
</v:roundrect>
<![endif]-->`;
};

/**
 * Insert MSO table fallback for buttons when parent VML is detected
 * Wraps the existing <a> with MSO table/td to transfer padding styles
 */
const insertMsoTableFallback = (innerTh: Element, anchorElement: Element) => {
  const anchor = anchorElement as HTMLElement;

  // Get computed styles from the anchor to transfer to td
  const computedStyle = window.getComputedStyle(anchor);
  const paddingTop = computedStyle.paddingTop || '0px';
  const paddingRight = computedStyle.paddingRight || '0px';
  const paddingBottom = computedStyle.paddingBottom || '0px';
  const paddingLeft = computedStyle.paddingLeft || '0px';
  const backgroundColor = computedStyle.backgroundColor || 'transparent';
  const borderRadius = computedStyle.borderRadius || '0px';
  const textAlign = computedStyle.textAlign || 'center';

  // Detect button alignment from parent elements
  const getButtonAlignment = (): string => {
    // Check th element (parent of anchor) for text-align or align attribute
    const thComputedStyle = window.getComputedStyle(innerTh as HTMLElement);
    const thTextAlign = thComputedStyle.textAlign;
    const thAlignAttr = (innerTh as HTMLElement).getAttribute('align');

    if (thAlignAttr && (thAlignAttr === 'left' || thAlignAttr === 'center' || thAlignAttr === 'right')) {
      return thAlignAttr;
    }

    if (thTextAlign && (thTextAlign === 'left' || thTextAlign === 'center' || thTextAlign === 'right')) {
      return thTextAlign;
    }

    return 'center'; // default alignment
  };

  // Detect if button should be full width
  const isFullWidth = (): boolean => {
    // Check if anchor has width 100% or display block
    const anchorWidth = computedStyle.width;
    const anchorDisplay = computedStyle.display;

    // Check if parent th has width 100%
    const thComputedStyle = window.getComputedStyle(innerTh as HTMLElement);
    const thWidth = thComputedStyle.width;

    // Consider full width if anchor is block or if parent th is 100%
    return anchorDisplay === 'block' ||
           anchorWidth === '100%' ||
           thWidth === '100%' ||
           anchor.style.width === '100%' ||
           (innerTh as HTMLElement).style.width === '100%';
  };

  const buttonAlignment = getButtonAlignment();
  const fullWidth = isFullWidth();

  // Get the original anchor HTML
  const originalAnchor = anchor.outerHTML;

  // Build table attributes
  const alignAttr = buttonAlignment !== 'center' ? ` align="${buttonAlignment}"` : '';
  const widthAttr = fullWidth ? ' width="100%"' : '';

  // Create MSO wrapper around the original anchor
  const msoTableOpen = `<!--[if mso]>
<table role="presentation" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse;"${alignAttr}${widthAttr}>
  <tr>
    <td style="padding: ${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}; background-color: ${backgroundColor}; border-radius: ${borderRadius}; text-align: ${textAlign};">
<![endif]-->`;

  const msoTableClose = `<!--[if mso]>
    </td>
  </tr>
</table>
<![endif]-->`;

  // Insert MSO wrapper around original anchor
  innerTh.innerHTML = msoTableOpen + originalAnchor + msoTableClose;
};

/**
 * Apply VML to a button widget
 * VML is placed in the inner th of table.spm_button, the <a> is wrapped with conditional comments
 * @param buttonTable - The table.spm_button element
 * @param radiusPx - Border radius in pixels
 */
const applyButtonVml = (buttonTable: Element, radiusPx: number) => {
  // Get the inner th of table.spm_button
  const innerTh = buttonTable.querySelector('th');
  if (!innerTh) return;

  // Get the anchor element
  const anchorElement = innerTh.querySelector('a');
  if (!anchorElement) return;

  // Remove existing VML from inner th first (always clean up, even if we'll be blocked)
  removeVmlButtonComments(innerTh);

  // Check for parent background VML to avoid nesting issues in Outlook
  if (hasParentBackgroundVml(buttonTable as HTMLElement)) {
    // Insert MSO table fallback to maintain button appearance in Outlook
    insertMsoTableFallback(innerTh, anchorElement);
    return;
  }

  // A full-width shape is sized in px from the editor viewport or in percent of the page, never of
  // its cell: the MSO table fallback keeps the button look in Outlook at the width of its cell.
  // An anchor without a declared display is inline, sized by its text like an inline-block one
  if (window.getComputedStyle(anchorElement).display === 'block') {
    insertMsoTableFallback(innerTh, anchorElement);
    return;
  }

  // Generate VML (always, even with radius 0 for proper Outlook rendering)
  const vmlBlock = generateButtonVmlBlock(buttonTable, radiusPx);
  if (!vmlBlock) return;

  const nonMsoOpen = '<!--[if !mso]><!-->';
  const nonMsoClose = '<!--<![endif]-->';

  // Insert VML before the anchor
  anchorElement.insertAdjacentHTML('beforebegin', vmlBlock);
  // Wrap anchor with non-mso comments (hidden from Outlook)
  anchorElement.insertAdjacentHTML('beforebegin', nonMsoOpen);
  anchorElement.insertAdjacentHTML('afterend', nonMsoClose);
};

/**
 * Strip a legacy button style rule that was historically written on the parent
 * `th` cell instead of the `<a>` anchor (smart product list button, email-v4).
 *
 * To be chained as the FIRST setter, BEFORE `setCssProperty`, on fields whose
 * target was moved from `th` to `th a`. It removes only the rule for the
 * current `property.name` on the legacy `th` selector (each property is its own
 * CSS rule, cf. addNewDynamicStyle), so sibling properties are preserved.
 * The new value is then written on the `th a` selector by the following setter.
 *
 * @param selector - The resolved `th a` selector passed to the field setters
 * @param property - The property being set (only its `name` is used here)
 */
export const removeLegacyButtonThStyle = (selector: string, property: Property) => {
  // First selector part is the base `… table.<button> th a` → derive `… th`.
  const legacyThSelector = selector.split(',')[0].trim().replace(/\s+a$/, '');
  if (legacyThSelector === selector.split(',')[0].trim()) {
    // No trailing anchor to strip → nothing legacy to clean, avoid touching `th a`.
    return;
  }
  removeDynamicStyle(legacyThSelector, { [property.name]: '' });
};

/**
 * Cleanup VML for button widget - called when border-radius is toggled off
 * Regenerates VML with arcsize=0 to maintain proper Outlook rendering
 * @param selector - CSS selector for the button element
 */
export const cleanupButtonVml = (selector: string) => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  const doc = getTemplateIframeDocument();
  if (!doc) return;

  // Handle both single widget selector and global selector (table#spm_body)
  const isGlobalSelector = selector.includes('table#spm_body') && !selector.includes('spm_widget');

  if (isGlobalSelector) {
    // Global design: reapply VML with radius 0 for all buttons
    const allButtons = doc.querySelectorAll('table.spm_button');
    allButtons.forEach((buttonTable) => {
      applyButtonVml(buttonTable, 0);
    });
  } else {
    // Single widget: find button table from selector
    const elements = doc.querySelectorAll(selector);
    elements.forEach((element) => {
      const buttonTable = element.closest('table.spm_button') || element.querySelector('table.spm_button');
      if (buttonTable) {
        applyButtonVml(buttonTable, 0);
      }
    });
  }
};

/**
 * Check if a button widget has a local border-radius defined in dynamicStyles
 * @param buttonTable - The table.spm_button element
 * @returns The local border-radius value if defined, null otherwise
 */
const getLocalButtonBorderRadius = (buttonTable: Element): string | null => {
  const widgetElement = buttonTable.closest('[id^="spm_widget"]');
  if (!widgetElement) return null;

  // Build the local selector for this widget's button anchor (styles are on <a>)
  const localSelector = `body #spm_body #${widgetElement.id} table.spm_button th a`;
  const dynamicStyles = getDynamicStylesForSelector(localSelector, false);

  // Check if border-radius is defined locally
  if (dynamicStyles.borderRadius && dynamicStyles.borderRadius !== '0px') {
    return dynamicStyles.borderRadius;
  }

  return null;
};

/**
 * Set border-radius on button widget with VML compatibility for Outlook
 * VML is only generated when border-radius > 0
 * VML is placed in the widget's th, the entire table.spm_button is wrapped with conditional comments
 * For global selectors, respects local widget values (local takes priority over global)
 * @param selector - CSS selector for the button th element
 * @param property - Property containing the border-radius value
 */
export const setButtonBorderRadius = (selector: string, property: Property) => {
  // Apply CSS border-radius
  setCssProperty(selector, property);

  // Only generate VML for email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Parse border-radius value
  const radiusValue = property.value || '0px';
  const radiusPx = parseInt(radiusValue.replace('px', ''), 10) || 0;

  const doc = getTemplateIframeDocument();
  if (!doc) return;

  // Handle both single widget selector and global selector
  const isGlobalSelector = selector.includes('table#spm_body') && !selector.includes('spm_widget');

  if (isGlobalSelector) {
    // Global design: apply to all buttons that don't have local border-radius
    const allButtons = doc.querySelectorAll('table.spm_button');
    allButtons.forEach((buttonTable) => {
      // Check if this button has a local border-radius defined
      const localRadius = getLocalButtonBorderRadius(buttonTable);

      if (localRadius !== null) {
        // Local value exists - use it instead of global
        const localRadiusPx = parseInt(localRadius.replace('px', ''), 10) || 0;
        applyButtonVml(buttonTable, localRadiusPx);
      } else {
        // No local value - apply global
        applyButtonVml(buttonTable, radiusPx);
      }
    });
  } else {
    // Single widget: find button table from selector
    const elements = doc.querySelectorAll(selector);
    elements.forEach((element) => {
      const buttonTable = element.closest('table.spm_button') || element.querySelector('table.spm_button');
      if (buttonTable) {
        applyButtonVml(buttonTable, radiusPx);
      }
    });
  }
};

/**
 * Get the effective border-radius for a button (local value takes priority over global)
 * @param buttonTable - The table.spm_button element
 * @returns The effective border-radius in pixels
 */
const getEffectiveButtonBorderRadius = (buttonTable: Element): number => {
  // First check for local value
  const localRadius = getLocalButtonBorderRadius(buttonTable);
  if (localRadius !== null) {
    return parseInt(localRadius.replace('px', ''), 10) || 0;
  }

  // Fall back to computed style (which includes global) - styles are on <a>
  const anchorElement = buttonTable.querySelector('th a') as HTMLElement;
  if (!anchorElement) return 0;

  const anchorStyles = window.getComputedStyle(anchorElement);
  const radiusValue = anchorStyles.borderRadius || '0px';
  return parseInt(radiusValue.replace('px', ''), 10) || 0;
};

/**
 * Refresh VML for button widget when any style property changes
 * Uses a small delay to allow browser to recalculate dimensions
 * Respects local vs global priority for border-radius
 * @param selector - CSS selector for the button element
 */
export const refreshButtonVml = (selector: string) => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Delay to allow browser to recalculate dimensions after style changes
  // Using requestAnimationFrame + setTimeout for more reliable dimension calculation
  requestAnimationFrame(() => {
    setTimeout(() => {
    const doc = getTemplateIframeDocument();
    if (!doc) return;

    // Check if this is a global selector
    const isGlobalSelector = selector.includes('table#spm_body') && !selector.includes('spm_widget');
    if (isGlobalSelector) {
      // Global change: update all buttons
      const allButtons = doc.querySelectorAll('table.spm_button');
      allButtons.forEach((buttonTable) => {
        const widgetTh = buttonTable.parentElement;
        if (!widgetTh || widgetTh.tagName !== 'TH') return;

        // Get effective border-radius (respects local vs global)
        const radiusPx = getEffectiveButtonBorderRadius(buttonTable);

        // Apply VML
        applyButtonVml(buttonTable, radiusPx);
      });
    } else {
      // Local change: only update this specific button
      const elements = doc.querySelectorAll(selector);
      elements.forEach((element) => {
        const buttonTable = element.closest('table.spm_button') || element.querySelector('table.spm_button');
        if (!buttonTable) return;

        const widgetTh = buttonTable.parentElement;
        if (!widgetTh || widgetTh.tagName !== 'TH') return;

        // Get effective border-radius
        const radiusPx = getEffectiveButtonBorderRadius(buttonTable);

        // Regenerate VML with new styles
        applyButtonVml(buttonTable, radiusPx);
      });
    }
    }, 100);
  });
};

// Alias for backward compatibility
export const refreshButtonVmlOnColorChange = refreshButtonVml;

/**
 * Refresh VML for button widget when link changes (if border-radius is active)
 * @param thElement - The th element containing the button (can be inner th or widget th)
 */
export const refreshButtonVmlOnLinkChange = (thElement: HTMLElement) => {
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    return;
  }

  // Find the button table
  const buttonTable = thElement.closest('table.spm_button') || thElement.querySelector('table.spm_button');
  if (!buttonTable) return;

  const widgetTh = buttonTable.parentElement;
  if (!widgetTh || widgetTh.tagName !== 'TH') return;

  // Check if VML exists
  let hasVml = false;
  widgetTh.childNodes.forEach((node) => {
    if (node.nodeType === Node.COMMENT_NODE && node.textContent?.includes('v:roundrect')) {
      hasVml = true;
    }
  });

  if (!hasVml) return;

  // Get effective border-radius (respects local vs global)
  const radiusPx = getEffectiveButtonBorderRadius(buttonTable);
  if (radiusPx === 0) return;

  // Regenerate VML with new link
  applyButtonVml(buttonTable, radiusPx);
};

/**
 * Apply VML for image with border-radius (used by validation and setters)
 * @param imgElement - The image element
 * @param radiusPx - Border radius in pixels
 */
export const applyImageVml = (imgElement: HTMLImageElement, radiusPx: number) => {
  const thElement = imgElement.closest('th') as HTMLElement;
  if (!thElement) return;

  // Remove any existing VML first (always clean up, even if we'll be blocked)
  removeVmlRoundrectComments(thElement);

  // Check for parent background VML to avoid nesting issues in Outlook
  if (hasParentBackgroundVml(imgElement)) {
    return;
  }

  // Skip VML generation if no border-radius
  if (radiusPx <= 0) return;

  // Get image dimensions
  const imgWidth = imgElement.offsetWidth || imgElement.naturalWidth || 300;
  const imgHeight = imgElement.offsetHeight || imgElement.naturalHeight || 200;
  const imgSrc = imgElement.getAttribute('src') || '';

  // Calculate VML arcsize
  const smallestDim = Math.min(imgWidth, imgHeight);
  const arcsize = Math.min(50, Math.round((radiusPx / smallestDim) * 100));

  // Get link href if image is inside an anchor
  const anchorElement = imgElement.closest('a');
  const linkHref = anchorElement?.getAttribute('href') || '';
  const hrefAttr = linkHref ? ` href="${linkHref}"` : '';

  // Build VML for Outlook
  const vmlBlock = `<!--[if mso]>
<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word"
  style="width:${imgWidth}px;height:${imgHeight}px;v-text-anchor:middle;"
  arcsize="${arcsize}%"
  stroke="false"
  fillcolor="${getEffectiveBackgroundColor(imgElement)}"${hrefAttr}>
  <v:fill type="frame" src="${imgSrc}" />
  <w:anchorlock/>
</v:roundrect>
<![endif]-->`;

  // Conditional comments to hide original image in Outlook
  const nonMsoOpen = '<!--[if !mso]><!-->';
  const nonMsoClose = '<!--<![endif]-->';

  // Insert VML before the anchor or img
  const insertTarget = anchorElement || imgElement;
  insertTarget.insertAdjacentHTML('beforebegin', vmlBlock);
  insertTarget.insertAdjacentHTML('beforebegin', nonMsoOpen);
  insertTarget.insertAdjacentHTML('afterend', nonMsoClose);
};

/**
 * Check if element has a parent with background VML (to avoid VML nesting)
 * @param element - The HTML element to check
 * @returns true if parent VML found, false otherwise
 */
const hasParentBackgroundVml = (element: HTMLElement): boolean => {
  // Every ancestor that may hold a background shape is checked, the section as well as its container:
  // a button or image shape nested in that shape's textbox breaks it
  for (
    let host = element.parentElement?.closest(BACKGROUND_VML_HOST_SELECTOR) as HTMLElement | null;
    host;
    host = host.parentElement?.closest(BACKGROUND_VML_HOST_SELECTOR) as HTMLElement | null
  ) {
    // A background image gets its shape at save time, whether or not the shape is already in the DOM
    if (getBackgroundImageUrl(host)) {
      return true;
    }

    // Check the host, its row and the row's host cell for an existing background VML
    const row = host.querySelector(':scope > tbody > tr') as HTMLElement | null;
    const elementsToCheck = [host, row, row ? getVmlHostCell(row) : null].filter((el) => el);

    for (const sectionElement of elementsToCheck) {
      if (!sectionElement) continue;

      const comments = Array.from(sectionElement.childNodes).filter(node =>
        node.nodeType === Node.COMMENT_NODE
      );

      for (const comment of comments) {
        const content = comment.textContent || '';

        if (content.includes('[if gte mso 9]') && content.includes('v:rect')) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Apply VML for element with background image (used by validation and setters)
 * @param element - The HTML element with background
 * @param backgroundImageUrl - URL of the background image
 */
export const applyBackgroundVml = (element: HTMLElement, backgroundImageUrl: string, selector?: string, property?: Property) => {
  // Use dynamic styles for reliable parameters (same logic as setBackgroundImage)
  const elementId = element.id;
  const dynamicSelector = selector || (elementId ? `#${elementId}` : '');
  const dynamicStyles = dynamicSelector ? getDynamicStylesForSelector(dynamicSelector, false) : {};

  const canvasWidth = resolveVmlCanvasWidth(element);
  const backgroundSize = dynamicStyles['background-size'] || 'cover';

  // Reconstruct background-position from separate x/y values
  const bgPosX = dynamicStyles['background-position-x'] || 'center';
  const bgPosY = dynamicStyles['background-position-y'] || 'center';
  const backgroundPosition = `${bgPosX} ${bgPosY}`;

  const backgroundColor = getPaintedBackgroundColor(element);

  // Get height for Outlook VML (same logic as setBackgroundImage)
  let vmlHeight: string | undefined;
  const isContainer = selector?.includes('.container');

  if (property) {
    // If height property is being toggled off (empty value), force no height
    const isHeightToggleOff = (property.name === 'height' || property.name === 'min-height') && property.value === '';
    // If height is being set directly via property, use that value
    const isHeightProperty = property.name === 'height' || property.name === 'min-height';

    if (isHeightToggleOff) {
      vmlHeight = undefined;
    } else if (isHeightProperty && property.value && property.value !== '0px' && property.value !== 'auto') {
      // Use property value directly when height is being changed
      vmlHeight = property.value;
    }
  }

  // If no height from property, get from dynamic styles
  if (vmlHeight === undefined) {
    const isRichEditorWrapper = element.classList.contains('spm_rich_editor_wrapper');
    if (isRichEditorWrapper || isContainer) {
      // For rich editor wrapper or container sections, check if height is explicitly set in dynamic styles on selector itself
      const dynamicHeight = dynamicStyles.height;
      vmlHeight = dynamicHeight && dynamicHeight !== '0px' && dynamicHeight !== 'auto' ? dynamicHeight : undefined;
    } else {
      const thSelector = `${selector} > tbody > tr > th`;
      const thDynamicStyles = getDynamicStylesForSelector(thSelector, false);
      const thHeight = thDynamicStyles.height;
      vmlHeight = thHeight && thHeight !== '0px' && thHeight !== 'auto' ? thHeight : undefined;
    }
  }

  // A declared height shorter than the content is ignored by every client but Outlook
  vmlHeight = resolveVmlHeight(element, vmlHeight);

  const useCoverMode = backgroundSize === 'cover';

  // Convert CSS background-position to VML position
  const vmlPosition = cssBackgroundPositionToVml(backgroundPosition, useCoverMode ? 'frame' : 'tile');

  // Calculate padding inset from DOM (same logic as setBackgroundImage's getPaddingInset)
  const isRichEditorWrapper = element.classList.contains('spm_rich_editor_wrapper');
  const getPaddingInset = (): string => buildVmlInset(getFirstCell(element));
  const vmlInset = getPaddingInset();

  // Determine correct VML target element (same logic as setBackgroundImage)
  let vmlTargetElement: HTMLElement;

  if (isRichEditorWrapper) {
    // For rich editor wrapper: VML goes on tbody > tr
    const wrapperRow = element.querySelector(':scope > tbody > tr') as HTMLElement;
    if (wrapperRow) {
      vmlTargetElement = wrapperRow;
      removeComments(wrapperRow);
    } else {
      console.error('❌ No tbody > tr found in rich editor wrapper!');
      return; // No target found
    }
  } else {
    // For sections/containers: VML goes on tbody > tr
    const sectionRow = element.querySelector(':scope > tbody > tr') as HTMLElement;
    if (sectionRow) {
      vmlTargetElement = sectionRow;
      removeComments(sectionRow);
    } else {
      // Fallback to element itself
      vmlTargetElement = element;
      removeComments(element);
    }
  }
  if (useCoverMode) {
    insertOutlookCoverCompatibility(
      vmlTargetElement,
      canvasWidth,
      backgroundImageUrl,
      backgroundColor,
      'afterbegin',
      'beforeend',
      vmlHeight, // Use calculated height
      vmlPosition,
      vmlInset,
    );
  } else {
    insertOutlookCompatibility(
      vmlTargetElement,
      canvasWidth,
      backgroundImageUrl,
      backgroundColor,
      'afterbegin',
      'beforeend',
      vmlHeight, // Use calculated height
      vmlPosition,
      vmlInset,
    );
  }
};

/**
 * Set background image and apply VML for email templates
 * Used in email-v4 configs to avoid VML duplication
 * @param selector - CSS selector for the element
 * @param property - Property containing background values
 */
export const setBackgroundImageWithVml = (selector: string, property: Property) => {
  // Only apply VML and HTML background attribute for email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type)) {
    setCssProperty(selector, property);
    return;
  }

  const element = getTemplateIframeDocument()?.querySelector(selector) as HTMLElement;

  // If removing background image, clean up VML and HTML background attribute
  if (property.name === 'background-image' && (property.value === 'none' || property.value === '')) {
    if (element) {
      element.removeAttribute('background');
      element.style.removeProperty('background-image');
      // Remove VML comments from the correct location (tbody > tr for background images)
      const isRichEditorWrapper = selector.includes('.spm_rich_editor_wrapper');
      if (isRichEditorWrapper) {
        const wrapperRow = element.querySelector(':scope > tbody > tr');
        if (wrapperRow) {
          removeComments(wrapperRow);
        }
      } else {
        // For sections, VML is on > tbody > tr
        const sectionRow = element.querySelector(':scope > tbody > tr');
        if (sectionRow) {
          removeComments(sectionRow);
        } else {
          removeComments(element);
        }
      }
    }
    // Remove CSS background properties for email templates
    removeDynamicStyle(selector, { backgroundImage: '', background: '', backgroundSize: '', backgroundRepeat: '' });
    return;
  }

  // Extract background image URL
  let backgroundImageUrl = '';

  if (property.name === 'background-image') {
    // Direct background-image property
    const urlMatch = property.value.match(/url\(['"]?([^'"]*?)['"]?\)/);
    if (urlMatch) {
      backgroundImageUrl = urlMatch[1];
    }
    if (element) {
      element.style.removeProperty('background-image');
    }
  } else if (element) {
    backgroundImageUrl = getBackgroundImageUrl(element);
  }

  // Set HTML background attribute for email templates (important for email client compatibility)
  if (backgroundImageUrl && element) {
    const isRichEditorWrapper = selector.includes('.spm_rich_editor_wrapper');
    if (isRichEditorWrapper) {
      // For rich editor wrapper, set background attribute on table.spm_rich_editor_wrapper itself
      element.setAttribute('background', backgroundImageUrl);
    } else {
      // For sections/containers, set background attribute directly
      element.setAttribute('background', backgroundImageUrl);
    }
  }

  // Apply CSS properties
  if (property.name === 'background-image') {
    // For email templates, don't store background-image URL in CSS (some email clients transform background to background:none)
    // URL is stored only in HTML background attribute. Remove any existing CSS background/background-image
    removeDynamicStyle(selector, { backgroundImage: '', background: '' });
  } else {
    setCssProperty(selector, property);
  }

  // Apply VML if we have a valid background image URL
  if (backgroundImageUrl && element) {
    // Use a small delay to ensure CSS and HTML attributes are applied first
    setTimeout(() => {
      applyBackgroundVml(element, backgroundImageUrl, selector, property);
    }, 50);
  }
};

/**
 * Validate and repair ALL VML elements in email template V4
 * Handles buttons, images with border-radius, and background images
 * This is a security layer to ensure all VML is correctly formatted before save
 * Only executed for V4 templates
 */
const repairAllVml = (template: Document): boolean => {
  let hasChanges = false;

  try {
    // 1. VALIDATE AND REPAIR BUTTON VML
    const allButtons = template.querySelectorAll('table.spm_button');
    allButtons.forEach((buttonTable) => {
      try {
        // Get effective border-radius for this button
        const radiusPx = getEffectiveButtonBorderRadius(buttonTable);

        // Always regenerate VML to ensure it's correct and up-to-date
        applyButtonVml(buttonTable, radiusPx);
        hasChanges = true;

      } catch (error) {
        console.warn('Error validating VML for button:', error);
      }
    });

    // 2. VALIDATE AND REPAIR IMAGE VML (border-radius)
    const allImages = template.querySelectorAll('img[src]');
    allImages.forEach((imgElement) => {
      try {
        const thElement = imgElement.closest('th') as HTMLElement;
        if (!thElement) return;

        // Get widget for dynamic styles check
        const widgetElement = thElement.closest('[id^="spm_widget"]');
        if (!widgetElement) return;

        const widgetSelector = `body #spm_body #${widgetElement.id}`;
        const imgSelector = `${widgetSelector} > tbody > tr > th img`;
        const dynamicStyles = getDynamicStylesForSelector(imgSelector, false);

        // Get border-radius from any of the corner-specific properties (they should all be the same for uniform radius)
        const radiusValue = dynamicStyles['border-radius'] ||
                           dynamicStyles['border-top-left-radius'] ||
                           dynamicStyles['border-top-right-radius'] ||
                           dynamicStyles['border-bottom-left-radius'] ||
                           dynamicStyles['border-bottom-right-radius'] || '0px';
        const radiusPx = parseInt(radiusValue.replace('px', ''), 10) || 0;



        // Use the dedicated method to apply image VML
        applyImageVml(imgElement as HTMLImageElement, radiusPx);
        if (radiusPx > 0) {
          hasChanges = true;
        }

      } catch (error) {
        console.warn('Error validating VML for image:', error);
      }
    });

    // 3. VALIDATE AND REPAIR BACKGROUND VML
    // Find elements that might have background images
    const backgroundElements = template.querySelectorAll(BACKGROUND_VML_HOST_SELECTOR);
    backgroundElements.forEach((element) => {
      try {
        const htmlElement = element as HTMLElement;
        const imageUrl = getBackgroundImageUrl(htmlElement);
        if (!imageUrl) return;
        // An inline image left by an earlier tool would be inlined and turned back into the attribute at send
        if (htmlElement.style.backgroundImage && !htmlElement.style.backgroundImage.includes(imageUrl)) {
          htmlElement.style.removeProperty('background-image');
          hasChanges = true;
        }

        // Reconstruct proper selector for dynamic styles
        let properSelector = '';
        if (htmlElement.classList.contains('spm_rich_editor_wrapper')) {
          const widgetElement = htmlElement.closest('[id^="spm_widget"]');
          if (widgetElement) {
            properSelector = `body #spm_body #${widgetElement.id} .spm_rich_editor_wrapper`;
          }
        } else if (htmlElement.classList.contains('container')) {
          const sectionElement = htmlElement.closest('[id^="spm_section"]');
          if (sectionElement) {
            properSelector = `body #spm_body #${sectionElement.id}.container`;
          }
        } else if (htmlElement.id?.startsWith('spm_section') || htmlElement.id?.startsWith('spm_widget')) {
          properSelector = `body #spm_body #${htmlElement.id}`;
        }

        // Use the dedicated method to apply background VML
        applyBackgroundVml(htmlElement, imageUrl, properSelector);
        hasChanges = true;

      } catch (error) {
        console.warn('Error validating VML for background:', error);
      }
    });

    // Every shape, section or widget, gets its Outlook adjustments brought up to date
    if (template.body && refreshVmlLayoutAdjustments(template.body)) {
      hasChanges = true;
    }

  } catch (error) {
    console.warn('Error in VML validation process:', error);
  }

  return hasChanges;
};

export const validateAndRepairAllVml = (template: Document): boolean => {
  // Only for V4 email templates
  if (!isEmailTemplate(TemplateEditorState.template?.type) || TemplateEditorState.template?.version !== 4) {
    return false;
  }
  // Outlook ignores media queries: its markup is sized on the desktop layout, whatever the editor window
  return withDesktopLayout(template, () => repairAllVml(template));
};

// ============================================
// INTEGRATION WIDGET SETTERS (generic image-as-a-service)
// Generalises the Enkore prototype: any integration image widget renders an
// <img> whose URL is rebuilt from data-url-template + data-config. The concrete
// widget is identified by data-integration-slug / data-widget-key.
// ============================================

const buildIntegrationWidgetUrl = (
  widget: HTMLElement,
  preview = true,
): string => {
  const urlTemplate = widget.getAttribute('data-url-template') || '';
  let config: Record<string, unknown> = {};
  try {
    config = JSON.parse(widget.getAttribute('data-config') || '{}');
  } catch {
    config = {};
  }
  // Editor preview: swap each {var=…} for the field's preview_value so the image
  // loads. The save reconciliation passes preview=false to keep {var=…} literal.
  let previewValues: Record<string, string> | undefined;
  if (preview) {
    try {
      previewValues = extractIntegrationPreviewValues(
        JSON.parse(widget.getAttribute('data-schema') || '{}'),
      );
    } catch {
      previewValues = undefined;
    }
  }
  return substituteIntegrationWidgetUrl(urlTemplate, config, previewValues);
};

const showIntegrationWidgetLoader = (widget: HTMLElement) => {
  const existingLoader = widget.querySelector('.spm_integration_widget_loader');
  if (existingLoader) {
    existingLoader.remove();
  }

  const loader = document.createElement('div');
  loader.className = 'spm_integration_widget_loader';
  loader.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: spm-integration-widget-spin 0.8s linear infinite;
  `;

  const doc = widget.ownerDocument;
  if (doc && !doc.getElementById('spm-integration-widget-loader-styles')) {
    const style = doc.createElement('style');
    style.id = 'spm-integration-widget-loader-styles';
    style.textContent = `
      @keyframes spm-integration-widget-spin {
        to { transform: rotate(360deg); }
      }
    `;
    doc.head.appendChild(style);
  }

  loader.appendChild(spinner);

  const widgetStyle = window.getComputedStyle(widget);
  if (widgetStyle.position === 'static') {
    widget.style.position = 'relative';
  }

  widget.appendChild(loader);
};

const hideIntegrationWidgetLoader = (widget: HTMLElement) => {
  const loader = widget.querySelector('.spm_integration_widget_loader');
  if (loader) {
    loader.remove();
  }
};

/**
 * Rebuild the <img> source of an integration widget from its current
 * data-url-template + data-config, with a cache-bust so the partner image
 * refreshes on every edit.
 */
export const updateIntegrationWidgetImage = (widget: HTMLElement) => {
  const img = widget.querySelector(
    'img.spm_integration_widget_img',
  ) as HTMLImageElement;
  if (!img) return;

  // The image src / alt must never be picked up by the native translator (an
  // integration image widget's URL and name are not translatable). Idempotent —
  // also normalises widgets dropped before this class was added at creation.
  img.classList.add('spm_no_translate');

  const imgUrl = buildIntegrationWidgetUrl(widget);
  const sep = imgUrl.includes('?') ? '&' : '?';
  const cacheBustUrl = `${imgUrl}${sep}_t=${Date.now()}`;

  showIntegrationWidgetLoader(widget);
  img.style.maxWidth = '100%';
  img.style.height = 'auto';

  const onLoadHandler = () => {
    hideIntegrationWidgetLoader(widget);
    img.removeEventListener('load', onLoadHandler);
    img.removeEventListener('error', onErrorHandler);
  };
  const onErrorHandler = () => {
    hideIntegrationWidgetLoader(widget);
    img.removeEventListener('load', onLoadHandler);
    img.removeEventListener('error', onErrorHandler);
  };

  img.addEventListener('load', onLoadHandler);
  img.addEventListener('error', onErrorHandler);

  img.setAttribute('src', cacheBustUrl);
};

/**
 * Save reconciliation for integration IMAGE widgets. The editor shows a preview
 * src where each `{var=…}` is swapped for the field's `preview_value` (so the
 * image loads at edit time). The PERSISTED src must keep `{var=…}` literal so
 * the templating engine resolves the real value per recipient at send. This
 * rebuilds the canonical src (no preview, no cache-bust) before save — mirrors
 * `restoreSmartListsOriginalCode`. Returns true if any src changed.
 */
export const restoreIntegrationImagesCanonicalSrc = (): boolean => {
  const doc = getTemplateIframeDocument();
  if (!doc) return false;
  const widgets = Array.from(
    doc.querySelectorAll('.spm_widget_integration[data-render-type="image"]'),
  ) as HTMLElement[];
  let changed = false;
  widgets.forEach((widget) => {
    const img = widget.querySelector(
      'img.spm_integration_widget_img',
    ) as HTMLImageElement | null;
    if (!img) return;
    const canonical = buildIntegrationWidgetUrl(widget, false);
    if (img.getAttribute('src') !== canonical) {
      img.setAttribute('src', canonical);
      changed = true;
    }
  });
  // The canonical src ({var=…}) can't load in the editor. Once the save state
  // has been captured (this tick), restore the preview src so the editor keeps
  // showing the image — this does NOT affect the already-persisted HTML.
  if (changed) {
    setTimeout(() => widgets.forEach((w) => updateIntegrationWidgetImage(w)), 0);
  }
  return changed;
};

/**
 * Inject a rendered widget's optional CSS / JS into its content cell (after the
 * html has been set). CSS → a scoped <style> (renderers namespace their own
 * selectors); JS → an IIFE <script>, web targets only (email clients strip JS).
 * Injected via textContent so the value can't break out of the element.
 */
const applyIntegrationWidgetAssets = (
  content: HTMLElement,
  css?: string,
  javascript?: string,
  target?: string,
) => {
  const doc = content.ownerDocument || document;
  if (css) {
    const styleEl = doc.createElement('style');
    styleEl.className = 'spm_integration_widget_style';
    styleEl.textContent = css;
    content.appendChild(styleEl);
  }
  if (javascript && target && target !== 'email_template') {
    const scriptEl = doc.createElement('script');
    scriptEl.className = 'spm_integration_widget_script';
    scriptEl.textContent = `(function(){\n${javascript}\n})();`;
    content.appendChild(scriptEl);
  }
};

/**
 * Section id (`data-spmelementid`) of the section containing the widget — the
 * widget's `{LANG_}` field translations are attached to it (so they persist /
 * clean up with the section, like native text). Falls back to the active section.
 */
const getIntegrationWidgetSectionId = (widget: HTMLElement): number => {
  const section = widget.closest('[data-spmelementid]');
  const id = section?.getAttribute('data-spmelementid');
  return id ? Number(id) : Number(TemplateEditorState.template?.activeSection ?? 0);
};

/**
 * Render an html integration widget (static or dynamic): POST /widgets/render
 * → inject { html, css?, js? } into the block's content cell. For static mode
 * that cell IS the saved source (sent as-is at mail send); for dynamic it
 * is an editor preview (the worker re-renders per recipient at send). data-config
 * stays the edit-time source of truth. On failure keeps existing content (or a
 * neutral placeholder on first drop).
 *
 * Translatable fields (config_schema `translatable: true`) are sent to the render
 * as their `{LANG_<key>}` token, so the rendered cell carries the token; the
 * tokens are then resolved to the current language for display (and converted back
 * to `{LANG_}` at save), plugging the widget into the native translation pipeline.
 */
export const renderIntegrationWidget = async (widget: HTMLElement) => {
  const content = widget.querySelector(
    '.spm_integration_widget_content',
  ) as HTMLElement | null;
  if (!content) return;

  const slug = widget.getAttribute('data-integration-slug') || '';
  const key = widget.getAttribute('data-widget-key') || '';
  const target = widget.getAttribute('data-target') || 'email_template';
  let config: Record<string, unknown> = {};
  try {
    config = JSON.parse(widget.getAttribute('data-config') || '{}');
  } catch {
    config = {};
  }
  let schema: any = {};
  try {
    schema = JSON.parse(widget.getAttribute('data-schema') || '{}');
  } catch {
    schema = {};
  }

  // Translatable fields → bake {LANG_} tokens + register their per-language
  // translation keys. buildHtmlRenderConfig mutates `config` (adds the __i18n
  // field→key map), so persist data-config afterwards.
  const renderConfig = buildHtmlRenderConfig(
    config,
    schema,
    getIntegrationWidgetSectionId(widget),
  );
  widget.setAttribute('data-config', JSON.stringify(config));

  // Distingue le PREMIER rendu (drop : cellule vide / simple loader / bandeau
  // d'erreur précédent) d'un RE-RENDU après édition (un rendu réel est déjà
  // affiché). Cf. G3 : premier échec → bandeau « Rendu indisponible — réessayer »
  // dans la cellule ; échec de re-rendu → toast, on garde l'ancien rendu visible.
  const hasPriorRender = !!content
    && !content.querySelector('.spm_integration_widget_loading')
    && !content.querySelector('.spm_integration_widget_render_error')
    && (content.innerHTML || '').trim() !== '';

  showIntegrationWidgetLoader(widget);
  try {
    const result = await renderIntegrationWidgetApi(slug, key, renderConfig, target as any);
    if (result.html) {
      content.innerHTML = result.html;
      // Resolve the {LANG_} tokens to the current language so the editor shows
      // real text; the native save converts the markup back to {LANG_} tokens.
      if (hasTranslatableFields(schema) && isShopMultilingual()) {
        translateDocument(getShopDefaultLang(), content);
      }
      applyIntegrationWidgetAssets(content, result.css, result.javascript, target);
    } else if (!hasPriorRender) {
      // First render returned nothing to keep → neutral placeholder.
      content.innerHTML =
        '<div style="padding: 16px; color: #9ca3af; font-family: Arial, sans-serif; font-size: 12px;">—</div>';
    }
  } catch (error) {
    if (!(error instanceof IntegrationWidgetRenderError)) throw error;
    if (hasPriorRender) {
      // Re-rendu en échec : on conserve l'ancien rendu affiché mais on prévient
      // l'utilisateur par un toast (jamais un échec silencieux).
      showToast('error', 'templateBuilder.panels.widgets.renderError');
    } else {
      // Premier rendu en échec : bandeau « Rendu indisponible — réessayer » dans
      // la cellule (au lieu d'un « — » muet), avec bouton relançant le rendu.
      renderIntegrationWidgetErrorBanner(widget);
    }
  } finally {
    hideIntegrationWidgetLoader(widget);
  }
};

/**
 * Affiche, dans la cellule du widget, un bandeau « Rendu indisponible — réessayer »
 * (G3) quand le tout premier rendu échoue. Le bouton relance `renderIntegrationWidget`
 * sur le même bloc. Textes lus depuis l'i18n courante (fallback FR intégré).
 */
export const renderIntegrationWidgetErrorBanner = (widget: HTMLElement): void => {
  const content = widget.querySelector(
    '.spm_integration_widget_content',
  ) as HTMLElement | null;
  if (!content) return;
  const tGlobal = (key: string): string => (i18n.global as any).t(key);
  const message = tGlobal('templateBuilder.panels.widgets.renderUnavailable');
  const retryLabel = tGlobal('templateBuilder.panels.widgets.retry');
  content.innerHTML = `
    <div class="spm_integration_widget_render_error" style="padding:14px 16px;text-align:center;font-family:Arial,sans-serif;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;color:#b91c1c;">
      <div style="font-size:12px;margin-bottom:8px;">${message}</div>
      <button type="button" class="spm_integration_widget_retry" style="cursor:pointer;font-size:12px;font-weight:600;color:#fff;background:#dc2626;border:none;border-radius:6px;padding:5px 12px;">${retryLabel}</button>
    </div>`;
  const btn = content.querySelector('.spm_integration_widget_retry') as HTMLElement | null;
  if (btn) {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      renderIntegrationWidget(widget);
    });
  }
};

/**
 * Initialise an integration widget (drop / template load / duplicate). Image
 * widgets rebuild their <img> URL; html widgets (static or dynamic) render
 * server-side. On `load`, an already-rendered block is left untouched; a
 * drop/duplicate or a not-yet-rendered block triggers a (re)render.
 */
export const initializeIntegrationWidget = (
  widgetId: string,
  trigger?: 'drop' | 'load' | 'duplicate',
) => {
  const template = getTemplateIframeDocument();
  const widget = template?.getElementById(widgetId) as HTMLElement;
  if (!widget) return;

  const renderType = widget.getAttribute('data-render-type');

  if (renderType === 'html') {
    const content = widget.querySelector('.spm_integration_widget_content');
    const hasRendered =
      !!content &&
      !content.querySelector('.spm_integration_widget_loading') &&
      (content.innerHTML || '').trim() !== '';
    if (trigger === 'drop' || trigger === 'duplicate' || !hasRendered) {
      renderIntegrationWidget(widget);
    }
    return;
  }

  updateIntegrationWidgetImage(widget);
};

/** Read one integration-widget config value from the block's data-config. */
export const getIntegrationWidgetConfigValue = (
  selector: string,
  name: string,
): string | undefined => {
  const el = getTemplateIframeDocument()?.querySelector(selector) as HTMLElement | null;
  if (!el) return undefined;
  try {
    const config = JSON.parse(el.getAttribute('data-config') || '{}');
    const v = config[name];
    return v == null ? undefined : String(v);
  } catch {
    return undefined;
  }
};

/**
 * Generic setter shared by every config_schema field of every integration
 * widget: write the value into data-config (colors normalised to hex) and
 * rebuild the image. The field name is the config_schema key.
 */
export const setIntegrationWidgetParam = (selector: string, property: Property) => {
  const el = getTemplateIframeDocument()?.querySelector(selector) as HTMLElement | null;
  if (!el) return;
  let config: Record<string, unknown> = {};
  try {
    config = JSON.parse(el.getAttribute('data-config') || '{}');
  } catch {
    config = {};
  }
  let value: any = property.value;
  if (typeof value === 'string' && /^rgba?\(/i.test(value)) {
    value = rgbaToHex(value);
  }
  config[property.name] = value;
  el.setAttribute('data-config', JSON.stringify(config));
  if (el.getAttribute('data-render-type') === 'image') {
    updateIntegrationWidgetImage(el);
  } else {
    renderIntegrationWidget(el);
  }
};

/**
 * Getter for a `strip_hash` colour field: data-config stores the hex WITHOUT a
 * leading `#` (for image URLs), but the colour picker needs `#rrggbb` to display.
 */
export const getIntegrationWidgetColorValue = (
  selector: string,
  name: string,
): string | undefined => {
  const v = getIntegrationWidgetConfigValue(selector, name);
  if (v == null || v === '') return v;
  return v.startsWith('#') ? v : `#${v}`;
};

/**
 * Setter for a `strip_hash` colour field: normalise to hex, drop the leading
 * `#`, store in data-config (so {widget.*} emits bare hex), then re-render.
 */
export const setIntegrationWidgetColorNoHash = (selector: string, property: Property) => {
  const el = getTemplateIframeDocument()?.querySelector(selector) as HTMLElement | null;
  if (!el) return;
  let config: Record<string, unknown> = {};
  try {
    config = JSON.parse(el.getAttribute('data-config') || '{}');
  } catch {
    config = {};
  }
  let value: any = property.value;
  if (typeof value === 'string' && /^rgba?\(/i.test(value)) {
    value = rgbaToHex(value);
  }
  if (typeof value === 'string') {
    value = value.replace(/^#/, '');
  }
  config[property.name] = value;
  el.setAttribute('data-config', JSON.stringify(config));
  if (el.getAttribute('data-render-type') === 'image') {
    updateIntegrationWidgetImage(el);
  } else {
    renderIntegrationWidget(el);
  }
};

export const setInputType = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const inputElement = template?.querySelector(selector) as HTMLInputElement | null;
  if (!inputElement || !property.value) return;

  const newType = property.value as string;
  const oldType = inputElement.getAttribute('type') || 'text';

  // Set the type attribute
  inputElement.setAttribute('type', newType);

  // Find the widget root
  const widgetEl = inputElement.closest('.spm_widget_input_text');
  if (!widgetEl) return;

  const widgetId = widgetEl.getAttribute('id') || '';
  const existingContainer = widgetEl.querySelector('.spm_widget_input_phone__input_container');

  if (newType === 'tel' && !existingContainer) {
    // Changing TO tel: inject country selector structure
    const defaultCountry = 'FR';
    const country = countriesList.find((c) => c.code === defaultCountry);

    // Build country options HTML
    const countryOptionsHtml = countriesList.map((c) => createCountrySelectOption(
      widgetId,
      c.code,
      getCountryName(c.code),
      c.dialCode,
      c.flag,
      c.code === defaultCountry,
    )).join('');

    // Create the container
    const container = template.createElement('div');
    container.className = 'spm_widget_input_phone__input_container';

    // Create country select
    const countrySelectHtml = `
      <div class="spm_custom_select spm_widget_input_phone__country_select" data-country-select="${widgetId}">
        <div class="spm_custom_select__trigger">${country ? `${country.flag} (${country.dialCode})` : ''}</div>
        <div class="spm_custom_select__dropdown">
          <input type="text" class="spm_custom_select__search" placeholder="Rechercher..." />
          <div class="spm_custom_select__options">
            ${countryOptionsHtml}
          </div>
        </div>
        <input type="hidden" class="spm_custom_select__hidden_input" value="${defaultCountry}" />
      </div>
    `;

    // Insert container before input, move input inside
    const parent = inputElement.parentElement;
    if (parent) {
      parent.insertBefore(container, inputElement);
      container.innerHTML = countrySelectHtml;
      container.appendChild(inputElement);
    }

    // Add phone input class for CSS styling (border-radius)
    inputElement.classList.add('spm_widget_input_phone__input');

    // Set default country attribute on widget root
    widgetEl.setAttribute('data-default-country', defaultCountry);
  } else if (oldType === 'tel' && newType !== 'tel' && existingContainer) {
    // Changing FROM tel: remove country selector, unwrap input
    const parent = existingContainer.parentElement;
    if (parent) {
      parent.insertBefore(inputElement, existingContainer);
      existingContainer.remove();
    }

    // Remove phone input class
    inputElement.classList.remove('spm_widget_input_phone__input');

    // Remove default country attribute
    widgetEl.removeAttribute('data-default-country');
  }

  updateSectionsInState(false);
};

export const setValidationRules = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const widgetEl = template?.querySelector(selector);
  if (!widgetEl) return;

  const input = widgetEl.querySelector('.spm_widget_input_text__input');
  if (!input) return;

  const rules: Array<{ type: string; value: string; message: string; translationKey: string }> = property.value || [];

  // Clear existing data-spm-val-* attributes
  const existingAttrs = Array.from(input.attributes).filter((attr) => attr.name.startsWith('data-spm-val-'));
  existingAttrs.forEach((attr) => input.removeAttribute(attr.name));

  // Keys we intend to reuse across this setter run — must be preserved in state
  // (otherwise translations already entered in other languages would be lost).
  const reusedKeys = new Set(
    rules.filter((r) => r.translationKey).map((r) => r.translationKey),
  );

  // Remove existing validation messages container and clean up state entries
  // for keys that will NOT be reused.
  const existingContainer = widgetEl.querySelector('.spm_validation_messages');
  if (existingContainer) {
    const existingMsgs = existingContainer.querySelectorAll(`${TRANSLATION_MARKUP}`);
    existingMsgs.forEach((el) => {
      const key = el.getAttribute('id')?.replace('LANG_', '');
      if (key && !reusedKeys.has(key)) removeTranslationInState(key);
    });
    existingContainer.remove();
  }

  if (rules.length === 0) return;

  // Create new validation messages container.
  const container = template.createElement('div');
  container.className = 'spm_validation_messages';
  container.style.display = 'none';

  rules.forEach((rule) => {
    // Set data attribute on input for param-based rules
    if (['minlength', 'maxlength', 'pattern', 'min', 'max'].includes(rule.type) && rule.value) {
      input.setAttribute(`data-spm-val-${rule.type}`, rule.value);
    }

    // Create message element with translation.
    const span = template.createElement('span');
    span.className = 'spm_val_msg';
    span.setAttribute('data-spm-val-rule', rule.type);

    if (rule.translationKey) {
      // Reuse existing key so translations already saved for other languages survive.
      // The state entries for this key were not deleted above.
      span.innerHTML = `<${TRANSLATION_MARKUP} id="LANG_${rule.translationKey}">${rule.message}</${TRANSLATION_MARKUP}>`;
    } else {
      // Create a new translation entry in state for every shop language.
      const translationResult = createTranslation('', 'text', rule.message, true) as TranslationCreationResult;
      if (translationResult.markup) {
        span.innerHTML = `<${translationResult.markup} id="${translationResult.id}">${translationResult.value}</${translationResult.markup}>`;
      }
    }

    container.appendChild(span);
  });

  widgetEl.appendChild(container);

  // Persist DOM mutations into section state so the new translations aren't
  // garbage-collected by removeUnusedTranslations on the next editor cycle.
  updateSectionsInState(false);
};
